import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslate } from '../translations/useTranslate';
import { useTranslation } from 'react-i18next';
import itemTranslationService from '../services/itemTranslationService';
import StockInForm from './StockInForm';
import BillScanner from './BillScanner';
import { useBmPickedVendor } from '../billMemory/pickedVendor';
import { IoChevronDown } from 'react-icons/io5';
import './InventoryOverview-module.css';
import http from '../services/httpClient';
import { onDataChanged, onScreenResumed } from '../utils/dataChangeSignal';
import UnitMappingService from '../services/unitMappingService';
import StockOutForm from './StockOutForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faFilter, faAngleDown, faLanguage, faArrowLeft, faTrash, faXmark, faPen, faFileExcel, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { StandardSearch, StandardScrollStyles, highlightMatch, standardTextSizes } from '../styles/standardStyles';
import TruckLoader from './TruckLoader';
import SettingsService from '../services/settingsService';
import UnitChangeConfirmationModal from './UnitChangeConfirmationModal';
import LegacyUnitWarning from './LegacyUnitWarning';
import BlockedBySalesNameCheck, { isSalesNameCheckBlock } from './shared/BlockedBySalesNameCheck';
import CrossFamilyConversionModal from './CrossFamilyConversionModal';
import { formatUnitDisplay, isCrossFamilyChange, UNIT_ID_MAP, formatQuantity } from '../utils/unitDisplay';
import { getItemCostBasisPerBaseUnit, pricePerUnitFromBaseCost, formatAutoPriceValue } from '../utils/priceBasis';
// PIECE GUARD §4 — the read-back, asked once per pcs item before its OPENING batch.
// Opening stock has no price history, so this is the only guard that can catch a packet
// entered as one piece before it becomes the benchmark everything else is judged against.
import PieceProfileModal from './PieceProfileModal';
// P3.4 — the SAME interpretation of the modal's result the stock-in door uses. This file
// used to have its own copy of the rule, which is how two doors drift apart.
import { applyPieceProfileResult } from '../utils/pieceProfileResult';
// The opening count draws a line under a day; tracking starts the next one.
import { computeTrackingPeriod, describeTrackingPeriod, formatTrackingDate } from '../utils/trackingPeriod';
import { HEAD_LABEL as EXPENSE_HEAD_LABELS } from './notInRecipe/notInRecipeApi';

const CLOSING_STOCK_CORRECTION_TOGGLE_KEY = 'inventory.closing_stock_correction_enabled';
const getISTDateString = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const isValidISODateString = (value) => {
  const dateValue = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return false;
  const [year, month, day] = dateValue.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === (month - 1)
    && parsed.getUTCDate() === day;
};
const formatClosingStockDateDisplay = (isoDate) => {
  const fallback = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  if (!isValidISODateString(isoDate)) {
    return fallback;
  }

  const [year, month, day] = String(isoDate).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Module-level unit canonicalization — delegates to centralized normalizer
const canonicalGlobalUnit = (unitValue) => UnitMappingService.normalizeUnitSymbol(unitValue);

// Module-level multiplier for converting per-unit prices between units.
// Returns multiplier to apply to a price expressed per `fromUnit` to
// convert it to the equivalent price per `toUnit`.
const getCostUnitMultiplier = (fromUnit, toUnit) => {
  const f = canonicalGlobalUnit(fromUnit);
  const t = canonicalGlobalUnit(toUnit);
  if (!f || !t) return null;
  if (f === t) return 1;
  if (f === 'g' && t === 'kg') return 1000;
  if (f === 'kg' && t === 'g') return 0.001;
  if (f === 'ml' && t === 'L') return 1000;
  if (f === 'L' && t === 'ml') return 0.001;
  return null;
};




/**
 * name -> "500 g / 2 kg" for the preparation lists.
 *
 * The usage endpoint reports preparations two ways: `preparations` (names, which the delete
 * checkboxes and the de-link request body key on) and `preparationDetails` (the same rows with
 * quantities). Keyed by name so a details row can be attached to a list that is still driven by
 * names, without changing what any of those callers send back.
 *
 * The label is per BATCH, not per plate — a preparation's component weights are recorded against
 * its own yield (500 g of this ingredient per 2 kg of Alfredo Sauce), so the denominator has to
 * travel with the number.
 */
const buildPrepQuantityLookup = (details) => {
  const map = new Map();
  (Array.isArray(details) ? details : []).forEach((entry) => {
    if (entry && entry.name && entry.perBatchLabel) {
      map.set(entry.name, entry.perBatchLabel);
    }
  });
  return map;
};

// API calls now use centralized api client
const InventoryOverview = ({ goTo, initialTab = 'overview', initialStockOutSubTab = null, isOnboarding = false, onOnboardingComplete = null, previousScreen = null, fromOnboarding = false, onExitOnboarding = null, tsrLockActive = false, recoveryDate = null, openWastageReasonModal = false, onWastageReasonModalOpened = null, onOpenSalesNameCheck = null }) => {
  const { t, language, changeLanguage } = useTranslate(); // Using custom translation hook
  const { i18n } = useTranslation(); // Using i18next for synchronization
  const prefersReducedMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [timestampNow, setTimestampNow] = useState(() => new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const billScannerRef = useRef(null); // Ref to access BillScanner's resetScanner method

  const [currentFilter, setCurrentFilter] = useState('expiry-nearest');
  // 'non-consumable' filter: the rows the overview keeps out of its main list, and the
  // server's list of which of them were bought in the month still open (since the last
  // closing stock). Nothing else on the screen reads either.
  const [nonConsumableRows, setNonConsumableRows] = useState([]);
  const [nonConsumableOpen, setNonConsumableOpen] = useState({ loading: false, error: null, items: [] });
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Stock Out lock state from child component
  const [stockOutLock, setStockOutLock] = useState({ isActive: false });
  // First-load truck loader state - disabled to remove animation
  const [showTruckLoader, setShowTruckLoader] = useState(false);
  const [truckLeaving, setTruckLeaving] = useState(false);
  const truckMinTimeReachedRef = useRef(false);
  
  // Onboarding stock-in states
  // PIECE GUARD: the deferred onboarding save waiting on a piece definition, or null.
  const [piecePrompt, setPiecePrompt] = useState(null);
  const [pieceProfileSaving, setPieceProfileSaving] = useState(false);
  const [pieceProfileError, setPieceProfileError] = useState(null);
  // Items already asked about this session. Without this, a save that is retried
  // after the profile POST fails would re-open the same modal forever.
  const piecePromptResolvedRef = useRef(new Set());
  const [onboardingStockModal, setOnboardingStockModal] = useState(false);
  const [selectedOnboardingItem, setSelectedOnboardingItem] = useState(null);
  const [negativeFixFlowActive, setNegativeFixFlowActive] = useState(false);
  const [onboardingDrafts, setOnboardingDrafts] = useState({});
  const [onboardingLocked, setOnboardingLocked] = useState(false);
  // WHICH DAY THE OPENING COUNT IS FOR — not the day setup happens to finish.
  // A cafe that counted its shelves on 31 Aug and finishes setup on 10 Sep owes
  // the app ten days of tracking (1 Sep .. 10 Sep); stamping the count "today"
  // erases them and files the opening batch after purchases that predate it.
  // Defaults to today, so an owner counting today sees no change at all.
  const [onboardingClosingDate, setOnboardingClosingDate] = useState(() => getISTDateString());
  // What the app is tracking, once there is a count to track from. Read from
  // the server after onboarding so the number the owner sees is the server's.
  const [trackingPeriod, setTrackingPeriod] = useState(null);
  const [backendOnboardedFlag, setBackendOnboardedFlag] = useState(null);
  const [closingStockEntryDismissed, setClosingStockEntryDismissed] = useState(false);
  const [closingStockCorrectionEnabled, setClosingStockCorrectionEnabled] = useState(false);
  const [closingStockCorrectionDrafts, setClosingStockCorrectionDrafts] = useState({});
  const [selectedClosingStockCorrectionItem, setSelectedClosingStockCorrectionItem] = useState(null);
  const [isApplyingClosingStockCorrections, setIsApplyingClosingStockCorrections] = useState(false);
  const [showClosingStockApplyConfirmModal, setShowClosingStockApplyConfirmModal] = useState(false);
  const [closingStockCorrectionDate, setClosingStockCorrectionDate] = useState(() => getISTDateString());
  const [showAddStockWizard, setShowAddStockWizard] = useState(false);
  const [addStockStep, setAddStockStep] = useState(1);
  const [recipeSearchTerm, setRecipeSearchTerm] = useState('');
  const [hasRecipeSearchInput, setHasRecipeSearchInput] = useState(false);
  const [recipeOptions, setRecipeOptions] = useState([]);
  const [isLoadingRecipes, setIsLoadingRecipes] = useState(false);
  const [preparationOptions, setPreparationOptions] = useState([]);
  // Saved preparations offered as a "Merge into" target. Loaded from
  // /decomposition/preparations rather than reusing preparationOptions above: that list is
  // merged with the GLOBAL template library for the add-stock picker, and a global template has
  // no PersonalizedDecompositions row to link an item to.
  const [mergePrepOptions, setMergePrepOptions] = useState([]);
  // Set when the user picked a preparation rather than an item — the two can share a name.
  const [mergeTargetPrep, setMergeTargetPrep] = useState(null);
  const [isLoadingPreparations, setIsLoadingPreparations] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedPreparation, setSelectedPreparation] = useState(null);
  const [recipeServingQty, setRecipeServingQty] = useState('');
  const [recipeQuantityMode, setRecipeQuantityMode] = useState('plates');
  const [recipeBatchServingsOverride, setRecipeBatchServingsOverride] = useState('');
  const [recipeIngredients, setRecipeIngredients] = useState([]);
  const [isAutoFillingRecipePrices, setIsAutoFillingRecipePrices] = useState(false);
  const [isSavingRecipeStock, setIsSavingRecipeStock] = useState(false);
  const [addStockError, setAddStockError] = useState('');
  const [activePriceTypeDropdownIndex, setActivePriceTypeDropdownIndex] = useState(null);

  const parseFiniteNumber = useCallback((value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const convertQuantity = useCallback((value, fromUnit, toUnit) => {
    const parsed = parseFiniteNumber(value);
    if (parsed === null) return null;
    // canonicalGlobalUnit()/normalizeUnitSymbol() returns lowercase for weight
    // (g/kg) but UPPERCASE 'L' for litres. Compare case-insensitively so the
    // ml<->L branches actually fire instead of silently returning the raw value.
    const from = String(canonicalGlobalUnit(fromUnit) || '').toLowerCase();
    const to = String(canonicalGlobalUnit(toUnit) || '').toLowerCase();
    if (!from || !to || from === to) return parsed;
    if (from === 'g' && to === 'kg') return parsed / 1000;
    if (from === 'kg' && to === 'g') return parsed * 1000;
    if (from === 'ml' && to === 'l') return parsed / 1000;
    if (from === 'l' && to === 'ml') return parsed * 1000;
    return parsed;
  }, [parseFiniteNumber]);

  const formatPriceInputValue = useCallback((value) => {
    if (!Number.isFinite(value)) return '';
    return String(Number(value.toFixed(4)));
  }, []);

  const formatTotalInputValue = useCallback((value) => {
    if (!Number.isFinite(value)) return '';
    return String(Number(value.toFixed(2)));
  }, []);

  const fetchPreviousBatchUnitPrice = useCallback(async (itemId, fallbackUnitPrice = 0) => {
    const fallback = parseFiniteNumber(fallbackUnitPrice);

    try {
      const result = await http.get(`/inventory/items/${itemId}/batches?includeHistorical=true`);
      const batches = Array.isArray(result?.data) ? result.data : [];

      const pricedBatches = batches.filter((batch) => {
        const unitCost = parseFiniteNumber(batch?.unit_cost);
        return unitCost !== null && unitCost > 0;
      });

      const preferredBatch = pricedBatches.find(
        (batch) => String(batch?.invoice_reference || '').toUpperCase() !== 'BATCH-01'
      ) || pricedBatches[0];

      const previousBatchPrice = parseFiniteNumber(preferredBatch?.unit_cost);
      const previousBatchUnit = preferredBatch?.unit_symbol || preferredBatch?.unit || null;
      if (previousBatchPrice !== null && previousBatchPrice > 0) {
        return { price: previousBatchPrice, unit: previousBatchUnit };
      }
    } catch (error) {
      console.warn(`Unable to fetch previous batch price for item ${itemId}:`, error?.message || error);
    }

    return { price: (fallback !== null && fallback > 0 ? fallback : 0), unit: null };
  }, [parseFiniteNumber]);

  // Merge modal states
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeItem, setMergeItem] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [mergeLoading, setMergeLoading] = useState(false);

  // Stock In discard progress confirm modal
  const [showStockInDiscardConfirm, setShowStockInDiscardConfirm] = useState(false);

  // Search handlers
  const clearSearch = () => {
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Real-time refs and state
  const intervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastFetchTime = useRef(0);
  const mountedRef = useRef(true);
  // Tracks whether any modal is currently open so background polling is paused
  const anyModalOpenRef = useRef(false);

  // Stock in sub-tab state
  const [stockInTab, setStockInTab] = useState('scan');
  // Bill Memory (Fix 13 row 7): the vendor picked on the form travels with the next scan and survives the form's remount
  const bmPick = useBmPickedVendor();
  const topTabsRef = useRef(null);
  const stockInTabsRef = useRef(null);
  const [topTabsIndicatorStyle, setTopTabsIndicatorStyle] = useState({ opacity: 0, width: '0px', transform: 'translateX(0px)' });
  const [stockInTabsIndicatorStyle, setStockInTabsIndicatorStyle] = useState({ opacity: 0, width: '0px', transform: 'translateX(0px)' });
  // Auto-correct is now always on (backend Google AI). Removed toggle UI.
  // OCR data state
  const [ocrData, setOcrData] = useState(null);
  const [showStockInForm, setShowStockInForm] = useState(false);
  const [manualHeaderAddTrigger, setManualHeaderAddTrigger] = useState(0);

  const getClosingStockDismissKey = useCallback(() => {
    const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId') || 'unknown';
    return `closingStockEntryDismissed_${businessId}`;
  }, []);

  const getClosingStockShownKey = useCallback(() => {
    const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId') || 'unknown';
    return `closingStockEntryShown_${businessId}`;
  }, []);

  const getClosingStockCorrectionToggleStorageKey = useCallback(() => {
    const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId') || 'unknown';
    return `closingStockCorrectionEnabled_${businessId}`;
  }, []);

  const parseBooleanLike = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || normalized === 't') return true;
      if (normalized === 'false' || normalized === '0' || normalized === 'f') return false;
    }
    return null;
  };

  const onboardingGateStatus = (() => {
    try {
      const rawUser = localStorage.getItem('user');
      if (!rawUser) {
        return {
          hasUser: false,
          onboardedFlag: null,
          onboardingFlag: null,
          blockByUserState: false,
        };
      }
      const parsedUser = JSON.parse(rawUser);

      const onboardedRaw = parsedUser?.isOnboarded ?? parsedUser?.is_onboarded;
      const onboardedFlag = parseBooleanLike(onboardedRaw);
      if (onboardedFlag !== null) {
        return {
          hasUser: true,
          onboardedFlag,
          onboardingFlag: null,
          blockByUserState: onboardedFlag === true,
        };
      }

      const onboardingRaw =
        parsedUser?.onboarding ??
        parsedUser?.isOnboarding ??
        parsedUser?.onBoarding ??
        parsedUser?.onboarding_active ??
        parsedUser?.onboardingActive;
      const onboardingFlag = parseBooleanLike(onboardingRaw);
      return {
        hasUser: true,
        onboardedFlag: null,
        onboardingFlag,
        blockByUserState: onboardingFlag === true,
      };
    } catch (e) {
      return {
        hasUser: false,
        onboardedFlag: null,
        onboardingFlag: null,
        blockByUserState: false,
      };
    }
  })();

  useEffect(() => {
    const fetchBackendOnboardedStatus = async () => {
      try {
        const result = await http.get('/auth/user');
        const rawFlag =
          result?.isOnboarded ??
          result?.is_onboarded ??
          result?.business?.isOnboarded ??
          result?.business?.is_onboarded;
        setBackendOnboardedFlag(parseBooleanLike(rawFlag));
      } catch (error) {
        // If endpoint is unavailable, keep fallback from local user object.
        setBackendOnboardedFlag(null);
      }
    };

    fetchBackendOnboardedStatus();
  }, []);

  useEffect(() => {
    const loadClosingStockCorrectionToggle = async () => {
      const localStorageKey = getClosingStockCorrectionToggleStorageKey();
      const localOverride = localStorage.getItem(localStorageKey);
      if (localOverride === 'true' || localOverride === 'false') {
        setClosingStockCorrectionEnabled(localOverride === 'true');
      }

      try {
        const bId = UnitMappingService.getBusinessId();
        if (!bId) return;
        const result = await http.get(`/settings/${bId}?keys=${encodeURIComponent(CLOSING_STOCK_CORRECTION_TOGGLE_KEY)}`);
        const records = Array.isArray(result?.data) ? result.data : [];
        const setting = records.find((entry) => entry.key === CLOSING_STOCK_CORRECTION_TOGGLE_KEY);
        if (!setting) return;
        const enabled = setting.value === true || String(setting.value).toLowerCase() === 'true';
        setClosingStockCorrectionEnabled(enabled);
        localStorage.setItem(localStorageKey, enabled ? 'true' : 'false');
      } catch (error) {
        console.warn('⚠️ Failed to load closing stock correction setting, using local fallback.', error?.message || error);
      }
    };

    loadClosingStockCorrectionToggle();
  }, [getClosingStockCorrectionToggleStorageKey]);

  useEffect(() => {
    if (closingStockCorrectionEnabled) return;
    setClosingStockCorrectionDrafts({});
    setSelectedClosingStockCorrectionItem(null);
    setShowClosingStockApplyConfirmModal(false);
  }, [closingStockCorrectionEnabled]);

  const shouldBlockByUserOnboardingState =
    backendOnboardedFlag !== null
      ? backendOnboardedFlag === true
      : onboardingGateStatus.blockByUserState;

  // Block modal only when account is onboarded=true.
  const shouldBlockClosingStockModal = shouldBlockByUserOnboardingState;

  // Fail-safe: if onboarding becomes active, force-close the closing-stock modal.
  useEffect(() => {
    if (shouldBlockClosingStockModal && onboardingStockModal && !negativeFixFlowActive) {
      setOnboardingStockModal(false);
      setSelectedOnboardingItem(null);
    }
  }, [shouldBlockClosingStockModal, onboardingStockModal, negativeFixFlowActive]);

  useEffect(() => {
    try {
      // Never show closing stock prompt whenever modal opening is blocked.
      if (shouldBlockClosingStockModal) {
        setClosingStockEntryDismissed(true);
        return;
      }

      const dismissedRaw = localStorage.getItem(getClosingStockDismissKey());
      if (dismissedRaw === 'true') {
        setClosingStockEntryDismissed(true);
        return;
      }

      // Show once per business in post-onboarding mode; then keep dismissed on future opens.
      const shownRaw = localStorage.getItem(getClosingStockShownKey());
      if (shownRaw === 'true') {
        setClosingStockEntryDismissed(true);
      } else {
        setClosingStockEntryDismissed(false);
        localStorage.setItem(getClosingStockShownKey(), 'true');
      }
    } catch (e) {
      setClosingStockEntryDismissed(false);
    }
  }, [shouldBlockClosingStockModal, getClosingStockDismissKey, getClosingStockShownKey]);

  const dismissClosingStockEntry = useCallback(() => {
    try {
      localStorage.setItem(getClosingStockDismissKey(), 'true');
    } catch (e) {
      // no-op
    }
    setClosingStockEntryDismissed(true);
    setOnboardingStockModal(false);
    setSelectedOnboardingItem(null);
  }, [getClosingStockDismissKey]);

  const resolveTabIndicatorStyle = (tabsRoot, activeSelector) => {
    if (!tabsRoot) {
      return { opacity: 0, width: '0px', transform: 'translateX(0px)' };
    }

    const activeTabNode = tabsRoot.querySelector(activeSelector);
    if (!activeTabNode) {
      return { opacity: 0, width: '0px', transform: 'translateX(0px)' };
    }

    return {
      width: `${activeTabNode.offsetWidth}px`,
      transform: `translateX(${activeTabNode.offsetLeft}px)`,
      opacity: 1
    };
  };

  // Expanded cards state
  const [expandedCards, setExpandedCards] = useState(new Set());
  const expandedCardsRef = useRef(new Set());
  useEffect(() => {
    expandedCardsRef.current = expandedCards;
  }, [expandedCards]);
  const previousQuantityByItemRef = useRef({});

  // Delete item state
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  const [deleteCheckLoading, setDeleteCheckLoading] = useState(false);
  const [deleteUsageDetails, setDeleteUsageDetails] = useState(null);
  const [deleteCheckedIds, setDeleteCheckedIds] = useState(new Set());
  const [deleteRemoving, setDeleteRemoving] = useState(false);
  const [deleteCheckedPrepNames, setDeleteCheckedPrepNames] = useState(new Set());
  const [deleteRemovingPreps, setDeleteRemovingPreps] = useState(false);
  const [deleteCheckedCompIds, setDeleteCheckedCompIds] = useState(new Set());
  const [deleteRemovingComp, setDeleteRemovingComp] = useState(false);
  // Accumulate recipe/prep/complimentary link rows removed before the final delete so they can be
  // merged into the structural_action_log snapshot for automatic revert restoration.
  const deletePendingRemovedLinksRef = useRef([]);
  const deletePendingRemovedPrepsRef = useRef([]);
  const deletePendingRemovedCompRef = useRef([]);

  // View Related Items state (triggered by double click)
  const [viewRelatedConfirmItem, setViewRelatedConfirmItem] = useState(null);
  const [viewRelatedCheckLoading, setViewRelatedCheckLoading] = useState(false);
  const [viewRelatedUsageDetails, setViewRelatedUsageDetails] = useState(null);
  const [viewRelatedCheckedIds, setViewRelatedCheckedIds] = useState(new Set());
  const [viewRelatedRemoving, setViewRelatedRemoving] = useState(false);
  const viewRelatedRequestSeqRef = useRef(0);

  // State for batch details
  const [batchDetails, setBatchDetails] = useState({});
  const [loadingBatches, setLoadingBatches] = useState(new Set());

  // Voice recording state - pause fetches during voice input for instant UI
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [batchErrors, setBatchErrors] = useState({}); // itemId -> error message 
  const [supplierConversionMap, setSupplierConversionMap] = useState({}); // itemName_unit -> {factor, toUnitDisplay}
  // Auto-update state
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true);
  // Filter dropdown state
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef(null);
  // Language dropdown state
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  // Local language for item translation only (independent from global app language)
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [translatedNames, setTranslatedNames] = useState({}); // itemId -> translated name
  const [translatingItems, setTranslatingItems] = useState(new Set());
  const [itemExpandState, setItemExpandState] = useState({}); // Track which items are expanded
  const languageDropdownRef = useRef(null);
  // Inventory edit actions (rename/merge/unit) from Overview header
  const [showEditActionsMenu, setShowEditActionsMenu] = useState(false);
  const editActionsRef = useRef(null);
  const [showInventoryEditModal, setShowInventoryEditModal] = useState(false);
  const [inventoryEditMode, setInventoryEditMode] = useState(null); // 'rename' | 'merge' | 'unit'
  const [inventoryEditForm, setInventoryEditForm] = useState({
    currentName: '',
    newName: '',
    mergeTargetName: '',
    currentUnit: '',
    newUnit: '',
    conversionRate: ''
  });
  const [inventoryEditError, setInventoryEditError] = useState('');
  const [inventoryEditLoading, setInventoryEditLoading] = useState(false);
  const [inventoryEditUsageDetails, setInventoryEditUsageDetails] = useState(null);
  const [inventoryEditUsageLoading, setInventoryEditUsageLoading] = useState(false);
  const [inventoryEditUsageError, setInventoryEditUsageError] = useState('');
  const [showInventoryRenameConfirm, setShowInventoryRenameConfirm] = useState(false);
  const [showInventoryNameSuggestions, setShowInventoryNameSuggestions] = useState(false);
  const [showMergeTargetSuggestions, setShowMergeTargetSuggestions] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [showCurrentUnitDropdown, setShowCurrentUnitDropdown] = useState(false);
  const inventoryEditUsageRequestKeyRef = useRef('');

  // Unit change confirmation modal state (global standardization)
  const [showUnitConfirmModal, setShowUnitConfirmModal] = useState(false);
  const [unitChangePreviewData, setUnitChangePreviewData] = useState(null);
  const [pendingOverviewUnitChange, setPendingOverviewUnitChange] = useState(null);
  const [isApplyingOverviewUnitChange, setIsApplyingOverviewUnitChange] = useState(false);
  
  // Cross-family conversion modal state (Strict 5 Unit Enforcement)
  const [showCrossFamilyModal, setShowCrossFamilyModal] = useState(false);
  const [crossFamilyModalData, setCrossFamilyModalData] = useState(null);
  
  // Keep a mirrored search value for filtering/highlighting without introducing input lag
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // Track viewport to apply mobile-only formatting without affecting desktop
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setHeaderScrolled((window.scrollY || 0) > 4);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const updateTabIndicators = () => {
      setTopTabsIndicatorStyle(resolveTabIndicatorStyle(topTabsRef.current, '.tab.active'));
      setStockInTabsIndicatorStyle(resolveTabIndicatorStyle(stockInTabsRef.current, '.sub-tab.active'));
    };

    const frameId = window.requestAnimationFrame(updateTabIndicators);
    window.addEventListener('resize', updateTabIndicators);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateTabIndicators);
    };
  }, [activeTab, stockInTab, i18n.language]);

  // Helper to abbreviate units — always normalize to standard symbols
  const formatUnit = (unit) => {
    if (!unit) return unit;
    return UnitMappingService.normalizeUnitSymbol(unit);
  };

  // Smart quantity formatter using Strict 5 Unit System
  // Wraps formatUnitDisplay() from unitDisplay.js for auto-scaling display logic
  // < 1000 = g/ml, >= 1000 = kg/L, pcs always as pcs
  // Canonical, app-wide display: full names (Gram/Kilogram/Millilitre/Litre/Pcs), auto-scaled.
  const formatQuantityDisplay = (qty, unit) => {
    if (qty === null || qty === undefined) return '0';
    const num = parseFloat(qty);
    if (!Number.isFinite(num)) return '0';
    return formatQuantity(num, unit);
  };

  // Helper to normalize various unit formats to strict units (g, kg, ml, L, pcs)
  const normalizeUnitToStrict = (unit) => {
    if (!unit) return 'g'; // Default to grams
    const u = String(unit).toLowerCase().trim();
    
    // Weight units
    if (['g', 'gram', 'grams'].includes(u)) return 'g';
    if (['kg', 'kilogram', 'kilograms'].includes(u)) return 'kg';
    
    // Volume units
    if (['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres'].includes(u)) return 'ml';
    if (['l', 'liter', 'liters', 'litre', 'litres'].includes(u)) return 'L';
    
    // Count units
    if (['piece', 'pieces', 'pc', 'pcs'].includes(u)) return 'pcs';
    
    // For unrecognized units, try to normalize via service or return as-is
    return UnitMappingService.normalizeUnitSymbol(unit) || unit;
  };

  const normalizeItemKey = (name) => (name || '').toString().toLowerCase().trim();
  
  const normalizeUnitDisplay = (unit) => UnitMappingService.normalizeUnitSymbol(unit) || unit || '';
  
  const isContainerUnit = (unit) => {
    const normalized = String(unit || '').toLowerCase().trim();
    return ['box', 'packet', 'pack', 'bag', 'dozen', 'carton', 'can', 'bottle', 'jar', 'tin', 'crate', 'bundle'].includes(normalized);
  };

  // Price per MASTER unit (kg / L / pcs) from the item's latest purchase batch.
  // InventoryBatches.unit_cost is per the item's STOCK unit, which may be a sub-unit
  // (g/ml) — pairing that price with a master-unit label without rescaling would be
  // wrong by 1000x. Everything is routed through the base unit (g/ml/pcs) first.
  const MASTER_UNIT_BY_BASE = { g: { label: 'kg', perBase: 1000 }, ml: { label: 'L', perBase: 1000 }, pcs: { label: 'pcs', perBase: 1 } };

  const getMasterUnitPrice = (item) => {
    const cost = Number(item?.latestUnitCost);
    if (!Number.isFinite(cost) || cost <= 0) return null;

    const stockUnit = item?.stockUnitSymbol || item?.rawUnit || item?.unit;
    const perStock = UnitMappingService.factorToBase(stockUnit); // base units per stock unit
    if (!(perStock > 0)) return null;
    const costPerBase = cost / perStock;

    const base = UnitMappingService.baseUnitOf(stockUnit);
    // Custom/kitchen units (cup, box, …) have no master unit — price them as themselves.
    const master = MASTER_UNIT_BY_BASE[base] || { label: normalizeUnitDisplay(stockUnit) || 'unit', perBase: 1 };
    const price = costPerBase * master.perBase;
    if (!Number.isFinite(price) || price <= 0) return null;

    return {
      price,
      unit: master.label,
      // Sub-rupee prices (spices by the piece, say) would round away to ₹0.
      label: `₹${price >= 100 ? Math.round(price).toLocaleString('en-IN') : price.toFixed(2)}/${master.label}`
    };
  };

  const abbreviateCategory = (category) => {
    if (!category) return '';
    const cat = String(category).trim();
    if (cat === 'Uncategorized') return 'Uncatog';
    if (cat === 'NON CONSUMABLE') return 'NC';
    return cat;
  };

  useEffect(() => {
    setDebouncedSearchTerm(searchTerm);
  }, [searchTerm]);

  // Match card-accordion behavior: collapse expanded cards while actively filtering
  useEffect(() => {
    const isSearching = Boolean(searchTerm && searchTerm.trim().length > 0);
    if (!isSearching) return;

    // Avoid redundant state churn while typing if cards are already collapsed.
    setExpandedCards((prev) => (prev.size === 0 ? prev : new Set()));
    setItemExpandState((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [searchTerm]);
  
  useEffect(() => {
    const fetchSupplierConversions = async () => {
      try {
        const businessId = UnitMappingService.getBusinessId();
        if (!businessId) return;

        const conversions = await UnitMappingService.getSupplierConversions(businessId);
        const map = {};
        if (Array.isArray(conversions)) {
          conversions.forEach(conv => {
            const itemName = normalizeItemKey(conv.item || conv.description?.match(/Supplier conversion:\s*(.+?)\s*-/)?.[1]);
            const fromUnit = String(conv.containerType || conv.from_unit_symbol || '').toLowerCase().trim();
            if (!itemName || !fromUnit) return;
            const factor = parseFloat(conv.quantity || conv.conversion_factor || 0);
            const toUnitDisplay = conv.unit || conv.to_unit_symbol || '';
            if (factor > 0) {
              map[`${itemName}_${fromUnit}`] = { factor, toUnitDisplay };
            }
          });
        }
        setSupplierConversionMap(map);
      } catch (error) {
        console.error('❌ Failed to fetch supplier conversions:', error);
      }
    };

    fetchSupplierConversions();
  }, []);
  
  // Spec §10.2/§10.4: the opening-stock routes answer 409 SALES_NAME_CHECK_IN_PROGRESS
  // while an onboarding Sales Name Check is in progress; the page then says so.
  const [blockedBySnc, setBlockedBySnc] = useState(false);

  // Fetch onboarding ingredients from item mapping
  const fetchOnboardingIngredients = useCallback(async () => {
    try {
      const bId = UnitMappingService.getBusinessId();
      const currentLanguage = language || localStorage.getItem('selectedLanguage') || 'en';
      
      const result = await http.get(`/stock-in/v2/onboarding/ingredients?businessId=${bId}&language=${currentLanguage}`);
      
      if (result.success && result.ingredients) {
        console.log(`📋 Loaded ${result.ingredients.length} ingredients from item mapping`);
        return result.ingredients;
      }
      return [];
    } catch (error) {
      if (isSalesNameCheckBlock(error)) setBlockedBySnc(true);
      console.error('❌ Error fetching onboarding ingredients:', error);
      return [];
    }
  }, [language]);
  
  // Fetch onboarding drafts (already entered stocks)
  const fetchOnboardingDrafts = useCallback(async () => {
    try {
      const bId = UnitMappingService.getBusinessId();
      const result = await http.get(`/stock-in/v2/onboarding/drafts?businessId=${bId}`);
      
      if (result.success && result.drafts) {
        console.log(`📋 [FETCH DRAFTS] Received ${result.drafts.length} draft(s) from backend`);
        result.drafts.forEach(draft => {
          if (draft.unit_conversion && draft.original_unit) {
            console.log(`   ✅ ${draft.item_name}: raw=${draft.raw_quantity} ${draft.original_unit}, converted=${draft.quantity} ${draft.unit}, factor=${draft.unit_conversion}`);
          } else {
            console.log(`   ℹ️  ${draft.item_name}: ${draft.quantity} ${draft.unit} (no conversion)`);
          }
        });
        
        const draftsMap = {};
        result.drafts.forEach(draft => {
          draftsMap[draft.item_id] = draft;
        });
        setOnboardingDrafts(draftsMap);
        return draftsMap;
      }
      return {};
    } catch (error) {
      if (isSalesNameCheckBlock(error)) setBlockedBySnc(true);
      console.error('❌ Error fetching onboarding drafts:', error);
      return {};
    }
  }, []);
  
  // Check if onboarding is locked
  const checkOnboardingLock = useCallback(async () => {
    try {
      const bId = UnitMappingService.getBusinessId();
      const result = await http.get(`/stock-in/v2/onboarding/status?businessId=${bId}`);
      
      if (result.success) {
        setOnboardingLocked(result.is_locked);
        return result.is_locked;
      }
      return false;
    } catch (error) {
      if (isSalesNameCheckBlock(error)) setBlockedBySnc(true);
      console.error('❌ Error checking onboarding lock:', error);
      return false;
    }
  }, []);
  // Optimized fetch with abort controller and caching
  const fetchInventoryData = useCallback(async (showLoader = false) => {
    // Skip background polls when any modal is open to prevent jarring refreshes
    if (!showLoader && anyModalOpenRef.current) {
      return;
    }
    // Skip fetches during voice recording for instant voice UI
    if (isVoiceRecording) {
      return;
    }
    // Prevent duplicate *background* polls firing within 1s of each other.
    // Explicit/loader fetches (mount, language change, manual refresh) must never
    // be swallowed — otherwise the initial load can be dropped when the mount and
    // language effects both fire on first render.
    const now = Date.now();
    if (!showLoader && now - lastFetchTime.current < 1000) return;
    lastFetchTime.current = now;
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    try {
      if (showLoader) setIsLoading(true);
      setError(null);
      const bId = UnitMappingService.getBusinessId();
      const currentLanguage = language || localStorage.getItem('selectedLanguage') || 'en';
      
      // During onboarding, fetch ingredients from item mapping
      if (fromOnboarding && isOnboarding && !onboardingLocked) {
        const ingredients = await fetchOnboardingIngredients();
        const drafts = await fetchOnboardingDrafts();
        
        // Process ingredients with draft data
        let processedData = ingredients.map(item => {
          const draft = drafts[item.item_id];
          const quantity = draft ? parseFloat(draft.quantity) : (parseFloat(item.quantity) || 0);
          const unitCost = draft ? parseFloat(draft.unit_price) : (parseFloat(item.unit_cost) || 0);
          const displayUnit = UnitMappingService.normalizeUnitSymbol(draft?.unit || item.unit_symbol || item.unit || 'g');

          return {
            id: item.item_id,
            name: item.item_name,
            quantity: quantity,
            quantityDisplay: formatQuantityDisplay(quantity, displayUnit),
            unit: displayUnit,
            category: item.category,
            status: 'No Expiry',
            daysToExpiry: 999,
            unitCost: unitCost,
            batchNumber: draft ? 'DRAFT' : (quantity > 0 ? 'STOCK IN' : 'Not Entered'),
            supplier: draft ? 'Closing Stock' : (quantity > 0 ? 'Stock In' : '-'),
            stockLevel: quantity === 0 ? 'out-of-stock' : 'adequate',
            needs_stock: quantity === 0,
            standard_unit_id: item.standard_unit_id,
            created_at: item.created_at,
            hasDraft: !!draft,
            is_duplicate_override: item.is_duplicate_override || false
          };
        });
        setInventoryItems(processedData);
        if (showLoader) setIsLoading(false);
        return;
      }
      
      // Normal flow: fetch regular inventory
      const result = await http.get(`/stock-in/v2/inventory/overview?businessId=${bId}&includeInactive=true&language=${currentLanguage}`, {
        headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
        signal: abortControllerRef.current.signal,
      });
      
      // Also fetch onboarding drafts to show as closing stock if not finalized
      await fetchOnboardingDrafts();
      
      if (result.success && result.data) {
        // Process the data with real-time calculations and automatic updates
        let processedData = result.data.map(item => {
          const now = new Date();
          const expiryDate = item.expiry_date ? new Date(item.expiry_date) : null;
          const daysToExpiry = expiryDate ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)) : 999;        
          // Automatic status calculation
          let autoStatus = 'Good';
          let urgencyLevel = 'normal';
          
          if (daysToExpiry < 0) {
            autoStatus = 'Expired';
            urgencyLevel = 'critical';
          } else if (daysToExpiry === 0) {
            autoStatus = 'Expires Today';
            urgencyLevel = 'critical';
          } else if (daysToExpiry === 1) {
            autoStatus = 'Expires Tomorrow';
            urgencyLevel = 'urgent';
          } else if (daysToExpiry <= 3) {
            autoStatus = `Expires in ${daysToExpiry} days`;
            urgencyLevel = 'urgent';
          } else if (daysToExpiry <= 7) {
            autoStatus = `Expires in ${daysToExpiry} days`;
            urgencyLevel = 'warning';
          } else if (daysToExpiry <= 30) {
            autoStatus = 'Fresh';
            urgencyLevel = 'normal';
          }
          // Automatic stock level calculation
          const quantity = parseFloat(item.quantity) || 0;
          let displayQuantity = quantity;
          let displayUnit = UnitMappingService.normalizeUnitSymbol(item.unit || 'units');
          let conversionAppliedForDisplay = false;
          const displayUnitLower = String(displayUnit || '').toLowerCase().trim();
          if (isContainerUnit(displayUnitLower)) {
            const convKey = `${normalizeItemKey(item.item_name)}_${displayUnitLower}`;
            const conv = supplierConversionMap[convKey];
            if (conv && conv.factor > 0) {
              displayQuantity = quantity * conv.factor;
              displayUnit = conv.toUnitDisplay || displayUnit;
              conversionAppliedForDisplay = true;
            }
          }
          const minStock = item.minimum_stock_level || 0;
          const maxStock = item.maximum_stock_level || 0;        
          let stockStatus = 'adequate';
          if (quantity <= 0) {
            stockStatus = 'out-of-stock';
          } else if (quantity <= minStock) {
            stockStatus = 'low';
          } else if (maxStock > 0 && quantity >= maxStock) {
            stockStatus = 'overstocked';
          }
          return {
            id: item.item_id,
            name: item.item_name,
            quantity: displayQuantity,
            rawQuantity: quantity,
            quantityDisplay: formatQuantityDisplay(displayQuantity, displayUnit),
            unit: displayUnit,
            rawUnit: item.unit || displayUnit,
            standard_unit_id: item.standard_unit_id, // Preserve for cross-family conversion modal
            category: item.category || 'Uncategorized',
            // The classification, not the (translated) label — see isNonConsumableRow below.
            categoryType: item.category_type || null,
            status: autoStatus,
            expiryDate: item.expiry_date,
            daysToExpiry: daysToExpiry,
            stockLevel: stockStatus,
            batchNumber: item.batch_number || 'N/A',
            lastUpdated: new Date().toISOString(), // Always current time for real-time feel
            minStock: minStock,
            maxStock: maxStock,
            urgencyLevel: urgencyLevel,
            isExpired: daysToExpiry < 0,
            isExpiringSoon: daysToExpiry <= 3 && daysToExpiry >= 0,
            isFresh: daysToExpiry > 7,
            // FIFO information - removed from here, will be set per batch
            fifoStatus: 'NORMAL',
            procuredDate: item.received_date || item.created_at,
            supplier: item.supplier || item.supplier_name || item.vendor || item.vendor_name || 'N/A',
            // Additional fields for filters
            unitCost: Number(item.unit_cost || 0),
            // Latest purchase price, per STOCK unit — the card rescales it to the
            // master unit (kg/L/pcs) via getMasterUnitPrice.
            latestUnitCost: Number(item.latest_unit_cost || 0),
            latestUnitCostDate: item.latest_unit_cost_date || null,
            // Untranslated symbol: the basis `latestUnitCost` is quoted in.
            stockUnitSymbol: item.stock_unit_symbol || item.unit_symbol || item.unit || '',
            createdAt: item.created_at ? new Date(item.created_at) : null,
            isNewlyAdded: item.is_newly_added === true,
            // Backend flag: no menu item, preparation or complimentary recipe uses this
            // ingredient. Undefined on payloads from older builds — the 'not-associated'
            // filter treats only an explicit true as unassociated so a missing column
            // shows nothing rather than flagging the entire inventory.
            isUnassociated: item.is_unassociated === true
          };
        });
        // Removal strategy for expired items:
        // 1. Only purge items that are expired more than 5 days ago
        // 2. Keep items with daysToExpiry = 999 (no expiry date) - these are raw ingredients or non-perishables
        // 3. Keep items with quantity > 0 (items with active batches)
        // 4. Remove items that show as expired but have quantity = 0 (all batches removed)
        processedData = processedData.filter(it => {
          // Remove expired items with no stock (all batches already removed)
          if (it.daysToExpiry < 0 && it.quantity === 0) {
            return false;
          }
          // Keep items with no expiry (999) or items not yet expired (-5 days buffer)
          return it.daysToExpiry === 999 || it.daysToExpiry >= -5;
        });

        // FIXED: Simplified consumable filtering - show ALL inventory items
        // The backend already filters by business_id, so show everything for that business
        // Consumable filtering is now optional and based on explicit backend flags only
        
        // Filter out items explicitly marked as non-consumable via category — kept aside
        // (not discarded) for the 'Non Consumable' filter, which is the only place they show.
        // By the shelf's TYPE as well as its name: the name arrives translated in Telugu and
        // Hindi (and some businesses seeded it under another name), so a name-only test let
        // those rows leak into this list AND the filter. The type means the same everywhere,
        // and the name normalization is the backend's (utils/nonConsumableCategory) — the
        // filter's server side uses this exact rule, so each item lives in one place.
        const isNonConsumableRow = (it) => it.categoryType === 'non_consumable'
          || String(it.category || '').replace(/[^a-z0-9]/gi, '').toLowerCase() === 'nonconsumable';
        setNonConsumableRows(processedData.filter(isNonConsumableRow));
        processedData = processedData.filter(it => !isNonConsumableRow(it));

        const anyFlagged = processedData.some(it => it.is_consumable === true || it.isConsumable === true || it.consumable === true);
        if (anyFlagged) {
          // If any items have explicit consumable flag, filter by that flag
          processedData = processedData.filter(it => (it.is_consumable === true) || (it.isConsumable === true) || (it.consumable === true));
        }
        // Otherwise show ALL items (removed keyword filtering to show Uncategorized items too)

        setInventoryItems(processedData);
      } else {
        throw new Error(result.error || 'Invalid response format');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching inventory data:', error);
        setError((error && typeof error.message === 'string') ? error.message : 'Failed to fetch inventory data');

        // Keep existing data on error
        setInventoryItems(prev => prev);
      }
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [language, supplierConversionMap, isVoiceRecording]); // Listen to language changes and conversion updates

  // Store fetchInventoryData in ref for stable access from effects/intervals
  const fetchInventoryDataRef = useRef(fetchInventoryData);
  useEffect(() => {
    fetchInventoryDataRef.current = fetchInventoryData;
  }, [fetchInventoryData]);

  // Listen for language changes and refetch data
  useEffect(() => {
    fetchInventoryDataRef.current(true); // Refetch with loader to show the change is happening
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Setup real-time updates with minimal latency
  useEffect(() => {
    mountedRef.current = true;

    // Initial fetch
    fetchInventoryDataRef.current(true);

    // Background polling is OFF (INVENTORY_POLL_MS = 0).
    //
    // This used to re-fetch the whole inventory list every 2s whenever any item
    // was low-stock or expiring within a day — which in a working kitchen is
    // essentially always — with no check for whether the tab was even on screen.
    // Neon bills compute by the second and suspends after 5 minutes of silence,
    // so this single screen, left open on a counter tablet, kept the database
    // billed around the clock. It was the largest inventory-side cost driver.
    //
    // Nothing is lost by stopping it: this component already calls
    // fetchInventoryData() directly after every action that changes stock
    // (stock-in, stock-out, wastage, conversions, closing-stock edits — ~20 call
    // sites), so the user who made a change always sees it immediately. The poll
    // existed only to notice changes made by ANOTHER device, and the
    // visibility handler below covers that the moment this tab is looked at
    // again — at one query per return instead of 1,800 per hour.
    //
    // To re-enable a slow safety net, set INVENTORY_POLL_MS to e.g. 60000.
    // Do not put it below 60s, and never back to 2s.
    const INVENTORY_POLL_MS = 0;

    const runBackgroundFetch = () => {
      if (!mountedRef.current) return;
      // Skip while a card is expanded (prevents flicker) or the tab is hidden.
      if (expandedCardsRef.current.size > 0) return;
      if (document.visibilityState !== 'visible') return;
      fetchInventoryDataRef.current(false);
    };

    const scheduleNextUpdate = () => {
      if (!mountedRef.current || !INVENTORY_POLL_MS) return;
      intervalRef.current = setTimeout(() => {
        runBackgroundFetch();
        scheduleNextUpdate();
      }, INVENTORY_POLL_MS);
    };

    // Catch up on other devices' changes when this tab comes back into view.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') runBackgroundFetch();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    scheduleNextUpdate();
    // Cleanup
    return () => {
      mountedRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - fetchInventoryDataRef.current always has latest version
  
  // ALWAYS load onboarding drafts on mount - they contain closing stock data
  // This ensures closing stock entered during onboarding is visible even after onboarding completes
  useEffect(() => {
    const loadDrafts = async () => {
      try {
        const drafts = await fetchOnboardingDrafts();
        console.log('📋 Onboarding drafts loaded:', Object.keys(drafts).length, 'items');
      } catch (error) {
        console.error('❌ Failed to load onboarding drafts:', error);
      }
    };
    loadDrafts();
  }, [fetchOnboardingDrafts]);

  // How long the app has been tracking. Read from the server rather than
  // derived here: the closing count that anchors it can be re-filed for a
  // different day from the Closing Stock page, and a locally cached guess
  // would then quietly disagree with every report built on the same anchor.
  //
  // Asked DURING onboarding too, not only after: a business whose opening
  // stock was already committed — through the Closing Stock page, or seeded —
  // has an anchor but no drafts, and the onboarding footer needs to know that
  // before it decides whether the step is finishable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await http.get('/onboarding/tracking-period');
        if (!cancelled && result?.success && result.data?.trackingStartDate) {
          setTrackingPeriod(result.data);
          // The count is already on record; its date is not ours to re-pick.
          if (result.data.closingStockDate) {
            setOnboardingClosingDate(result.data.closingStockDate);
          }
        }
      } catch (error) {
        // A business with no count yet has no period — not worth a banner.
        console.debug('Tracking period unavailable:', error.message);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Onboarding lock status.
  //
  // This used to re-ask the server every 5 seconds — 720 queries an hour — to
  // notice a single event: the sales report being submitted, possibly in another
  // tab. The lock is just a flag that a write sets, so the check now runs when
  // that write happens rather than on a clock:
  //
  //   - onDataChanged            any successful write, this tab or another tab
  //   - daily-operations:refresh the app's existing "a daily checkpoint moved"
  //                              event, which the sales report already fires
  //   - onScreenResumed          coming back to this tab, which is the one thing
  //                              that must be true before a stale lock could be
  //                              seen, and covers a submission from another device
  useEffect(() => {
    if (!(fromOnboarding && isOnboarding)) return undefined;

    checkOnboardingLock();

    const recheck = () => { checkOnboardingLock(); };
    const stopListening = onDataChanged(recheck, { debounceMs: 300 });
    const stopResuming = onScreenResumed(recheck, { minGapMs: 2000 });
    window.addEventListener('daily-operations:refresh', recheck);

    return () => {
      stopListening();
      stopResuming();
      window.removeEventListener('daily-operations:refresh', recheck);
    };
  }, [fromOnboarding, isOnboarding, checkOnboardingLock]);

  // Hide the truck loader once initial data (or an error) arrives
  // Ensure the truck animation runs at least 4s before leaving
  useEffect(() => {
    if (!showTruckLoader) return;
    const timer = setTimeout(() => {
      truckMinTimeReachedRef.current = true;
      // If data already loaded, initiate exit now
      if (!isLoading) {
        setTruckLeaving(true);
        const t2 = setTimeout(() => setShowTruckLoader(false), 1300);
        return () => clearTimeout(t2);
      }
    }, 4000); // Changed from 5000 to 4000 (4 seconds)
    return () => clearTimeout(timer);
  }, [showTruckLoader, isLoading]);

  // When data loads after the 5s gate, start the drive-out
  useEffect(() => {
    if (!isLoading && showTruckLoader && truckMinTimeReachedRef.current) {
      setTruckLeaving(true);
      const t = setTimeout(() => setShowTruckLoader(false), 1300);
      return () => clearTimeout(t);
    }
  }, [isLoading, showTruckLoader]);

  // Keep the "Data as of" timestamp ticking every second
  useEffect(() => {
    // Only update timestamp when no card is expanded (avoids re-render flicker on open cards)
    const id = setInterval(() => {
      if (!anyModalOpenRef.current && expandedCards.size === 0) {
        setTimestampNow(new Date());
      }
    }, 5000); // Reduced to every 5s and skipped while cards are open
    return () => clearInterval(id);
  }, [expandedCards]);

  // Keep a stable ref to inventoryItems so the expiry clock doesn't need it in deps
  const inventoryItemsRef = useRef([]);
  useEffect(() => {
    inventoryItemsRef.current = inventoryItems;
  }, [inventoryItems]);

  // Real-time clock for automatic expiry calculations
  // Uses inventoryItemsRef so the effect never needs to re-run when items change
  useEffect(() => {
    const clockInterval = setInterval(() => {
      // Skip costly re-render when any modal is currently open or a card is expanded
      if (anyModalOpenRef.current || expandedCards.size > 0) return;
      setLastUpdateTime(new Date());
      // Recalculate expiry status for all items automatically
      const currentItems = inventoryItemsRef.current;
      if (currentItems.length > 0 && autoUpdateEnabled) {
        const now = new Date();
        let hasChanges = false;
        const updatedItems = currentItems.map(item => {
          if (!item.expiryDate) return item;
          
          const expiryDate = new Date(item.expiryDate);
          const daysToExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
          
          // Only recompute if days changed
          if (daysToExpiry === item.daysToExpiry) return item;
          hasChanges = true;

          let autoStatus = 'Good';
          let urgencyLevel = 'normal';

          if (daysToExpiry < 0) {
            autoStatus = 'Expired';
            urgencyLevel = 'critical';
          } else if (daysToExpiry === 0) {
            autoStatus = 'Expires Today';
            urgencyLevel = 'critical';
          } else if (daysToExpiry === 1) {
            autoStatus = 'Expires Tomorrow';
            urgencyLevel = 'urgent';
          } else if (daysToExpiry <= 3) {
            autoStatus = `Expires in ${daysToExpiry} days`;
            urgencyLevel = 'urgent';
          } else if (daysToExpiry <= 7) {
            autoStatus = `Expires in ${daysToExpiry} days`;
            urgencyLevel = 'warning';
          }
          return {
            ...item,
            daysToExpiry,
            status: autoStatus,
            urgencyLevel,
            fifoStatus: 'NORMAL',
            isExpired: daysToExpiry < 0,
            isExpiringSoon: daysToExpiry <= 3 && daysToExpiry >= 0
          };
        });
        if (!hasChanges) return; // Nothing actually changed — skip re-render
        // Purge items expired more than 5 days ago
        const purged = updatedItems.filter(it => it.daysToExpiry >= -5);
        setInventoryItems(purged);
      }
    }, 60000); // Update every minute — paused when cards are open

    return () => clearInterval(clockInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoUpdateEnabled, expandedCards]);
  // Optimized filtering and searching with memoization
  useEffect(() => {
    let filtered = [...inventoryItems];
    // 'non-consumable': the server decides WHICH items are on the non-consumable shelf and
    // how much of each is still open (received since the last closing stock — older stock
    // reads 0); the rows themselves come from this screen's own data. Both lists are looked
    // in as a safety net — a row set aside above is the norm, but an older backend without
    // category_type can leave one in the main list. The open quantity is a DISPLAY value only
    // (see quantityLabel): the row's own quantity, batches and valuation are untouched.
    if (currentFilter === 'non-consumable') {
      const openById = new Map((nonConsumableOpen.items || []).map(o => [String(o.item_id), o]));
      const seen = new Set();
      filtered = [...nonConsumableRows, ...inventoryItems]
        .filter(it => openById.has(String(it.id)) && !seen.has(String(it.id)) && seen.add(String(it.id)))
        .map(it => {
          const o = openById.get(String(it.id));
          // Same stock unit as the row's raw quantity; carry over any container-unit
          // conversion the row's display applied.
          const raw = Number(it.rawQuantity);
          const scale = Number.isFinite(raw) && raw !== 0 ? Number(it.quantity) / raw : 1;
          return {
            ...it,
            nonConsumableLastBought: o.last_purchase_date || '',
            nonConsumableQuantity: (Number(o.open_quantity) || 0) * (Number.isFinite(scale) ? scale : 1),
            // Not in Recipe's expense head — sent only when the business has it switched on.
            nonConsumableExpenseHead: o.expense_head || null,
          };
        });
    }
    // Apply search filter - ONLY search by item name with priority ordering
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase().trim();
      // Priority 1: Items that start with the search term (exact prefix match)
      const startsWithMatches = filtered.filter(item => 
        item.name && item.name.toLowerCase().startsWith(searchLower)
      );
      // Priority 2: Items that contain the search term but don't start with it
      const containsMatches = filtered.filter(item => 
        item.name && 
        item.name.toLowerCase().includes(searchLower) && 
        !item.name.toLowerCase().startsWith(searchLower)
      );
      // Combine results with priority order
      filtered = [...startsWithMatches, ...containsMatches];
    }

    // Apply sorting based on current filter ONLY if no search term is active
    if (!debouncedSearchTerm) {
      if (currentFilter === 'expiry-nearest') {
        // Prioritize urgent items first, then sort by expiry
        filtered.sort((a, b) => {
          // Urgent items always come first
          if (a.urgencyLevel === 'urgent' && b.urgencyLevel !== 'urgent') return -1;
          if (a.urgencyLevel !== 'urgent' && b.urgencyLevel === 'urgent') return 1;
          if (a.urgencyLevel === 'critical' && b.urgencyLevel !== 'critical') return -1;
          if (a.urgencyLevel !== 'critical' && b.urgencyLevel === 'critical') return 1;
          
          // Then sort by days to expiry
          if (a.daysToExpiry === b.daysToExpiry) {
            return a.name.localeCompare(b.name);
          }
          return a.daysToExpiry - b.daysToExpiry;
        });
      } else if (currentFilter === 'quantity-high-low') {
        // Sort by quantity in descending order (highest first)
        // Extract numeric value from quantityDisplay to handle unit conversions properly
        filtered.sort((a, b) => {
          // Parse the display value to get the actual shown number (e.g., "2.0kg" -> 2.0, "208kg" -> 208)
          const parseDisplayQty = (item) => {
            const display = item.quantityDisplay || '';
            const match = display.match(/^([\d.]+)/);
            return match ? parseFloat(match[1]) : parseFloat(item.quantity) || 0;
          };
          
          const qtyA = parseDisplayQty(a);
          const qtyB = parseDisplayQty(b);
          
          // Sort descending (high to low)
          if (qtyB !== qtyA) {
            return qtyB - qtyA;
          }
          // If quantities are equal, sort alphabetically by name
          return a.name.localeCompare(b.name);
        });
      } else if (currentFilter === 'quantity-low-high') {
        // Sort by quantity in ascending order (lowest first)
        // Extract numeric value from quantityDisplay to handle unit conversions properly
        filtered.sort((a, b) => {
          // Parse the display value to get the actual shown number (e.g., "2.0kg" -> 2.0, "208kg" -> 208)
          const parseDisplayQty = (item) => {
            const display = item.quantityDisplay || '';
            const match = display.match(/^([\d.]+)/);
            return match ? parseFloat(match[1]) : parseFloat(item.quantity) || 0;
          };
          
          const qtyA = parseDisplayQty(a);
          const qtyB = parseDisplayQty(b);
          
          // Sort ascending (low to high)
          if (qtyA !== qtyB) {
            return qtyA - qtyB;
          }
          // If quantities are equal, sort alphabetically by name
          return a.name.localeCompare(b.name);
        });
      } else if (currentFilter === 'cost-high-low') {
        filtered.sort((a, b) => {
          const costA = Number(a.unitCost) || 0;
          const costB = Number(b.unitCost) || 0;
          if (costA === costB) return a.name.localeCompare(b.name);
          return costB - costA; // High to Low
        });
      } else if (currentFilter === 'cost-low-high') {
        filtered.sort((a, b) => {
          const costA = Number(a.unitCost) || 0;
          const costB = Number(b.unitCost) || 0;
          if (costA === costB) return a.name.localeCompare(b.name);
          return costA - costB; // Low to High
        });
      } else if (currentFilter === 'newly-added') {
        // Newest created first; items lacking createdAt go last
        filtered.sort((a, b) => {
          const atA = a.createdAt ? a.createdAt.getTime() : -Infinity;
          const atB = b.createdAt ? b.createdAt.getTime() : -Infinity;
          if (atA === atB) {
            // Prefer isNewlyAdded true items if timestamps tie or missing
            if (a.isNewlyAdded !== b.isNewlyAdded) return (a.isNewlyAdded ? -1 : 1);
            return a.name.localeCompare(b.name);
          }
          return atB - atA;
        });
      } else if (currentFilter === 'category') {
        filtered.sort((a, b) => {
          if (a.category === b.category) {
            return a.name.localeCompare(b.name);
          }
          return a.category.localeCompare(b.category);
        });
      } else if (currentFilter === 'non-consumable') {
        // Most recently bought first (ISO dates compare as strings).
        filtered.sort((a, b) => {
          const da = a.nonConsumableLastBought || '';
          const db = b.nonConsumableLastBought || '';
          if (da !== db) return db.localeCompare(da);
          return a.name.localeCompare(b.name);
        });
      }
    }

    // Apply 'saved-prep' filter: keep only items that are saved preparations
    if (currentFilter === 'saved-prep') {
      const prepNameSet = new Set(
        (Array.isArray(preparationOptions) ? preparationOptions : [])
          .map((p) => String(p?.composite_name || p?.name || p?.preparationName || '').trim().toLowerCase().replace(/\s+/g, ' '))
          .filter(Boolean)
      );
      filtered = filtered.filter((item) =>
        prepNameSet.has(String(item?.name || '').trim().toLowerCase().replace(/\s+/g, ' '))
      );
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    // Apply 'not-associated' filter: keep only ingredients no menu item, saved
    // preparation or complimentary recipe references (backend is_unassociated flag).
    if (currentFilter === 'not-associated') {
      filtered = filtered.filter((item) => item.isUnassociated === true);
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    setFilteredItems(filtered);
  }, [debouncedSearchTerm, currentFilter, inventoryItems, preparationOptions, nonConsumableRows, nonConsumableOpen]);

  // The 'non-consumable' filter's list of items bought in the open month. Fetched only
  // while that filter is on, and again whenever the overview reloads its rows.
  useEffect(() => {
    if (currentFilter !== 'non-consumable') return undefined;
    let alive = true;
    setNonConsumableOpen(prev => ({ ...prev, loading: true, error: null }));
    http.get('/purchase-history/non-consumables')
      .then((res) => {
        if (!res?.success) throw new Error(res?.error || 'Could not load the non-consumable items.');
        if (alive) setNonConsumableOpen({ loading: false, error: null, items: res.items || [] });
      })
      .catch((e) => {
        if (alive) setNonConsumableOpen({ loading: false, error: e?.data?.error || e?.message || 'Could not load the non-consumable items.', items: [] });
      });
    return () => { alive = false; };
  }, [currentFilter, nonConsumableRows]);

  // Manual refresh with loading state
  const handleRefresh = useCallback(() => {
    // Clear batch details cache on manual refresh
    setBatchDetails({});
    setLoadingBatches(new Set());
    
    // Clear expanded cards to force fresh batch data on re-expansion
    setExpandedCards(new Set());
    
    fetchInventoryData(true);
  }, [fetchInventoryData]);

  // Handle filter dropdown toggle
  const toggleFilterDropdown = useCallback(() => {
    setShowFilterDropdown(prev => !prev);
    setShowLanguageDropdown(false); // Close language dropdown when opening filter
  }, []);

  // Toggle language dropdown
  const toggleLanguageDropdown = useCallback(() => {
    setShowLanguageDropdown(prev => !prev);
    setShowFilterDropdown(false); // Close filter dropdown when opening language
  }, []);

  // Handle filter option selection
  const handleFilterSelect = useCallback((selectedFilter) => {
    setCurrentFilter(selectedFilter);
    setShowFilterDropdown(false);
  }, []);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
      if (editActionsRef.current && !editActionsRef.current.contains(event.target)) {
        setShowEditActionsMenu(false);
      }
      if (!event.target.closest('.add-stock-ingredient-price-type')) {
        setActivePriceTypeDropdownIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle language selection - ONLY for item translation, NOT for entire app
  const handleLanguageSelect = async (lang) => {
    setSelectedLanguage(lang);
    setShowLanguageDropdown(false);
    
    // DO NOT sync with global translation hooks
    // This is only for translating item names, not the entire app
    
    // Clear previous translations when changing language
    setTranslatedNames({});
    setItemExpandState({});
    itemTranslationService.clearCache();
    
    
    // Don't auto-translate - only translate when user expands an item
  };

  const normalizeInventoryName = useCallback((value) => {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }, []);

  const inventoryNameOptions = useMemo(() => {
    const seen = new Set();
    const names = [];
    inventoryItems.forEach((item) => {
      const name = String(item?.name || '').trim();
      const key = normalizeInventoryName(name);
      if (!key || seen.has(key)) return;
      seen.add(key);
      names.push(name);
    });
    return names.sort((a, b) => a.localeCompare(b));
  }, [inventoryItems, normalizeInventoryName]);

  const inventoryCurrentUnitOptions = useMemo(() => {
    const currentRaw = String(inventoryEditForm?.currentUnit || '').trim();
    const currentNorm = UnitMappingService.normalizeUnitSymbol(currentRaw) || currentRaw;
    const family = UnitMappingService.getUnitFamily(currentNorm);
    
    if (family === 'weight') {
      return [
        { value: 'kg', label: 'kg' },
        { value: 'g', label: 'g' }
      ];
    }
    if (family === 'volume') {
      return [
        { value: 'L', label: 'L' },
        { value: 'ml', label: 'ml' }
      ];
    }
    if (family === 'count') {
      return [
        { value: 'pcs', label: 'pcs' }
      ];
    }
    return currentRaw ? [{ value: currentRaw, label: currentRaw }] : [];
  }, [inventoryEditForm?.currentUnit]);

  const inventoryUnitOptions = useMemo(() => {
    const currentRaw = String(inventoryEditForm?.currentUnit || '').trim();
    const current = (UnitMappingService?.normalizeUnitSymbol?.(currentRaw) || currentRaw).toLowerCase();
    if (current === 'kg' || current === 'g') {
      return [
        { value: 'ml', label: 'ml' },
        { value: 'L', label: 'L' },
        { value: 'pcs', label: 'pcs' }
      ];
    }
    if (current === 'l' || current === 'ml') {
      return [
        { value: 'g', label: 'g' },
        { value: 'kg', label: 'kg' },
        { value: 'pcs', label: 'pcs' }
      ];
    }
    return [
      { value: 'g', label: 'g' },
      { value: 'kg', label: 'kg' },
      { value: 'ml', label: 'ml' },
      { value: 'L', label: 'L' },
      { value: 'pcs', label: 'pcs' }
    ].filter(option => option.value.toLowerCase() !== current);
  }, [inventoryEditForm?.currentUnit]);

  const addStockOptions = useMemo(() => {
    const recipes = (Array.isArray(recipeOptions) ? recipeOptions : []).map((recipe) => {
      const displayName = recipe?.display_name || recipe?.name || recipe?.item_name || '';
      return {
        __type: 'recipe',
        __displayName: String(displayName || '').trim(),
        __category: recipe?.category || '',
        __source: recipe
      };
    });

    const preparations = (Array.isArray(preparationOptions) ? preparationOptions : []).map((prep) => {
      const displayName = prep?.composite_name || prep?.name || prep?.preparationName || '';
      return {
        __type: 'preparation',
        __displayName: String(displayName || '').trim(),
        __category: 'Preparation',
        __source: prep
      };
    });

    return [...recipes, ...preparations].filter((entry) => entry.__displayName);
  }, [preparationOptions, recipeOptions]);

  const filteredAddStockOptions = useMemo(() => {
    if (!hasRecipeSearchInput) return [];
    const query = normalizeInventoryName(recipeSearchTerm);
    if (!query) return [];

    return addStockOptions
      .filter((entry) => normalizeInventoryName(entry.__displayName).includes(query))
      .slice(0, 8);
  }, [addStockOptions, hasRecipeSearchInput, normalizeInventoryName, recipeSearchTerm]);

  const inventoryNameSuggestions = useMemo(() => {
    const query = normalizeInventoryName(inventoryEditForm.currentName);
    if (!query) return [];
    return inventoryNameOptions
      .filter((name) => normalizeInventoryName(name).includes(query))
      .slice(0, 8);
  }, [inventoryEditForm.currentName, inventoryNameOptions, normalizeInventoryName]);

  /**
   * What "Merge into" may point at: inventory items AND saved preparations.
   *
   * Items only was the whole problem. A preparation is not an inventory item, so however
   * carefully its name was typed it could never appear here — and because the current item is
   * filtered out, typing a prefix the two share left the list completely empty and the field
   * looked broken.
   *
   * Preparations are tagged, and picking one runs Convert item → preparation instead of a merge:
   * nothing is deleted, no recipe is re-pointed, and one id binds the item to the preparation
   * that from then on says what it is made of.
   */
  const mergeTargetSuggestions = useMemo(() => {
    const query = normalizeInventoryName(inventoryEditForm.mergeTargetName);
    if (!query) return [];
    const currentKey = normalizeInventoryName(inventoryEditForm.currentName);

    const itemEntries = inventoryNameOptions
      .filter((name) => {
        const key = normalizeInventoryName(name);
        // Merging an item into itself is not a merge.
        if (currentKey && key === currentKey) return false;
        return key.includes(query);
      })
      .map((name) => ({ key: `item:${name}`, name, label: name, prep: null }));

    // A preparation sharing the item's name is KEPT: converting "Garam Masala" the item into
    // "Garam Masala" the preparation is a legitimate and very common pairing.
    const prepEntries = (Array.isArray(mergePrepOptions) ? mergePrepOptions : [])
      .filter((prep) => normalizeInventoryName(prep.name).includes(query))
      .map((prep) => ({
        key: `prep:${prep.id}`,
        name: prep.name,
        label: `${prep.name} — Preparation`,
        prep,
      }));

    return [...itemEntries, ...prepEntries].slice(0, 10);
  }, [
    inventoryEditForm.currentName,
    inventoryEditForm.mergeTargetName,
    inventoryNameOptions,
    mergePrepOptions,
    normalizeInventoryName,
  ]);

  /**
   * True while "Merge into" is naming a preparation — the screen then describes convert, not
   * merge.
   *
   * Item existence is checked against inventoryNameOptions rather than findInventoryItemByName,
   * which is declared further down this component: a `const` in a dependency array is READ
   * during render, so naming it here would be a temporal-dead-zone ReferenceError, not a
   * lint nit.
   */
  const mergeTargetIsPreparation = useMemo(() => {
    if (inventoryEditMode !== 'merge') return false;
    if (mergeTargetPrep) return true;
    const key = normalizeInventoryName(inventoryEditForm.mergeTargetName);
    if (!key) return false;
    // An item of the same name wins — merging two items is the safer reading.
    if (inventoryNameOptions.some((name) => normalizeInventoryName(name) === key)) return false;
    return (mergePrepOptions || []).some((p) => normalizeInventoryName(p.name) === key);
  }, [
    inventoryEditMode,
    mergeTargetPrep,
    inventoryEditForm.mergeTargetName,
    inventoryNameOptions,
    mergePrepOptions,
    normalizeInventoryName,
  ]);

  const selectedRecipeName = useMemo(() => {
    if (selectedRecipe) {
      return selectedRecipe?.display_name || selectedRecipe?.name || selectedRecipe?.item_name || '';
    }
    if (selectedPreparation) {
      return selectedPreparation?.composite_name || selectedPreparation?.name || selectedPreparation?.preparationName || '';
    }
    return '';
  }, [selectedPreparation, selectedRecipe]);

  const selectedAddStockCategory = useMemo(() => {
    if (selectedRecipe?.category) return selectedRecipe.category;
    if (selectedPreparation) return 'Preparation';
    return '';
  }, [selectedPreparation, selectedRecipe]);

  const selectedPreparationUnit = useMemo(() => {
    if (!selectedPreparation) return '';
    return canonicalGlobalUnit(
      selectedPreparation?.total_weight_unit_symbol || selectedPreparation?.total_weight_unit_name || selectedPreparation?.total_unit || ''
    );
  }, [selectedPreparation]);

  const recipeBatchServings = useMemo(() => {
    const overrideValue = parseFiniteNumber(recipeBatchServingsOverride);
    if (overrideValue && overrideValue > 0) {
      return overrideValue;
    }
    const value = parseFiniteNumber(selectedRecipe?.servings);
    return value && value > 0 ? value : 1;
  }, [parseFiniteNumber, recipeBatchServingsOverride, selectedRecipe]);

  const preparedCount = useMemo(() => {
    const value = parseFiniteNumber(recipeServingQty);
    return value && value > 0 ? value : 0;
  }, [parseFiniteNumber, recipeServingQty]);

  const effectivePreparedPlates = useMemo(() => (
    recipeQuantityMode === 'batches'
      ? preparedCount * recipeBatchServings
      : preparedCount
  ), [preparedCount, recipeBatchServings, recipeQuantityMode]);

  const getRecipeIngredientPricing = useCallback((ingredient) => {
    const quantity = parseFiniteNumber(ingredient?.scaledQuantity) || 0;
    const priceMode = ingredient?.priceMode === 'total' ? 'total' : 'unit';
    const unitPriceValue = parseFiniteNumber(ingredient?.unitPrice);
    const totalPriceValue = parseFiniteNumber(ingredient?.totalPrice);
    let resolvedUnitPrice = null;
    let resolvedTotalPrice = null;

    if (priceMode === 'total') {
      if (totalPriceValue !== null) {
        resolvedTotalPrice = totalPriceValue;
        if (quantity > 0) {
          resolvedUnitPrice = totalPriceValue / quantity;
        }
      } else if (unitPriceValue !== null) {
        resolvedUnitPrice = unitPriceValue;
        resolvedTotalPrice = unitPriceValue * quantity;
      }
    } else {
      if (unitPriceValue !== null) {
        resolvedUnitPrice = unitPriceValue;
        resolvedTotalPrice = unitPriceValue * quantity;
      } else if (totalPriceValue !== null) {
        resolvedTotalPrice = totalPriceValue;
        if (quantity > 0) {
          resolvedUnitPrice = totalPriceValue / quantity;
        }
      }
    }

    return {
      quantity,
      priceMode,
      unitPrice: resolvedUnitPrice,
      totalPrice: resolvedTotalPrice
    };
  }, [parseFiniteNumber]);

  const addStockTotal = useMemo(() => (
    recipeIngredients.reduce((sum, ingredient) => {
      const pricing = getRecipeIngredientPricing(ingredient);
      if (pricing.totalPrice === null) return sum;
      return sum + pricing.totalPrice;
    }, 0)
  ), [getRecipeIngredientPricing, recipeIngredients]);

  const findInventoryItemByName = useCallback((name) => {
    const key = normalizeInventoryName(name);
    if (!key) return null;
    return inventoryItems.find((item) => normalizeInventoryName(item?.name) === key) || null;
  }, [inventoryItems, normalizeInventoryName]);

  const toggleEditActionsMenu = useCallback(() => {
    setShowEditActionsMenu(prev => !prev);
    setShowFilterDropdown(false);
    setShowLanguageDropdown(false);
  }, []);

  /**
   * The saved preparations this business has, for the "Merge into" picker.
   *
   * An ingredient merged into a preparation is not a merge: it is Convert item → preparation.
   * The item stays as the face every recipe already points at, and the preparation becomes what
   * it is made from. Offering them here is the only way that operation is reachable from this
   * screen — and without it, typing the preparation's name matched nothing at all.
   */
  const loadMergePreparationOptions = useCallback(async () => {
    try {
      const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId');
      if (!businessId) { setMergePrepOptions([]); return; }
      const result = await http.get(`/medium-features/decomposition/preparations?businessId=${businessId}`);
      const list = (result?.success && Array.isArray(result.data) ? result.data : [])
        .map((p) => ({
          id: p.id,
          name: String(p.name || '').trim(),
          unit: String(p.unit_symbol || p.unit_name || '').trim(),
        }))
        .filter((p) => p.id && p.name);
      setMergePrepOptions(list);
    } catch (error) {
      // Non-fatal: the picker falls back to items only.
      console.warn('Unable to load preparations for merge target:', error?.message || error);
      setMergePrepOptions([]);
    }
  }, []);

  const openInventoryEditModal = useCallback((mode) => {
    setInventoryEditMode(mode);
    setInventoryEditError('');
    setInventoryEditLoading(false);
    setMergeTargetPrep(null);
    if (mode === 'merge') loadMergePreparationOptions();
    setInventoryEditForm({
      currentName: '',
      newName: '',
      mergeTargetName: '',
      currentUnit: '',
      newUnit: '',
      conversionRate: ''
    });
    setShowEditActionsMenu(false);
    anyModalOpenRef.current = true;
    setShowInventoryEditModal(true);
  }, [loadMergePreparationOptions]);

  const closeInventoryEditModal = useCallback(() => {
    anyModalOpenRef.current = false;
    setShowInventoryEditModal(false);
    setInventoryEditMode(null);
    setInventoryEditError('');
    setInventoryEditLoading(false);
    setInventoryEditUsageDetails(null);
    setInventoryEditUsageLoading(false);
    setInventoryEditUsageError('');
    setShowInventoryRenameConfirm(false);
    inventoryEditUsageRequestKeyRef.current = '';
    setShowCurrentUnitDropdown(false);
    setMergeTargetPrep(null);
    setInventoryEditForm({
      currentName: '',
      newName: '',
      mergeTargetName: '',
      currentUnit: '',
      newUnit: '',
      conversionRate: ''
    });
  }, []);

  const handleInventoryEditFieldChange = useCallback((field, value) => {
    setInventoryEditForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'currentName' && inventoryEditMode === 'unit') {
        const match = findInventoryItemByName(value);
        if (match) {
          const rawUnit = String(match.rawUnit || match.unit || '').trim();
          next.currentUnit = UnitMappingService.normalizeUnitSymbol(rawUnit) || rawUnit;
        } else {
          next.currentUnit = '';
        }
      }
      return next;
    });
    if (showInventoryRenameConfirm && ['currentName', 'newName', 'mergeTargetName', 'currentUnit', 'newUnit'].includes(field)) {
      setShowInventoryRenameConfirm(false);
    }
    if (inventoryEditError) {
      setInventoryEditError('');
    }
  }, [findInventoryItemByName, inventoryEditError, inventoryEditMode, showInventoryRenameConfirm]);

  const loadInventoryEditUsageDetails = useCallback(async (itemId) => {
    const result = await http.get(`/inventory/items/${itemId}/usage`);
    if (!result?.success) {
      throw new Error(result?.error || 'Failed to load related items.');
    }

    return {
      menuItems: Array.isArray(result.menuItems) ? result.menuItems : [],
      preparations: Array.isArray(result.preparations) ? result.preparations : [],
      preparationDetails: Array.isArray(result.preparationDetails) ? result.preparationDetails : [],
      complimentaryItems: Array.isArray(result.complimentaryItems) ? result.complimentaryItems : []
    };
  }, []);

  useEffect(() => {
    if (!showInventoryEditModal || !inventoryEditMode) {
      setInventoryEditUsageLoading(false);
      return;
    }

    const currentName = inventoryEditForm.currentName.trim();
    if (!currentName) {
      setInventoryEditUsageDetails(null);
      setInventoryEditUsageLoading(false);
      setInventoryEditUsageError('');
      return;
    }

    const match = findInventoryItemByName(currentName);
    if (!match) {
      setInventoryEditUsageDetails(null);
      setInventoryEditUsageLoading(false);
      setInventoryEditUsageError('');
      inventoryEditUsageRequestKeyRef.current = '';
      return;
    }

    const requestKey = `${inventoryEditMode}:${match.id}:${currentName.toLowerCase()}`;
    if (inventoryEditUsageRequestKeyRef.current === requestKey) {
      return;
    }

    inventoryEditUsageRequestKeyRef.current = requestKey;

    let cancelled = false;
    setInventoryEditUsageLoading(true);
    setInventoryEditUsageError('');

    loadInventoryEditUsageDetails(match.id)
      .then((details) => {
        if (cancelled) return;
        setInventoryEditUsageDetails(details);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error loading inventory edit usage details:', error);
        setInventoryEditUsageDetails({ menuItems: [], preparations: [] });
        setInventoryEditUsageError('Could not load related items right now.');
      })
      .finally(() => {
        if (cancelled) return;
        setInventoryEditUsageLoading(false);
      });

    return () => {
      cancelled = true;
    };

  }, [findInventoryItemByName, inventoryEditForm.currentName, inventoryEditMode, loadInventoryEditUsageDetails, showInventoryEditModal]);

  const inventoryEditUsagePanel = (
    <div style={{ minHeight: '132px', border: '1px solid #eef2f7', borderRadius: '14px', background: '#fff', padding: '12px 14px', marginBottom: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827', marginBottom: '10px', textAlign: 'left' }}>
        {isMobile ? 'Related items are affected' : 'Related items affected by this ingredient'}
      </div>
      {inventoryEditUsageLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#6b7280', fontSize: '14px', minHeight: '64px', justifyContent: 'center' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid #d1d5db', borderTopColor: '#111827', animation: 'spin 0.8s linear infinite' }} />
          {isMobile ? 'Loading related recipes...' : 'Loading related recipes and preparations...'}
        </div>
      ) : inventoryEditUsageError ? (
        <div style={{ fontSize: '14px', color: '#b45309', textAlign: 'left', minHeight: '64px' }}>{inventoryEditUsageError}</div>
      ) : inventoryEditUsageDetails && (inventoryEditUsageDetails.menuItems.length > 0 || inventoryEditUsageDetails.preparations.length > 0 || (inventoryEditUsageDetails.complimentaryItems || []).length > 0) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', textAlign: 'left' }}>
          {inventoryEditUsageDetails.menuItems.length > 0 && (
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>Menu items</div>
              <ul className="in-use-list" style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 160, overflowY: 'auto' }}>
                {inventoryEditUsageDetails.menuItems.map((item, index) => (
                  <li key={`confirm-menu-${index}`} style={{ padding: '8px 6px', marginBottom: 0, fontSize: '13px', color: '#333', display: 'flex', gap: '8px', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontWeight: 700, color: '#374151', width: 20, textAlign: 'right' }}>{index + 1}.</span>
                    <span style={{ flex: 1 }}>{typeof item === 'object' ? item.name : item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {inventoryEditUsageDetails.preparations.length > 0 && (
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>Preparations</div>
              <ul className="in-use-list" style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 160, overflowY: 'auto' }}>
                {(() => {
                  const prepQty = buildPrepQuantityLookup(inventoryEditUsageDetails.preparationDetails);
                  return inventoryEditUsageDetails.preparations.map((name, index) => (
                    <li key={`confirm-prep-${index}`} style={{ padding: '8px 6px', marginBottom: 0, fontSize: '13px', color: '#333', display: 'flex', gap: '8px', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ fontWeight: 700, color: '#374151', width: 20, textAlign: 'right' }}>{index + 1}.</span>
                      <span style={{ flex: 1 }}>{name}</span>
                      {prepQty.get(name) && (
                        <span style={{ color: '#6b7280', flexShrink: 0, whiteSpace: 'nowrap' }}>{prepQty.get(name)}</span>
                      )}
                    </li>
                  ));
                })()}
              </ul>
            </div>
          )}
          {(inventoryEditUsageDetails.complimentaryItems || []).length > 0 && (
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>Complimentary items</div>
              <ul className="in-use-list" style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 160, overflowY: 'auto' }}>
                {inventoryEditUsageDetails.complimentaryItems.map((item, index) => (
                  <li key={`confirm-comp-${item.id}`} style={{ padding: '8px 6px', marginBottom: 0, fontSize: '13px', color: '#333', display: 'flex', gap: '8px', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontWeight: 700, color: '#374151', width: 20, textAlign: 'right' }}>{index + 1}.</span>
                    <span style={{ flex: 1 }}>{item.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'left', minHeight: '64px', display: 'flex', alignItems: 'center' }}>
          No linked recipes, preparations, or complimentary items were found for this item.
        </div>
      )}
    </div>
  );

  const applyInventoryNameSuggestion = useCallback((name) => {
    handleInventoryEditFieldChange('currentName', name);
    setShowInventoryNameSuggestions(false);
  }, [handleInventoryEditFieldChange]);

  const applyMergeTargetSuggestion = useCallback((entry) => {
    // Remember WHICH row was clicked, not just the text. An item and a preparation can share a
    // name and they do very different things.
    setMergeTargetPrep(entry.prep || null);
    handleInventoryEditFieldChange('mergeTargetName', entry.name);
    setShowMergeTargetSuggestions(false);
  }, [handleInventoryEditFieldChange]);

  const handleInventoryEditSubmit = useCallback(async () => {
    if (!inventoryEditMode) return;

    const currentName = inventoryEditForm.currentName.trim();
    if (!currentName) {
      setInventoryEditError('Please enter the current item name.');
      return;
    }

    if (!showInventoryRenameConfirm && inventoryEditMode !== 'unit') {
      setInventoryEditError('');
      setShowInventoryRenameConfirm(true);
      return;
    }

    setInventoryEditLoading(true);
    setInventoryEditError('');

    try {
      if (inventoryEditMode === 'rename') {
        const newName = inventoryEditForm.newName.trim();
        if (!newName) {
          setInventoryEditError('Please enter the new item name.');
          return;
        }
        const result = await http.put('/inventory/items/rename', {
          currentName,
          newName
        });
        if (!result?.success) {
          throw new Error(result?.error || 'Failed to rename item.');
        }
      }

      if (inventoryEditMode === 'merge') {
        const mergeTargetName = inventoryEditForm.mergeTargetName.trim();
        if (!mergeTargetName) {
          setInventoryEditError('Please enter the item name to merge into.');
          return;
        }

        const sourceItem = findInventoryItemByName(currentName);
        if (!sourceItem) {
          setInventoryEditError('Current item not found in inventory.');
          return;
        }

        const targetItem = findInventoryItemByName(mergeTargetName);

        // ── Target is a saved preparation ───────────────────────────────────
        // Not a merge: no row is deleted and no recipe is re-pointed. The item stays as the face
        // every recipe already points at, and the preparation becomes what it is made from, so a
        // sale deducts the preparation's components scaled to the recipe's amount.
        //
        // An explicitly clicked preparation wins; a typed name resolves to an item first, because
        // merging two items is the safer reading of an ambiguous name.
        const targetPrep = mergeTargetPrep
          || (targetItem
            ? null
            : (mergePrepOptions || []).find(
              (p) => normalizeInventoryName(p.name) === normalizeInventoryName(mergeTargetName)
            ) || null);

        if (targetPrep) {
          const bId = UnitMappingService.getBusinessId();
          // Leftover stock is written off as part of the change rather than asked about: "use it
          // up first" is not something anyone can do with 2 g of a spice. The write-off is in the
          // operation's snapshot, so reverting it from the activity log restores the stock.
          const result = await http.post('/medium-features/decomposition/convert', {
            itemId: sourceItem.id, prepId: targetPrep.id, businessId: bId, settleStock: true,
          });
          if (!result?.success) {
            throw new Error(result?.error || 'Failed to use this preparation for the item.');
          }
          closeInventoryEditModal();
          fetchInventoryData(false);
          return;
        }

        if (!targetItem) {
          setInventoryEditError('That name matches no item or saved preparation.');
          return;
        }
        if (sourceItem.id === targetItem.id) {
          setInventoryEditError('Please choose a different item to merge into.');
          return;
        }

        const sourceUnit = canonicalGlobalUnit(sourceItem.rawUnit || sourceItem.unit);
        const targetUnit = canonicalGlobalUnit(targetItem.rawUnit || targetItem.unit);
        // Allow merging units from the same family (e.g. kg↔g, L↔ml) — the backend
        // converts the source's stock/batches into the target's unit. Only block
        // cross-family merges (e.g. weight vs volume).
        if (sourceUnit && targetUnit && isCrossFamilyChange(sourceUnit, targetUnit)) {
          setInventoryEditError(`Items must be in the same unit family to merge (${sourceUnit} vs ${targetUnit}).`);
          return;
        }

        const bId = UnitMappingService.getBusinessId();
        const result = await http.post('/recipes/merge-items', {
          duplicate_item_id: sourceItem.id,
          master_item_id: targetItem.id,
          businessId: bId
        });
        if (!result?.success) {
          throw new Error(result?.error || 'Failed to merge items.');
        }
      }

      if (inventoryEditMode === 'unit') {
        const currentUnit = inventoryEditForm.currentUnit.trim();
        const newUnit = inventoryEditForm.newUnit.trim();
        if (!currentUnit || !newUnit) {
          setInventoryEditError('Please enter both current unit and new unit.');
          return;
        }
        
        // Normalize units to strict unit symbols (g, kg, ml, L, pcs)
        const normalizedCurrentUnit = UnitMappingService.normalizeUnitSymbol(currentUnit) || currentUnit;
        const normalizedNewUnit = UnitMappingService.normalizeUnitSymbol(newUnit) || newUnit;
        
        // Check if this is a cross-family unit change (Strict 5 Unit Enforcement)
        const isCrossFamily = isCrossFamilyChange(normalizedCurrentUnit, normalizedNewUnit);
        
        if (isCrossFamily) {
          // Open CrossFamilyConversionModal for cross-family changes
          const match = findInventoryItemByName(currentName);
          if (!match) {
            setInventoryEditError('Item not found.');
            return;
          }
          
          // Use standard_unit_id if available, otherwise lookup from UNIT_ID_MAP
          let currentUnitId = match.standard_unit_id;
          if (!currentUnitId) {
            currentUnitId = Object.keys(UNIT_ID_MAP).find(id => UNIT_ID_MAP[id] === normalizedCurrentUnit);
            currentUnitId = currentUnitId ? parseInt(currentUnitId) : null;
          }
          
          const newUnitId = Object.keys(UNIT_ID_MAP).find(id => UNIT_ID_MAP[id] === normalizedNewUnit);
          
          setCrossFamilyModalData({
            itemId: match.id,
            itemName: currentName,
            currentStock: match.rawQuantity, // Use rawQuantity which contains current_stock
            currentUnit: normalizedCurrentUnit, // Use normalized units
            currentUnitId: currentUnitId,
            newUnit: normalizedNewUnit, // Use normalized units
            newUnitId: newUnitId ? parseInt(newUnitId) : null
          });
          
          // Close the inventory edit modal first to prevent stacking
          closeInventoryEditModal();
          
          // Then open the cross-family conversion modal
          setShowCrossFamilyModal(true);
          
          // Return early - cross-family modal will handle the conversion
          setInventoryEditLoading(false);
          return;
        }
        
        // Apply same-family unit change directly
        try {
          const result = await http.put('/inventory/items/change-unit', {
            itemName: currentName,
            currentUnit: normalizedCurrentUnit,
            newUnit: normalizedNewUnit
          });
          if (!result?.success) {
            throw new Error(result?.error || 'Failed to change unit.');
          }
          closeInventoryEditModal();
          fetchInventoryData(true);
          return;
        } catch (previewErr) {
          console.warn('Unit change failed:', previewErr.message);
          throw previewErr;
        }
      }

      closeInventoryEditModal();
      fetchInventoryData(true);
    } catch (error) {
      setInventoryEditError(error?.message || 'Failed to update item.');
    } finally {
      setInventoryEditLoading(false);
    }
  }, [closeInventoryEditModal, fetchInventoryData, findInventoryItemByName, inventoryEditForm, inventoryEditMode, showInventoryRenameConfirm, mergeTargetPrep, mergePrepOptions, normalizeInventoryName]);

  // Handler for confirming unit change from UnitChangeConfirmationModal
  const handleOverviewUnitChangeConfirm = useCallback(async (conversionFactor) => {
    if (!pendingOverviewUnitChange) return;
    setIsApplyingOverviewUnitChange(true);
    try {
      const result = await UnitMappingService.confirmUnitChange(
        pendingOverviewUnitChange.itemName,
        pendingOverviewUnitChange.currentUnit,
        pendingOverviewUnitChange.newUnit,
        conversionFactor,
        'Changed via Inventory Overview'
      );
      if (result.success) {
        setShowUnitConfirmModal(false);
        setPendingOverviewUnitChange(null);
        setUnitChangePreviewData(null);
        closeInventoryEditModal();
        fetchInventoryData(true);
      } else {
        setInventoryEditError(result.error || 'Failed to apply unit change');
      }
    } catch (err) {
      console.error('Unit change error:', err);
      setInventoryEditError(err.message || 'Failed to apply unit change');
    } finally {
      setIsApplyingOverviewUnitChange(false);
    }
  }, [pendingOverviewUnitChange, closeInventoryEditModal, fetchInventoryData]);

  // Handler for confirming cross-family conversion from CrossFamilyConversionModal
  const handleCrossFamilyConversionConfirm = useCallback(() => {
    // Close both modals and refresh inventory
    setShowCrossFamilyModal(false);
    setCrossFamilyModalData(null);
    closeInventoryEditModal();
    fetchInventoryData(true);
  }, [closeInventoryEditModal, fetchInventoryData]);

  // Handler for canceling cross-family conversion
  const handleCrossFamilyConversionCancel = useCallback(() => {
    setShowCrossFamilyModal(false);
    setCrossFamilyModalData(null);
  }, []);

  const loadDeleteUsageDetails = useCallback(async (itemId) => {
    try {
      const result = await http.get(`/inventory/items/${itemId}/usage`);
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to load related items.');
      }

      setDeleteUsageDetails({
        menuItems: Array.isArray(result.menuItems) ? result.menuItems : [],
        preparations: Array.isArray(result.preparations) ? result.preparations : [],
        preparationDetails: Array.isArray(result.preparationDetails) ? result.preparationDetails : [],
        complimentaryItems: Array.isArray(result.complimentaryItems) ? result.complimentaryItems : []
      });
    } catch (error) {
      console.error('Error loading delete usage details:', error);
      setDeleteUsageDetails({ menuItems: [], preparations: [], complimentaryItems: [] });
      setError(error?.message || 'Failed to load related items.');
    } finally {
      setDeleteCheckLoading(false);
    }
  }, []);

  // Remove ingredient from selected menu items (called from delete modal)
  const handleRemoveFromDeleteModal = useCallback(async () => {
    if (!deleteConfirmItem || deleteCheckedIds.size === 0) return;
    setDeleteRemoving(true);
    try {
      const menuItemIds = Array.from(deleteCheckedIds);
      const result = await http.delete(
        `/inventory/items/${deleteConfirmItem.id}/from-recipes`,
        { menuItemIds }
      );
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to remove ingredient.');
      }
      // Accumulate the removed recipe link rows so the final delete can include them in the snapshot
      if (Array.isArray(result.removedLinks) && result.removedLinks.length > 0) {
        deletePendingRemovedLinksRef.current = [
          ...deletePendingRemovedLinksRef.current,
          ...result.removedLinks
        ];
      }
      // Remove checked items from the list
      setDeleteUsageDetails((prev) => ({
        ...prev,
        menuItems: (prev?.menuItems || []).filter((item) => !deleteCheckedIds.has(item.id))
      }));
      setDeleteCheckedIds(new Set());
    } catch (error) {
      console.error('Error removing ingredient from recipes (delete modal):', error);
      alert('Failed to remove: ' + (error?.message || 'Unknown error'));
    } finally {
      setDeleteRemoving(false);
    }
  }, [deleteConfirmItem, deleteCheckedIds]);

  // Remove ingredient from selected preparations (called from delete modal)
  const handleRemovePrepsFromDeleteModal = useCallback(async () => {
    if (!deleteConfirmItem || deleteCheckedPrepNames.size === 0) return;
    setDeleteRemovingPreps(true);
    try {
      const preparationNames = Array.from(deleteCheckedPrepNames);
      const result = await http.delete(
        `/inventory/items/${deleteConfirmItem.id}/from-preparations`,
        { preparationNames }
      );
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to remove ingredient from preparations.');
      }
      // Accumulate the removed prep component rows so the final delete can include them in the snapshot
      if (Array.isArray(result.removedPreps) && result.removedPreps.length > 0) {
        deletePendingRemovedPrepsRef.current = [
          ...deletePendingRemovedPrepsRef.current,
          ...result.removedPreps
        ];
      }
      // Remove the de-linked preparations from the modal list
      setDeleteUsageDetails((prev) => ({
        ...prev,
        preparations: (prev?.preparations || []).filter((name) => !deleteCheckedPrepNames.has(name))
      }));
      setDeleteCheckedPrepNames(new Set());
    } catch (error) {
      console.error('Error removing ingredient from preparations (delete modal):', error);
      alert('Failed to remove: ' + (error?.message || 'Unknown error'));
    } finally {
      setDeleteRemovingPreps(false);
    }
  }, [deleteConfirmItem, deleteCheckedPrepNames]);

  // Remove ingredient from selected complimentary items (called from delete modal)
  const handleRemoveCompFromDeleteModal = useCallback(async () => {
    if (!deleteConfirmItem || deleteCheckedCompIds.size === 0) return;
    setDeleteRemovingComp(true);
    try {
      const compRecipeIds = Array.from(deleteCheckedCompIds);
      const result = await http.delete(
        `/inventory/items/${deleteConfirmItem.id}/from-complimentary`,
        { compRecipeIds }
      );
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to remove ingredient from complimentary items.');
      }
      // Accumulate the removed complimentary recipe ingredient rows so the final delete can include them in the snapshot
      if (Array.isArray(result.removedComplimentary) && result.removedComplimentary.length > 0) {
        deletePendingRemovedCompRef.current = [
          ...deletePendingRemovedCompRef.current,
          ...result.removedComplimentary
        ];
      }
      // Remove checked complimentary items from the list
      setDeleteUsageDetails((prev) => ({
        ...prev,
        complimentaryItems: (prev?.complimentaryItems || []).filter((item) => !deleteCheckedCompIds.has(item.id))
      }));
      setDeleteCheckedCompIds(new Set());
    } catch (error) {
      console.error('Error removing ingredient from complimentary items (delete modal):', error);
      alert('Failed to remove: ' + (error?.message || 'Unknown error'));
    } finally {
      setDeleteRemovingComp(false);
    }
  }, [deleteConfirmItem, deleteCheckedCompIds]);

  const closeViewRelatedModal = useCallback(() => {
    anyModalOpenRef.current = false;
    viewRelatedRequestSeqRef.current += 1;
    setViewRelatedConfirmItem(null);
    setViewRelatedUsageDetails(null);
    setViewRelatedCheckLoading(false);
    setViewRelatedCheckedIds(new Set());
    setViewRelatedRemoving(false);
  }, []);

  const loadRelatedUsageDetails = useCallback(async (itemId, requestSeq) => {
    try {
      setViewRelatedCheckLoading(true);
      if (!itemId) {
        setViewRelatedUsageDetails({ menuItems: [], preparations: [], complimentaryItems: [] });
        return;
      }

      const result = await http.get(`/inventory/items/${itemId}/usage`);
      if (requestSeq !== viewRelatedRequestSeqRef.current) {
        return;
      }

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to load related items.');
      }

      setViewRelatedUsageDetails({
        menuItems: Array.isArray(result.menuItems) ? result.menuItems : [],
        preparations: Array.isArray(result.preparations) ? result.preparations : [],
        preparationDetails: Array.isArray(result.preparationDetails) ? result.preparationDetails : [],
        complimentaryItems: Array.isArray(result.complimentaryItems) ? result.complimentaryItems : []
      });
    } catch (error) {
      if (requestSeq !== viewRelatedRequestSeqRef.current) {
        return;
      }

      console.error('Error loading related usage details:', error);
      setViewRelatedUsageDetails({ menuItems: [], preparations: [], complimentaryItems: [] });
    } finally {
      if (requestSeq === viewRelatedRequestSeqRef.current) {
        setViewRelatedCheckLoading(false);
      }
    }
  }, []);

  // Remove this ingredient from selected menu items
  const handleRemoveIngredientFromSelected = useCallback(async () => {
    if (!viewRelatedConfirmItem || viewRelatedCheckedIds.size === 0) return;
    setViewRelatedRemoving(true);
    try {
      const menuItemIds = Array.from(viewRelatedCheckedIds);
      const result = await http.delete(
        `/inventory/items/${viewRelatedConfirmItem.id}/from-recipes`,
        { menuItemIds }
      );
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to remove ingredient.');
      }
      // Update the modal list — remove checked items
      setViewRelatedUsageDetails((prev) => ({
        ...prev,
        menuItems: (prev?.menuItems || []).filter((item) => !viewRelatedCheckedIds.has(item.id))
      }));
      setViewRelatedCheckedIds(new Set());
    } catch (error) {
      console.error('Error removing ingredient from recipes:', error);
      alert('Failed to remove ingredient: ' + (error?.message || 'Unknown error'));
    } finally {
      setViewRelatedRemoving(false);
    }
  }, [viewRelatedConfirmItem, viewRelatedCheckedIds]);

  const translateItemName = useCallback(async (itemId, itemName) => {
    
    // If already translating, skip
    if (translatingItems.has(itemId)) {
      return;
    }

    // If English is selected, no need to translate
    if (selectedLanguage === 'en') {
      return;
    }

    // If already translated, skip
    if (translatedNames[itemId]) {
      return;
    }

    setTranslatingItems(prev => new Set([...prev, itemId]));

    try {
      let translatedText = null;
      
      // Step 1: Try i18next first
      const i18nKey = `items.${itemName.toLowerCase().replace(/\s+/g, '_').replace(/[()]/g, '')}`;
      const i18nTranslation = i18n.t(i18nKey, { lng: selectedLanguage });
      
      // Check if i18next actually translated (not returning the key itself)
      if (i18nTranslation && i18nTranslation !== i18nKey && i18nTranslation !== itemName) {
        translatedText = i18nTranslation;
      } else {
        
        // Step 2: Fallback to Google Translate via itemTranslationService
        translatedText = await itemTranslationService.translate(itemName, selectedLanguage);
      }
      
      
      // Always store the result (even if it's the original text)
      setTranslatedNames(prev => ({
        ...prev,
        [itemId]: translatedText
      }));
      
    } catch (error) {
      console.error('❌ Unexpected translation error:', error);
      // Even on error, store the original name so we don't keep retrying
      setTranslatedNames(prev => ({
        ...prev,
        [itemId]: itemName
      }));
    } finally {
      setTranslatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  }, [selectedLanguage, translatedNames, translatingItems, i18n]);

  // Get display name for item (translated or original)
  const getDisplayName = (item) => {
    const isExpanded = itemExpandState[item.id];
    
    // Show translation only when expanded and translation exists
    if (isExpanded && selectedLanguage !== 'en' && translatedNames[item.id]) {
      return translatedNames[item.id];
    }
    
    return item.name;
  };

  // Fetch batch details for an item
  // IMPORTANT: This function also checks for onboarding drafts and displays them as closing stock
  // if no finalized BATCH-01 batch exists. This ensures closing stock entered during onboarding
  // is always visible in Overview, even if finalization hasn't occurred yet.
  const fetchBatchDetails = useCallback(async (itemId, forceRefresh = false) => {
    if (!forceRefresh && (batchDetails[itemId] || loadingBatches.has(itemId))) {
      return; // Already loaded or loading
    }

    setLoadingBatches(prev => new Set(prev).add(itemId));

  try {
  const result = await http.get(`/inventory/items/${itemId}/batches`);

      if (result.success) {
        // Backend already returns batches sorted by FIFO (received_date ASC)
        // No need to re-sort, just use the data as-is
        const sortedBatches = result.data;

        const isMalformedQuantityDisplay = (value) => {
          const text = String(value || '').trim();
          if (!text) return true;
          if (/nan|undefined|null/i.test(text)) return true;
          // Reject values like "-gm" that have a sign/unit but no numeric digits.
          if (/^[+-]?\s*[a-zA-Z]+$/.test(text)) return true;
          // Reject signed zero and sub-unit values so the UI falls back to the normalized display.
          if (/^[+-]?\s*0+(?:\.0+)?(?:\s*[a-zA-Z].*)?$/.test(text)) return true;
          const leadingNumber = text.match(/^[+-]?\d+(?:\.\d+)?/);
          if (leadingNumber && Math.abs(Number(leadingNumber[0])) < 1) return true;
          return !/\d/.test(text);
        };

        const getSafeBatchQuantity = (batch) => {
          const remainingQuantity = parseFiniteNumber(batch?.remaining_quantity);
          const fallbackQuantity = parseFiniteNumber(batch?.quantity);
          const rawQuantity = remainingQuantity !== null
            ? remainingQuantity
            : (fallbackQuantity !== null ? fallbackQuantity : 0);
          const normalizedQuantity = Math.abs(rawQuantity);
          const normalizedUnit = String(batch?.unit_symbol || batch?.unit || 'units').trim() || 'units';
          const computedDisplay = formatQuantityDisplay(normalizedQuantity, normalizedUnit);
          const backendDisplay = String(batch?.quantity_display || '').trim();

          return {
            quantity: normalizedQuantity,
            unit: normalizedUnit,
            quantityDisplay: isMalformedQuantityDisplay(backendDisplay) ? computedDisplay : backendDisplay
          };
        };

        const processedBatches = sortedBatches.map((batch, index) => {
          const displayBatchNo = (batch.invoice_reference && String(batch.invoice_reference).trim().length > 0)
            ? String(batch.invoice_reference).trim()
            : `#${batch.batch_id}`;
          const supplierName = batch.supplier || batch.supplier_name || batch.vendor || batch.vendor_name || 'N/A';
          const daysToExpiry = batch.days_to_expiry || 999;
          const safeBatchQuantity = getSafeBatchQuantity(batch);

          // Check if this is a closing stock batch (created from onboarding)
          const isOpeningStock = batch.invoice_reference === 'BATCH-01';

          // Mark ONLY the first batch (oldest by received_date) as "USE FIRST" 
          const isFirstBatch = index === 0;

          return {
            id: batch.batch_id,
            batchNumber: displayBatchNo,
            quantityDisplay: safeBatchQuantity.quantityDisplay,
            quantity: safeBatchQuantity.quantity,
            unit: safeBatchQuantity.unit,
            procuredDate: batch.procured_date_formatted || (batch.procured_date ? new Date(batch.procured_date).toLocaleDateString('en-US') : 'N/A'),
            expiryDate: batch.expiry_date_formatted || (batch.expiry_date ? new Date(batch.expiry_date).toLocaleDateString('en-US') : null),
            supplier: supplierName,
            status: batch.status,
            useFirst: isFirstBatch, // FIFO: First batch (oldest received_date) gets "USE FIRST"
            daysToExpiry: daysToExpiry,
            isOpeningStock: isOpeningStock, // Flag to identify closing stock batches
            unitCost: batch.unit_cost,
            totalCost: batch.total_cost
          };
        });

        // Check if there's an onboarding draft for this item that wasn't finalized
        const draft = onboardingDrafts[itemId];
        const hasOpeningStockBatch = processedBatches.some(b => b.isOpeningStock);
        
        // If draft exists and no BATCH-01 batch exists, add draft as closing stock
        if (draft && !hasOpeningStockBatch) {
          console.log(`📝 Adding closing stock draft for item ${itemId}:`, draft.quantity, draft.unit);

          const draftQuantity = parseFiniteNumber(draft?.quantity);
          const safeDraftQuantity = draftQuantity !== null ? Math.abs(draftQuantity) : 0;
          const safeDraftUnit = String(draft?.unit || 'g').trim() || 'g';
          const rawDraftQuantity = parseFiniteNumber(draft?.raw_quantity);
          const safeRawDraftQuantity = rawDraftQuantity !== null ? Math.abs(rawDraftQuantity) : null;
          
          // Build quantity display with conversion info if available
          let quantityDisplay = formatQuantityDisplay(safeDraftQuantity, safeDraftUnit);
          if (safeRawDraftQuantity !== null && draft.original_unit && draft.unit_conversion && draft.conversion_unit) {
            // Show conversion: "10 kg (from 1 bags)"
            quantityDisplay = `${formatQuantityDisplay(safeDraftQuantity, safeDraftUnit)} (from ${safeRawDraftQuantity} ${normalizeUnitDisplay(draft.original_unit)})`;
          }
          
          const draftBatch = {
            id: `draft-${itemId}`,
            batchNumber: 'BATCH-01',
            quantityDisplay: quantityDisplay,
            quantity: safeDraftQuantity,
            unit: safeDraftUnit,
            procuredDate: new Date(draft.created_at || Date.now()).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            expiryDate: null,
            supplier: 'Closing Stock',
            status: 'Good',
            useFirst: false,
            daysToExpiry: 999,
            isOpeningStock: true,
            isDraft: true, // Flag to indicate this is not yet finalized
            unitCost: parseFloat(draft.unit_price || 0),
            totalCost: parseFloat(draft.total_price || 0),
            // Include conversion details for display
            conversionInfo: draft.unit_conversion && draft.original_unit ? 
              `1 ${normalizeUnitDisplay(draft.original_unit)} = ${draft.unit_conversion} ${normalizeUnitDisplay(draft.conversion_unit)}` : null
          };
          
          // Add draft at the beginning (oldest stock)
          processedBatches.unshift(draftBatch);
        }

        setBatchDetails(prev => ({
          ...prev,
          [itemId]: processedBatches
        }));

        const batchTotal = processedBatches.reduce((sum, batch) => {
          if (batch.isDraft) return sum;
          return sum + (Number(batch.quantity) || 0);
        }, 0);

        setInventoryItems(prev => prev.map(item => {
          if (item.id !== itemId) return item;

          const existingQuantity = Number(item.rawQuantity ?? item.quantity ?? 0);
          if (Number.isFinite(existingQuantity) && existingQuantity < 0) {
            return item;
          }

          const rawQuantity = batchTotal;
          let displayQuantity = rawQuantity;
          let displayUnit = UnitMappingService.normalizeUnitSymbol(item.unit || item.rawUnit || 'units');
          const displayUnitLower = String(displayUnit || '').toLowerCase().trim();

          if (isContainerUnit(displayUnitLower)) {
            const convKey = `${normalizeItemKey(item.name)}_${displayUnitLower}`;
            const conv = supplierConversionMap[convKey];
            if (conv && conv.factor > 0) {
              displayQuantity = rawQuantity * conv.factor;
              displayUnit = conv.toUnitDisplay || displayUnit;
            }
          }

          const minStock = item.minStock || 0;
          const maxStock = item.maxStock || 0;
          let stockStatus = 'adequate';
          if (rawQuantity <= 0) {
            stockStatus = 'out-of-stock';
          } else if (rawQuantity <= minStock) {
            stockStatus = 'low';
          } else if (maxStock > 0 && rawQuantity >= maxStock) {
            stockStatus = 'overstocked';
          }

          return {
            ...item,
            rawQuantity,
            quantity: displayQuantity,
            quantityDisplay: formatQuantityDisplay(displayQuantity, displayUnit),
            unit: displayUnit,
            stockLevel: stockStatus
          };
        }));
        setBatchErrors(prev => ({ ...prev, [itemId]: undefined }));
      }
    } catch (error) {
      console.error('Error fetching batch details:', error);
      
      // Even if batch fetch fails, check if there's a draft to show
      const draft = onboardingDrafts[itemId];
      const fallbackBatches = [];
      
      if (draft) {
        console.log(`📝 Using draft as fallback for item ${itemId} (batch fetch failed)`);

        const draftQuantity = parseFiniteNumber(draft?.quantity);
        const safeDraftQuantity = draftQuantity !== null ? Math.abs(draftQuantity) : 0;
        const safeDraftUnit = String(draft?.unit || 'g').trim() || 'g';
        const rawDraftQuantity = parseFiniteNumber(draft?.raw_quantity);
        const safeRawDraftQuantity = rawDraftQuantity !== null ? Math.abs(rawDraftQuantity) : null;
        
        // Build quantity display with conversion info if available
        let quantityDisplay = formatQuantityDisplay(safeDraftQuantity, safeDraftUnit);
        if (safeRawDraftQuantity !== null && draft.original_unit && draft.unit_conversion && draft.conversion_unit) {
          quantityDisplay = `${formatQuantityDisplay(safeDraftQuantity, safeDraftUnit)} (from ${safeRawDraftQuantity} ${normalizeUnitDisplay(draft.original_unit)})`;
        }
        
        const draftBatch = {
          id: `draft-${itemId}`,
          batchNumber: 'BATCH-01',
          quantityDisplay: quantityDisplay,
          quantity: safeDraftQuantity,
          unit: safeDraftUnit,
          procuredDate: new Date(draft.created_at || Date.now()).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          expiryDate: null,
          supplier: 'Closing Stock',
          status: 'Good',
          useFirst: false,
          daysToExpiry: 999,
          isOpeningStock: true,
          isDraft: true,
          unitCost: parseFloat(draft.unit_price || 0),
          totalCost: parseFloat(draft.total_price || 0),
          conversionInfo: draft.unit_conversion && draft.original_unit ? 
            `1 ${normalizeUnitDisplay(draft.original_unit)} = ${draft.unit_conversion} ${normalizeUnitDisplay(draft.conversion_unit)}` : null
        };
        fallbackBatches.push(draftBatch);
      }
      
      // Store draft or empty array
      setBatchDetails(prev => ({
        ...prev,
        [itemId]: fallbackBatches
      }));
      setBatchErrors(prev => ({ ...prev, [itemId]: error.message || 'Failed to load batches' }));
    } finally {
      setLoadingBatches(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  }, [batchDetails, loadingBatches, onboardingDrafts, parseFiniteNumber, supplierConversionMap]);

  // Keep expanded batch details in sync with card quantity changes.
  useEffect(() => {
    if (!inventoryItems.length) {
      previousQuantityByItemRef.current = {};
      return;
    }

    const previousMap = previousQuantityByItemRef.current || {};
    const nextMap = {};
    const idsToRefresh = [];

    inventoryItems.forEach((item) => {
      const itemId = Number(item?.id);
      if (!Number.isFinite(itemId) || itemId <= 0) {
        return;
      }

      const raw = Number(item?.rawQuantity ?? item?.quantity ?? 0);
      const currentQuantity = Number.isFinite(raw) ? raw : 0;
      nextMap[itemId] = currentQuantity;

      if (!expandedCards.has(itemId)) {
        return;
      }

      const previousQuantity = previousMap[itemId];
      if (!Number.isFinite(previousQuantity)) {
        return;
      }

      if (Math.abs(previousQuantity - currentQuantity) > 0.0001) {
        idsToRefresh.push(itemId);
      }
    });

    previousQuantityByItemRef.current = nextMap;

    if (idsToRefresh.length === 0) {
      return;
    }

    setBatchDetails((prev) => {
      let changed = false;
      const next = { ...prev };
      idsToRefresh.forEach((itemId) => {
        if (Object.prototype.hasOwnProperty.call(next, itemId)) {
          delete next[itemId];
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    idsToRefresh.forEach((itemId) => {
      fetchBatchDetails(itemId, true);
    });
  }, [inventoryItems, expandedCards, fetchBatchDetails]);

  // Toggle card expansion
  const toggleCardExpansion = useCallback((itemId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
        // Fetch batch details when expanding
        fetchBatchDetails(itemId);
      }
      return newSet;
    });
  }, [fetchBatchDetails]);

  // Canonical unit price multiplier for converting previous batch unit price
  // to the currently selected invoice unit. Multiplier is applied to a
  // unit-price to convert it into the target unit's per-unit price.
  // Example: price_per_ml -> price_per_l = price_per_ml * 1000
  const getCostUnitMultiplier = (fromUnit, toUnit) => {
    const f = UnitMappingService.normalizeUnitSymbol(fromUnit);
    const t = UnitMappingService.normalizeUnitSymbol(toUnit);
    if (!f || !t) return null;
    if (f === t) return 1;
    if (f === 'g' && t === 'kg') return 1000;
    if (f === 'kg' && t === 'g') return 0.001;
    if (f === 'ml' && t === 'L') return 1000;
    if (f === 'L' && t === 'ml') return 0.001;
    return null;
  };

  const getRecipeId = useCallback((recipe) => {
    const rawId = recipe?.id ?? recipe?.recipe_id ?? recipe?.recipeId;
    const parsed = Number(rawId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, []);

  const resetAddStockSelection = useCallback(() => {
    setRecipeSearchTerm('');
    setHasRecipeSearchInput(false);
    setSelectedRecipe(null);
    setSelectedPreparation(null);
    setRecipeServingQty('');
    setRecipeQuantityMode('plates');
    setRecipeBatchServingsOverride('');
    setRecipeIngredients([]);
  }, []);

  const resetAddStockWizard = useCallback(() => {
    setAddStockStep(1);
    resetAddStockSelection();
    setAddStockError('');
    setActivePriceTypeDropdownIndex(null);
  }, [resetAddStockSelection]);

  const loadRecipeOptions = useCallback(async () => {
    setIsLoadingRecipes(true);
    setAddStockError('');

    try {
      const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId');
      if (!businessId) {
        setRecipeOptions([]);
        setAddStockError('Business not available.');
        return;
      }

      const result = await http.get(`/recipes?businessId=${businessId}&includeInactive=true&includeComplimentary=true`);
      const recipes = Array.isArray(result?.data) ? result.data : [];
      setRecipeOptions(recipes);
    } catch (error) {
      console.warn('Unable to load menu items:', error);
      setRecipeOptions([]);
      setAddStockError('Failed to load menu items.');
    } finally {
      setIsLoadingRecipes(false);
    }
  }, []);

  const loadPreparationOptions = useCallback(async () => {
    setIsLoadingPreparations(true);
    try {
      const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId');
      if (!businessId) {
        setPreparationOptions([]);
        return;
      }

      const result = await http.get(`/medium-features/decomposition/templates?businessId=${businessId}`);
      const payload = (result?.data && (result?.data?.personalized || result?.data?.default)) ? result.data : result;
      const personalized = Array.isArray(payload?.personalized) ? payload.personalized : [];
      const defaults = Array.isArray(payload?.default) ? payload.default : [];
      const templates = Array.isArray(payload?.templates) ? payload.templates : [];

      const merged = [...personalized, ...defaults, ...templates];
      const seen = new Set();
      const deduped = merged.filter((prep) => {
        const key = normalizeInventoryName(prep?.composite_name || prep?.name || prep?.preparationName);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setPreparationOptions(deduped);
    } catch (error) {
      console.warn('Unable to load saved preparations:', error?.message || error);
      setPreparationOptions([]);
    } finally {
      setIsLoadingPreparations(false);
    }
  }, [normalizeInventoryName]);

  // Ensure saved preparations list is available for rendering saved-prep indicators
  useEffect(() => {
    loadPreparationOptions();
  }, [loadPreparationOptions]);

  // Set of normalized saved preparation names for quick lookup
  const savedPreparationNameSet = useMemo(() => {
    try {
      return new Set((Array.isArray(preparationOptions) ? preparationOptions : []).map((p) => {
        return normalizeInventoryName(p?.composite_name || p?.name || p?.preparationName || '');
      }).filter(Boolean));
    } catch (e) {
      return new Set();
    }
  }, [preparationOptions, normalizeInventoryName]);

  const openAddStockWizard = useCallback(async () => {
    resetAddStockWizard();
    setShowAddStockWizard(true);
    await Promise.all([loadRecipeOptions(), loadPreparationOptions()]);
  }, [loadPreparationOptions, loadRecipeOptions, resetAddStockWizard]);

  const closeAddStockWizard = useCallback(() => {
    setShowAddStockWizard(false);
    setAddStockError('');
    resetAddStockWizard();
  }, [resetAddStockWizard]);

  const handleRecipeSelect = useCallback((option) => {
    const selectionType = option?.__type === 'preparation' ? 'preparation' : 'recipe';
    const source = option?.__source || option;

    if (selectionType === 'preparation') {
      const displayName = source?.composite_name || source?.name || source?.preparationName || '';
      setSelectedPreparation(source);
      setSelectedRecipe(null);
      setRecipeSearchTerm(String(displayName || '').trim());
      setHasRecipeSearchInput(false);
      setRecipeServingQty('');
      setRecipeBatchServingsOverride('');
      setRecipeQuantityMode('plates');
    } else {
      const servingsPerBatch = parseFiniteNumber(source?.servings);
      setSelectedRecipe(source);
      setSelectedPreparation(null);
      setRecipeSearchTerm(source?.display_name || source?.name || source?.item_name || '');
      setHasRecipeSearchInput(false);
      setRecipeServingQty('');
      setRecipeBatchServingsOverride(servingsPerBatch && servingsPerBatch > 0 ? String(servingsPerBatch) : '');
      setRecipeQuantityMode(servingsPerBatch && servingsPerBatch > 1 ? 'batches' : 'plates');
    }

    setAddStockStep(2);
    setAddStockError('');
  }, [parseFiniteNumber]);

  const handleRecipeStepBack = useCallback(() => {
    setAddStockStep((prev) => {
      const nextStep = Math.max(1, prev - 1);
      if (nextStep === 1) {
        resetAddStockSelection();
      }
      return nextStep;
    });
    setAddStockError('');
  }, [resetAddStockSelection]);

  const handleRecipeSearchChange = useCallback((event) => {
    const nextValue = event.target.value;
    setRecipeSearchTerm(nextValue);
    setHasRecipeSearchInput(Boolean(String(nextValue || '').trim()));
  }, []);

  // Monotonic sequence for ingredient loads. Every recipe/prep load bumps this;
  // async continuations (batch-price fetches) compare against it so an older,
  // slower load can never overwrite the state a newer load has produced.
  const recipeIngredientsRequestRef = useRef(0);

  const autoFillRecipeIngredientPrices = useCallback(async (ingredients, requestId) => {
    if (!Array.isArray(ingredients) || ingredients.length === 0) {
      return;
    }

    const updated = await Promise.all(
      ingredients.map(async (ingredient) => {
        if (ingredient?.ingredientMissing || !ingredient?.inventoryId) {
          return ingredient;
        }

        const fallbackPrice = parseFiniteNumber(ingredient?.unitCostFallback) || 0;
        const previousBatch = await fetchPreviousBatchUnitPrice(ingredient.inventoryId, fallbackPrice);
        const basePrice = parseFiniteNumber(previousBatch?.price) || 0;
        if (!Number.isFinite(basePrice) || basePrice <= 0) {
          return ingredient;
        }

        const previousUnit = previousBatch?.unit || null;
        let resolvedPrice = basePrice;
        if (previousUnit && ingredient?.unit) {
          const multiplier = getCostUnitMultiplier(previousUnit, ingredient.unit);
          if (multiplier !== null) {
            resolvedPrice = basePrice * multiplier;
          }
        }

        if (!Number.isFinite(resolvedPrice) || resolvedPrice <= 0) {
          return ingredient;
        }

        const quantity = parseFiniteNumber(ingredient?.scaledQuantity) || 0;
        const totalPrice = quantity > 0 ? formatTotalInputValue(resolvedPrice * quantity) : '';

        return {
          ...ingredient,
          priceMode: 'unit',
          unitPrice: formatPriceInputValue(resolvedPrice),
          totalPrice,
          priceSource: previousUnit ? 'Latest batch' : (fallbackPrice > 0 ? 'Recipe cost' : '')
        };
      })
    );

    // Drop stale auto-fills: a newer ingredient load may have started while these
    // batch prices were in flight.
    if (requestId !== undefined && recipeIngredientsRequestRef.current !== requestId) {
      return;
    }

    // Merge computed prices into the LIVE state (not the stale snapshot) so a price
    // the user typed during the fetch window is preserved rather than reverted.
    setRecipeIngredients((prev) => {
      if (requestId !== undefined && recipeIngredientsRequestRef.current !== requestId) {
        return prev;
      }
      if (!Array.isArray(prev) || prev.length !== updated.length) {
        return prev;
      }
      return prev.map((current, idx) => {
        const computed = updated[idx];
        if (!computed) return current;
        // Preserve any price the user has already entered for this row.
        const userEnteredPrice =
          (current.unitPrice !== '' && current.unitPrice != null) ||
          (current.totalPrice !== '' && current.totalPrice != null);
        if (userEnteredPrice) return current;
        return {
          ...current,
          priceMode: computed.priceMode,
          unitPrice: computed.unitPrice,
          totalPrice: computed.totalPrice,
          priceSource: computed.priceSource
        };
      });
    });
  }, [fetchPreviousBatchUnitPrice, formatPriceInputValue, formatTotalInputValue, getCostUnitMultiplier, parseFiniteNumber]);

  const loadPreparationIngredients = useCallback(async (prepItem, preparedQty) => {
    const requestId = ++recipeIngredientsRequestRef.current;
    setIsAutoFillingRecipePrices(true);
    setAddStockError('');

    try {
      const prepComponents = Array.isArray(prepItem?.components) ? prepItem.components : [];
      const totalWeight = parseFiniteNumber(prepItem?.total_weight);
      const totalUnit = canonicalGlobalUnit(prepItem?.total_weight_unit_symbol || prepItem?.total_weight_unit_name || prepItem?.total_unit || '');
      const preparedValue = parseFiniteNumber(preparedQty);

      const mapped = prepComponents.map((component) => {
        const inventoryId = Number(component?.atomic_item_id);
        const matchedItem = Number.isFinite(inventoryId)
          ? inventoryItems.find((item) => Number(item?.id) === inventoryId)
          : inventoryItems.find((item) => normalizeInventoryName(item?.name) === normalizeInventoryName(component?.item_name));
        const resolvedInventoryId = Number.isFinite(inventoryId)
          ? inventoryId
          : (matchedItem ? Number(matchedItem.id) : null);
        const ingredientMissing = !resolvedInventoryId;
        const baseQuantity = parseFiniteNumber(component?.weight ?? component?.quantity) || 0;
        const unit = canonicalGlobalUnit(component?.unit_symbol || component?.unit_name || matchedItem?.rawUnit || matchedItem?.unit || '')
          || (component?.unit_symbol || component?.unit_name || matchedItem?.rawUnit || matchedItem?.unit || 'units');

        let scaledQuantity = baseQuantity;
        if (Number.isFinite(preparedValue) && preparedValue > 0 && Number.isFinite(totalWeight) && totalWeight > 0 && totalUnit) {
          const totalInUnit = convertQuantity(totalWeight, totalUnit, unit) ?? totalWeight;
          const preparedInUnit = convertQuantity(preparedValue, totalUnit, unit) ?? preparedValue;
          if (Number.isFinite(totalInUnit) && totalInUnit > 0) {
            scaledQuantity = baseQuantity * (preparedInUnit / totalInUnit);
          }
        }

        return {
          id: component?.id,
          inventoryId: resolvedInventoryId,
          name: component?.item_name || matchedItem?.name || 'Unknown Ingredient',
          unit,
          baseQuantity,
          scaledQuantity,
          quantitySource: 'auto',
          ingredientMissing,
          unitCostFallback: 0,
          priceMode: 'unit',
          unitPrice: '',
          totalPrice: '',
          priceSource: ''
        };
      });

      if (recipeIngredientsRequestRef.current !== requestId) return;
      setRecipeIngredients(mapped);
      await autoFillRecipeIngredientPrices(mapped, requestId);
    } catch (error) {
      if (recipeIngredientsRequestRef.current !== requestId) return;
      console.warn('Unable to load preparation ingredients:', error);
      setRecipeIngredients([]);
      setAddStockError('Failed to load preparation ingredients.');
    } finally {
      if (recipeIngredientsRequestRef.current === requestId) {
        setIsAutoFillingRecipePrices(false);
      }
    }
  }, [autoFillRecipeIngredientPrices, convertQuantity, inventoryItems, normalizeInventoryName, parseFiniteNumber]);

  const loadRecipeIngredients = useCallback(async (recipeId, servings) => {
    const requestId = ++recipeIngredientsRequestRef.current;
    setIsAutoFillingRecipePrices(true);
    setAddStockError('');

    try {
      const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId');
      if (!businessId) {
        setRecipeIngredients([]);
        setAddStockError('Business not available.');
        return;
      }

      const result = await http.get(`/recipes/${recipeId}/ingredients?businessId=${businessId}`);
      const ingredients = Array.isArray(result?.data) ? result.data : [];
      const baseServings = (parseFiniteNumber(servings) || 1);
      const batchMultiplier = recipeQuantityMode === 'batches' ? recipeBatchServings : 1;
      const servingMultiplier = baseServings * batchMultiplier;

      const mapped = ingredients.map((ingredient) => {
        const inventoryId = Number(ingredient?.inventory_id);
        const matchedItem = Number.isFinite(inventoryId)
          ? inventoryItems.find((item) => Number(item?.id) === inventoryId)
          : inventoryItems.find((item) => normalizeInventoryName(item?.name) === normalizeInventoryName(ingredient?.ingredient_name));
        const resolvedInventoryId = Number.isFinite(inventoryId)
          ? inventoryId
          : (matchedItem ? Number(matchedItem.id) : null);
        const ingredientMissing = Boolean(ingredient?.ingredient_missing) || !resolvedInventoryId;
        const baseQuantity = parseFiniteNumber(ingredient?.quantity) || 0;
        const unit = canonicalGlobalUnit(ingredient?.unit || ingredient?.unit_name || matchedItem?.rawUnit || matchedItem?.unit || '')
          || (ingredient?.unit || ingredient?.unit_name || matchedItem?.rawUnit || matchedItem?.unit || 'units');

        return {
          id: ingredient?.id,
          inventoryId: resolvedInventoryId,
          name: ingredient?.ingredient_name || matchedItem?.name || 'Unknown Ingredient',
          unit,
          baseQuantity,
          scaledQuantity: baseQuantity * servingMultiplier,
          quantitySource: 'auto',
          ingredientMissing,
          unitCostFallback: parseFiniteNumber(ingredient?.unit_cost) || 0,
          priceMode: 'unit',
          unitPrice: '',
          totalPrice: '',
          priceSource: ''
        };
      });

      // Ensure 'basmati rice' appears after other ingredients for clearer layout
      const ordered = [...mapped].sort((a, b) => {
        const aIsBasmati = String(a.name || '').toLowerCase().includes('basmati');
        const bIsBasmati = String(b.name || '').toLowerCase().includes('basmati');
        if (aIsBasmati && !bIsBasmati) return 1;
        if (!aIsBasmati && bIsBasmati) return -1;
        return 0;
      });

      if (recipeIngredientsRequestRef.current !== requestId) return;
      setRecipeIngredients(ordered);
      await autoFillRecipeIngredientPrices(ordered, requestId);
    } catch (error) {
      if (recipeIngredientsRequestRef.current !== requestId) return;
      console.warn('Unable to load recipe ingredients:', error);
      setRecipeIngredients([]);
      setAddStockError('Failed to load ingredient list.');
    } finally {
      if (recipeIngredientsRequestRef.current === requestId) {
        setIsAutoFillingRecipePrices(false);
      }
    }
  }, [autoFillRecipeIngredientPrices, inventoryItems, normalizeInventoryName, parseFiniteNumber, recipeBatchServings, recipeQuantityMode]);

  const handleRecipeQuantityNext = useCallback(async () => {
    const servings = parseFiniteNumber(recipeServingQty);
    const recipeId = selectedPreparation ? null : getRecipeId(selectedRecipe);
    if (!selectedPreparation && !recipeId) {
      setAddStockError('Select a menu item to continue.');
      setAddStockStep(1);
      return;
    }

    if (servings === null || servings <= 0) {
      setAddStockError('Enter a valid quantity.');
      return;
    }

    if (selectedPreparation) {
      setAddStockStep(3);
      await loadPreparationIngredients(selectedPreparation, servings);
      return;
    }

    if (recipeQuantityMode === 'batches' && (!recipeBatchServings || recipeBatchServings <= 0)) {
      setAddStockError('Enter a valid batch size.');
      return;
    }

    setAddStockStep(3);
    await loadRecipeIngredients(recipeId, servings);
  }, [getRecipeId, loadPreparationIngredients, loadRecipeIngredients, parseFiniteNumber, recipeBatchServings, recipeQuantityMode, recipeServingQty, selectedPreparation, selectedRecipe]);

  const handleRecipeIngredientPriceModeChange = useCallback((index, nextMode) => {
    if (nextMode !== 'unit' && nextMode !== 'total') {
      return;
    }

    setRecipeIngredients((prev) => {
      if (!prev[index]) return prev;
      const next = [...prev];
      const current = next[index];
      const quantity = parseFiniteNumber(current?.scaledQuantity) || 0;
      const unitPrice = parseFiniteNumber(current?.unitPrice);
      const totalPrice = parseFiniteNumber(current?.totalPrice);
      const updated = {
        ...current,
        priceMode: nextMode,
        priceSource: 'Manual'
      };

      if (nextMode === 'total') {
        if ((current?.totalPrice === '' || totalPrice === null) && unitPrice !== null && quantity > 0) {
          updated.totalPrice = formatTotalInputValue(unitPrice * quantity);
        }
      } else if ((current?.unitPrice === '' || unitPrice === null) && totalPrice !== null && quantity > 0) {
        updated.unitPrice = formatPriceInputValue(totalPrice / quantity);
      }

      next[index] = updated;
      return next;
    });
  }, [formatPriceInputValue, formatTotalInputValue, parseFiniteNumber]);

  const handleRecipeIngredientQuantityChange = useCallback((index, value) => {
    if (value !== '' && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
      return;
    }

    setRecipeIngredients((prev) => {
      if (!prev[index]) return prev;
      const next = [...prev];
      const current = next[index];
      const priceMode = current?.priceMode === 'total' ? 'total' : 'unit';
      const totalPrice = parseFiniteNumber(current?.totalPrice);
      const unitPrice = parseFiniteNumber(current?.unitPrice);
      const quantityValue = value === '' ? '' : value;
      const quantity = value === '' ? null : parseFiniteNumber(value);
      const updated = {
        ...current,
        scaledQuantity: quantityValue,
        quantitySource: 'manual'
      };

      if (quantity !== null && quantity > 0) {
        if (priceMode === 'total') {
          if (totalPrice !== null) {
            updated.unitPrice = formatPriceInputValue(totalPrice / quantity);
          }
        } else if (unitPrice !== null) {
          updated.totalPrice = formatTotalInputValue(unitPrice * quantity);
        }
      } else if (priceMode === 'unit' && unitPrice !== null) {
        updated.totalPrice = '';
      }

      next[index] = updated;
      return next;
    });
  }, [formatPriceInputValue, formatTotalInputValue, parseFiniteNumber]);

  const handleRecipeIngredientPriceChange = useCallback((index, value) => {
    if (value !== '' && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
      return;
    }

    setRecipeIngredients((prev) => {
      if (!prev[index]) return prev;
      const next = [...prev];
      const current = next[index];
      const priceMode = current?.priceMode === 'total' ? 'total' : 'unit';
      const quantity = parseFiniteNumber(current?.scaledQuantity) || 0;
      const updated = {
        ...current,
        priceSource: 'Manual'
      };

      if (priceMode === 'total') {
        updated.totalPrice = value;
        const totalPrice = parseFiniteNumber(value);
        if (totalPrice !== null && quantity > 0) {
          updated.unitPrice = formatPriceInputValue(totalPrice / quantity);
        }
      } else {
        updated.unitPrice = value;
        const unitPrice = parseFiniteNumber(value);
        if (unitPrice !== null && quantity > 0) {
          updated.totalPrice = formatTotalInputValue(unitPrice * quantity);
        }
      }

      next[index] = updated;
      return next;
    });
  }, [formatPriceInputValue, formatTotalInputValue, parseFiniteNumber]);

  const handleRecipeAddStockSave = useCallback(async () => {
    if (isSavingRecipeStock) return;

    const servingQty = parseFiniteNumber(recipeServingQty);
    if (servingQty === null || servingQty <= 0) {
      setAddStockError('Enter a valid quantity.');
      setAddStockStep(2);
      return;
    }

    const missingQuantity = recipeIngredients.find((ingredient) => {
      if (!ingredient?.inventoryId || ingredient?.ingredientMissing) {
        return false;
      }
      const quantity = parseFiniteNumber(ingredient?.scaledQuantity);
      return quantity === null || quantity <= 0;
    });

    if (missingQuantity) {
      setAddStockError('Enter quantity for all ingredients.');
      return;
    }

    const validIngredients = recipeIngredients.filter((ingredient) => {
      const quantity = parseFiniteNumber(ingredient?.scaledQuantity);
      return Boolean(ingredient?.inventoryId)
        && !ingredient?.ingredientMissing
        && quantity !== null
        && quantity > 0;
    });

    if (validIngredients.length === 0) {
      setAddStockError('No ingredients available to add.');
      return;
    }

    const missingPrice = validIngredients.find((ingredient) => {
      const priceMode = ingredient?.priceMode === 'total' ? 'total' : 'unit';
      if (priceMode === 'total') {
        const totalPrice = parseFiniteNumber(ingredient?.totalPrice);
        return totalPrice === null || totalPrice <= 0;
      }
      const unitPrice = parseFiniteNumber(ingredient?.unitPrice);
      return unitPrice === null || unitPrice <= 0;
    });

    if (missingPrice) {
      setAddStockError('Enter price for all ingredients.');
      return;
    }

    setIsSavingRecipeStock(true);
    setAddStockError('');

    try {
      const businessId = UnitMappingService.getBusinessId();
      if (!businessId) {
        setAddStockError('Business not available.');
        return;
      }

      await Promise.all(
        validIngredients.map(async (ingredient) => {
          const quantity = parseFiniteNumber(ingredient?.scaledQuantity) || 0;
          const pricing = getRecipeIngredientPricing(ingredient);
          const unitPrice = Number.isFinite(pricing.unitPrice) ? pricing.unitPrice : 0;
          const totalPrice = Number.isFinite(pricing.totalPrice) ? pricing.totalPrice : (quantity * unitPrice);

          return http.post('/stock-in/v2/onboarding/save-draft', {
            businessId,
            item_id: ingredient.inventoryId,
            item_name: ingredient.name,
            quantity,
            unit: ingredient.unit || 'units',
            unit_price: unitPrice,
            total_price: totalPrice,
            price_mode: pricing.priceMode,
            pack_quantity: 1,
            pack_size: 1,
            raw_quantity: quantity,
            unit_conversion: null,
            conversion_unit: null,
            is_negative_fix: false
          });
        })
      );

      await fetchOnboardingDrafts();

      setBatchDetails((prev) => {
        let changed = false;
        const next = { ...prev };
        validIngredients.forEach((ingredient) => {
          if (ingredient.inventoryId && Object.prototype.hasOwnProperty.call(next, ingredient.inventoryId)) {
            delete next[ingredient.inventoryId];
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      fetchInventoryDataRef.current(false);
      setShowAddStockWizard(false);
      resetAddStockWizard();
    } catch (error) {
      console.error('Failed to add stock from recipe:', error);
      setAddStockError('Failed to add stock. Please try again.');
    } finally {
      setIsSavingRecipeStock(false);
    }
  }, [fetchOnboardingDrafts, getRecipeIngredientPricing, isSavingRecipeStock, parseFiniteNumber, recipeIngredients, recipeServingQty, resetAddStockWizard]);

  const openClosingStockCorrectionModal = useCallback(async (item) => {
    const itemId = Number(item?.id);
    if (!Number.isFinite(itemId) || itemId <= 0) {
      return;
    }

    const draft = closingStockCorrectionDrafts[itemId] || closingStockCorrectionDrafts[String(itemId)];
    const resolvedUnit = String(draft?.unit || item?.rawUnit || item?.unit || 'g').trim() || 'g';
    const existingDraftPrice = parseFiniteNumber(draft?.unitPrice);
    const shouldLoadPrice = !(existingDraftPrice !== null && existingDraftPrice > 0);

    setSelectedClosingStockCorrectionItem({
      itemId,
      itemName: item?.name || 'Item',
      unit: resolvedUnit,
      quantity: draft?.quantity ?? '',
      unitPrice: existingDraftPrice !== null && existingDraftPrice > 0
        ? existingDraftPrice
        : null,
      previousUnit: null,
      isLoadingPrice: shouldLoadPrice
    });

    if (!shouldLoadPrice) {
      return;
    }

    const fallbackPrice = parseFiniteNumber(item?.unitCost) || 0;
    const previousBatch = await fetchPreviousBatchUnitPrice(itemId, fallbackPrice);

    // Compute an initial manual total if possible so modal shows value on first open
    const qty = parseFiniteNumber(draft?.quantity) ?? parseFiniteNumber(item?.quantity) ?? 0;
    let initialManualTotal = '';
    if (previousBatch && previousBatch.price && previousBatch.price > 0) {
      const multiplier = getCostUnitMultiplier(previousBatch.unit || previousBatch.unit_symbol || previousBatch.unit_symbol, resolvedUnit);
      if (Number.isFinite(qty) && qty > 0 && Number.isFinite(previousBatch.price) && multiplier !== null) {
        const convertedUnitPrice = previousBatch.price * multiplier;
        const computedTotal = qty * convertedUnitPrice;
        initialManualTotal = String(Number(computedTotal.toFixed(2)));
      }
    }

    setSelectedClosingStockCorrectionItem((prev) => {
      if (!prev || prev.itemId !== itemId) {
        return prev;
      }

      return {
        ...prev,
        unitPrice: previousBatch.price || 0,
        previousUnit: previousBatch.unit || previousBatch.unit_symbol || null,
        initialManualTotal: initialManualTotal,
        isLoadingPrice: false
      };
    });
  }, [closingStockCorrectionDrafts, fetchPreviousBatchUnitPrice, parseFiniteNumber]);

  const closeClosingStockCorrectionModal = useCallback(() => {
    setSelectedClosingStockCorrectionItem(null);
  }, []);

  const saveClosingStockCorrectionDraft = useCallback((draftInput) => {
    const itemId = Number(draftInput?.itemId);
    const quantity = parseFiniteNumber(draftInput?.quantity);
    const unitPrice = parseFiniteNumber(draftInput?.unitPrice);
    const inputTotalPrice = parseFiniteNumber(draftInput?.totalPrice);

    if (!Number.isFinite(itemId) || itemId <= 0) {
      alert('Invalid ingredient selected for closing stock correction.');
      return false;
    }

    if (quantity === null || quantity < 0) {
      alert('Please enter a valid quantity (0 or greater).');
      return false;
    }

    if (unitPrice === null || unitPrice <= 0) {
      alert('Please enter a valid price greater than zero.');
      return false;
    }

    const normalizedUnit = String(draftInput?.unit || 'g').trim() || 'g';
    const totalPrice = inputTotalPrice !== null && inputTotalPrice >= 0
      ? inputTotalPrice
      : (quantity * unitPrice);

    setClosingStockCorrectionDrafts((prev) => ({
      ...prev,
      [itemId]: {
        itemId,
        itemName: String(draftInput?.itemName || ''),
        quantity,
        unit: normalizedUnit,
        unitPrice,
        totalPrice,
        updatedAt: Date.now()
      }
    }));

    setSelectedClosingStockCorrectionItem(null);
    return true;
  }, [parseFiniteNumber]);

  const cancelClosingStockCorrections = useCallback(() => {
    setClosingStockCorrectionDrafts({});
    setSelectedClosingStockCorrectionItem(null);
    setShowClosingStockApplyConfirmModal(false);
  }, []);

  const resolveClosingStockCorrectionDate = useCallback(() => {
    if (isValidISODateString(recoveryDate)) {
      return String(recoveryDate).trim();
    }

    const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId') || 'unknown';
    const businessScopedAuditDate = localStorage.getItem(`weeklyAudit.selectedClosingDate_${businessId}`);
    if (isValidISODateString(businessScopedAuditDate)) {
      return String(businessScopedAuditDate).trim();
    }

    const globalAuditDate = localStorage.getItem('weeklyAudit.selectedClosingDate');
    if (isValidISODateString(globalAuditDate)) {
      return String(globalAuditDate).trim();
    }

    return getISTDateString();
  }, [recoveryDate]);

  const openClosingStockApplyConfirmModal = useCallback(() => {
    const draftEntries = Object.values(closingStockCorrectionDrafts || {});
    if (isApplyingClosingStockCorrections || draftEntries.length === 0) {
      return;
    }
    setClosingStockCorrectionDate(resolveClosingStockCorrectionDate());
    setShowClosingStockApplyConfirmModal(true);
  }, [closingStockCorrectionDrafts, isApplyingClosingStockCorrections, resolveClosingStockCorrectionDate]);

  const closeClosingStockApplyConfirmModal = useCallback(() => {
    if (isApplyingClosingStockCorrections) {
      return;
    }
    setShowClosingStockApplyConfirmModal(false);
  }, [isApplyingClosingStockCorrections]);

  const persistClosingStockCorrectionToggleState = useCallback(async (enabled) => {
    const correctionToggleStorageKey = getClosingStockCorrectionToggleStorageKey();
    const normalizedEnabled = Boolean(enabled);

    // Apply immediately in UI/local storage so correction modal does not keep reopening.
    setClosingStockCorrectionEnabled(normalizedEnabled);
    localStorage.setItem(correctionToggleStorageKey, normalizedEnabled ? 'true' : 'false');

    const businessId = Number(UnitMappingService.getBusinessId() || localStorage.getItem('businessId') || 0);
    window.dispatchEvent(new CustomEvent('inventory:closing-stock-correction-toggle-updated', {
      detail: {
        businessId,
        enabled: normalizedEnabled,
      }
    }));

    if (!Number.isFinite(businessId) || businessId <= 0) {
      return false;
    }

    try {
      await SettingsService.upsertSettings(businessId, [{
        key: CLOSING_STOCK_CORRECTION_TOGGLE_KEY,
        value: normalizedEnabled,
        data_type: 'boolean',
        module_scope: 'inventory',
        description: 'When enabled, Overview displays zero and allows closing-stock correction entry.',
      }]);
      return true;
    } catch (error) {
      console.warn('⚠️ Failed to persist closing stock correction toggle state.', error?.message || error);
      return false;
    }
  }, [getClosingStockCorrectionToggleStorageKey]);

  const applyClosingStockCorrections = useCallback(async () => {
    const draftEntries = Object.values(closingStockCorrectionDrafts || {});
    const normalizedClosingStockDate = String(closingStockCorrectionDate || '').trim();

    if (isApplyingClosingStockCorrections || draftEntries.length === 0) {
      return;
    }

    if (!isValidISODateString(normalizedClosingStockDate)) {
      alert('Please select a valid closing stock date (YYYY-MM-DD).');
      return;
    }

    setShowClosingStockApplyConfirmModal(false);

    // Confirm action should auto-disable correction toggle so the correction modal does not reopen repeatedly.
    await persistClosingStockCorrectionToggleState(false);

    const businessId = Number(UnitMappingService.getBusinessId() || localStorage.getItem('businessId') || 0);
    if (!Number.isFinite(businessId) || businessId <= 0) {
      alert('Unable to determine business context. Please refresh and try again.');
      return;
    }

    setIsApplyingClosingStockCorrections(true);

    try {
      const corrections = draftEntries.map((draft) => ({
        item_id: Number(draft.itemId),
        item_name: String(draft.itemName || ''),
        quantity: Number(draft.quantity),
        unit: String(draft.unit || 'g'),
        unit_price: Number(draft.unitPrice),
        total_price: Number(draft.totalPrice || (Number(draft.quantity) * Number(draft.unitPrice)))
      }));

      const result = await http.post('/stock-in/v2/onboarding/apply-corrections', {
        businessId,
        closingStockDate: normalizedClosingStockDate,
        corrections
      });

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to apply closing stock corrections');
      }

      // Keep UI and settings page in sync even when they are already mounted.
      const correctionToggleStorageKey = getClosingStockCorrectionToggleStorageKey();
      setClosingStockCorrectionEnabled(false);
      localStorage.setItem(correctionToggleStorageKey, 'false');
      window.dispatchEvent(new CustomEvent('inventory:closing-stock-correction-toggle-updated', {
        detail: {
          businessId,
          enabled: false,
        }
      }));

      setClosingStockCorrectionDrafts({});
      setSelectedClosingStockCorrectionItem(null);

      await fetchOnboardingDrafts();
      await fetchInventoryData(true);

      alert(result?.message || 'Closing stock corrections applied successfully.');
    } catch (error) {
      console.error('Failed to apply closing stock corrections:', error);
      const errorMessage = error && typeof error.message === 'string'
        ? error.message
        : 'Failed to apply closing stock corrections.';
      alert(errorMessage);
    } finally {
      setIsApplyingClosingStockCorrections(false);
    }
  }, [closingStockCorrectionDrafts, closingStockCorrectionDate, isApplyingClosingStockCorrections, fetchOnboardingDrafts, fetchInventoryData, persistClosingStockCorrectionToggleState, getClosingStockCorrectionToggleStorageKey]);
  
  // Handle opening onboarding stock modal
  const openOnboardingStockModal = useCallback((item) => {
    const isNegativeStockItem = Number(item?.quantity) < 0;
    if (shouldBlockClosingStockModal && !isNegativeStockItem) {
      return;
    }

    setNegativeFixFlowActive(false);

    // After first open, stop showing repeated closing-stock prompts.
    if (!closingStockEntryDismissed) {
      dismissClosingStockEntry();
    }
    
    setSelectedOnboardingItem(item);
    setOnboardingStockModal(true);
  }, [shouldBlockClosingStockModal, closingStockEntryDismissed, dismissClosingStockEntry]);

  const openNegativeFixModal = useCallback((item) => {
    const parseDisplayedQuantity = (quantityDisplay) => {
      const text = String(quantityDisplay || '').replace(/,/g, '').trim();
      const match = text.match(/(-?\d+(?:\.\d+)?)\s*([A-Za-z]+)/);
      if (!match) return null;
      const parsedQty = Math.abs(parseFloat(match[1]));
      const parsedUnit = match[2];
      if (!Number.isFinite(parsedQty) || parsedQty <= 0) return null;
      return { quantity: parsedQty, unit: parsedUnit };
    };

    const displayed = parseDisplayedQuantity(item?.quantityDisplay);
    const exactQty = Math.abs(Number(item?.rawQuantity ?? item?.quantity ?? 0));
    const exactUnit = String(item?.rawUnit || item?.unit || '').trim();

    // For negative-fix UX, prefill exactly what user sees on the card (rounded display quantity + display unit).
    const fixSuggestedQuantity = (displayed?.quantity ?? (Number.isFinite(exactQty) && exactQty > 0 ? exactQty : ''));
    const fixSuggestedUnit = displayed?.unit || exactUnit || 'g';

    setNegativeFixFlowActive(true);
    setSelectedOnboardingItem({
      ...item,
      fixSuggestedQuantity,
      fixSuggestedUnit
    });
    setOnboardingStockModal(true);
  }, []);
  
  // Handle closing onboarding stock modal
  const closeOnboardingStockModal = useCallback(() => {
    setOnboardingStockModal(false);
    setSelectedOnboardingItem(null);
    setNegativeFixFlowActive(false);
  }, []);
  
  // Handle saving onboarding stock draft
  const saveOnboardingStockDraft = useCallback(async (itemId, quantity, unitPrice, totalPrice, priceMode, packQuantity, packSize, unit, unitConversion, conversionUnit) => {
    try {
      const bId = UnitMappingService.getBusinessId();
      const item = inventoryItems.find(i => i.id === itemId);
      if (!item) {
        console.error(`❌ Item not found for save: ${itemId}`);
        return false;
      }

      // ── PIECE GUARD §4 on the OPENING BATCH ────────────────────────────────
      // This is the one place the read-back matters most and the one place nothing
      // else can catch the mistake. Opening stock has no price history, so the money
      // check has nothing to compare against — §9.3's second and third nets do not
      // exist yet. The read-back ("so one piece costs ₹27.50 — does that sound right
      // for one Kit Kat bar?") is the ONLY guard here, and a wrong opening batch is
      // the worst kind: it becomes the benchmark every later, correct purchase is then
      // measured against and flagged for.
      const effectiveUnit = String(unit || item.unit || '').trim().toLowerCase();
      if (['pcs', 'piece', 'pieces', 'pc'].includes(effectiveUnit) && !piecePromptResolvedRef.current.has(itemId)) {
        try {
          const pr = await http.get(`/stock-in/v2/piece-profiles?businessId=${bId}&itemIds=${itemId}`);
          const existing = pr?.profiles?.[itemId] || null;
          if (!existing && !pr?.unavailable) {
            // Defer the save and ask what one piece is. The modal calls back into this
            // same function once the definition is confirmed, so there is exactly one
            // save path — the question is inserted in front of it, not around it.
            setPiecePrompt({
              itemId,
              itemName: item.name,
              category: item.category || item.category_name || '',
              quantity,
              totalPrice: totalPrice != null ? totalPrice : (Number(quantity) * Number(unitPrice || 0)),
              pendingArgs: [itemId, quantity, unitPrice, totalPrice, priceMode, packQuantity, packSize, unit, unitConversion, conversionUnit],
            });
            return false;
          }
        } catch (profileErr) {
          // Piece profiles unavailable (migration not applied, or the request failed).
          // Onboarding must never be blocked by a hint it could not fetch.
          console.debug('Piece profile check skipped during onboarding:', profileErr.message);
        }
      }
      const isNegativeFixEntry = negativeFixFlowActive || Number(item.quantity || 0) < 0;
      
      const result = await http.post('/stock-in/v2/onboarding/save-draft', {
        businessId: bId,
        item_id: itemId,
        item_name: item.name,
        quantity,
        unit: unit || item.unit,
        unit_price: unitPrice,
        total_price: totalPrice,
        price_mode: priceMode,
        pack_quantity: packQuantity,
        pack_size: packSize,
        raw_quantity: quantity,
        unit_conversion: unitConversion,
        conversion_unit: conversionUnit,
        is_negative_fix: isNegativeFixEntry
      });
      
      if (result.success) {
        console.log(`✅ Stock saved for ${item.name}`);
        if (result.batch_created) {
          console.log(`✅ BATCH-01 created automatically - closing stock is now active`);
        }
        
        // Reload drafts immediately to update state
        await fetchOnboardingDrafts();
        
        // Clear batch details cache for this item so it re-fetches with new batch
        setBatchDetails(prev => {
          const newDetails = { ...prev };
          delete newDetails[itemId];
          return newDetails;
        });
        
        // Refresh inventory to show updated quantity
        fetchInventoryDataRef.current(false);
        closeOnboardingStockModal();
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Error saving draft:', error);
      return false;
    }
  }, [inventoryItems, closeOnboardingStockModal, negativeFixFlowActive]);

  // Finalize onboarding - convert drafts to batches
  const finalizeOnboarding = useCallback(async () => {
    try {
      const bId = UnitMappingService.getBusinessId();
      const draftCount = Object.keys(onboardingDrafts).length;
      
      if (draftCount === 0) {
        alert('No closing stock entered yet. Please add stock for at least one item.');
        return false;
      }
      
      console.log(`🎯 Finalizing onboarding: Converting ${draftCount} drafts to batches...`);

      const result = await http.post('/stock-in/v2/onboarding/finalize', {
        businessId: bId,
        closingStockDate: onboardingClosingDate
      });

      if (result.success) {
        console.log(`✅ Onboarding finalized! ${result.batches_created} batches created`);

        // The server recomputed the period from the date it actually stored —
        // show that, never the local guess.
        if (result.tracking_period) setTrackingPeriod(result.tracking_period);
        const trackingLine = describeTrackingPeriod(result.tracking_period);

        // Show success message
        alert(`✅ Success! Closing stock finalized for ${result.batches_created} items.\n\n` +
              `Total value: ₹${result.total_value ? result.total_value.toFixed(2) : '0.00'}\n\n` +
              (trackingLine ? `${trackingLine}.\n\n` : '') +
              `Your closing stock is now converted to batches (BATCH-01) and will remain visible in Overview.`);

        // Clear drafts since they're now converted to batches
        setOnboardingDrafts({});
        
        // Clear all batch details cache to force refresh
        setBatchDetails({});
        
        // Refresh inventory to show finalized batches
        await fetchInventoryDataRef.current(false);
        
        // Small delay to let the refresh complete
        setTimeout(() => {
          // Navigate to Stock In tab
          setActiveTab('stock-in');
        }, 500);
        
        return true;
      }
      
      alert('Failed to finalize closing stock. Please try again.');
      return false;
    } catch (error) {
      console.error('❌ Error finalizing onboarding:', error);
      alert('Error finalizing closing stock: ' + (error.message || 'Unknown error'));
      return false;
    }
  }, [onboardingDrafts, onboardingClosingDate]);

  // Whether this business's opening stock is ALREADY on the books — committed
  // through the Closing Stock page, or seeded — as opposed to sitting in
  // onboarding drafts waiting to be finalised.
  const hasCommittedOpeningStock = Boolean(trackingPeriod?.closingStockDate);

  // "Save & Continue" — the opening count is committed and the flow moves on to
  // the next onboarding step. It does NOT complete the setup; that happens once
  // when the last step is done (App.js `completeOnboarding`).
  const handleOnboardingSaveBtnClick = useCallback(async () => {
    try {
      const bId = UnitMappingService.getBusinessId();
      const draftCount = Object.keys(onboardingDrafts).length;

      // Nothing to finalise and nothing on the books — there is no opening
      // stock at all, and the step genuinely is not done.
      if (draftCount === 0 && !hasCommittedOpeningStock) return;

      if (draftCount === 0) {
        // The opening count is already recorded (a committed closing stock, or
        // a seeded BATCH-01). Calling finalize here would 400 with "No
        // onboarding drafts found" — which is how this button came to be a
        // dead end for exactly the businesses that had already done the work.
        if (onOnboardingComplete) onOnboardingComplete();
        return;
      }

      const result = await http.post('/stock-in/v2/onboarding/finalize', {
        businessId: bId,
        // The day the shelves were counted. Everything after it is the app's.
        closingStockDate: onboardingClosingDate
      });

      if (result.success) {
        if (result.tracking_period) setTrackingPeriod(result.tracking_period);
        setOnboardingDrafts({});
        setBatchDetails({});
        if (onOnboardingComplete) {
          onOnboardingComplete();
        }
      } else {
        alert(result.error || 'Failed to save onboarding stock');
      }
    } catch (err) {
      console.error('Failed to finalize onboarding:', err);
      alert('Error finalizing onboarding: ' + err.message);
    }
  }, [onboardingDrafts, onboardingClosingDate, hasCommittedOpeningStock, onOnboardingComplete]);

  // Delete item from database
  const deleteItem = useCallback(async (itemId, batchId) => {
    try {
      const result = await http.delete(`/inventory/items/${itemId}/batches/${batchId}`);

      if (result.success) {
        // Clear batch details cache for the deleted item
        setBatchDetails(prev => {
          const newDetails = { ...prev };
          delete newDetails[itemId];
          return newDetails;
        });
        
        // Remove from loading batches if it was loading
        setLoadingBatches(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });

        // Immediately refresh inventory data after successful deletion
        fetchInventoryData(false);

        // Remove from expanded cards if it was expanded
        setExpandedCards(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });

        // Clear any existing errors
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      setError(`Failed to delete item: ${error.message}`);
    }
  }, [fetchInventoryData]);

  // Delete entire inventory item (only if not in active use)
  const deleteEntireItem = useCallback(async (itemId) => {
    setDeleteCheckLoading(true);
    setDeleteUsageDetails(null);
    try {
      // Pass any recipe/prep/complimentary link rows that were removed before this call
      // so the backend can merge them into the structural_action_log snapshot.
      const result = await http.delete(`/inventory/items/${itemId}`, {
        previouslyRemovedLinks: deletePendingRemovedLinksRef.current,
        previouslyRemovedPreps: deletePendingRemovedPrepsRef.current,
        previouslyRemovedComplimentary: deletePendingRemovedCompRef.current
      });
      if (result.success) {
        // Clear caches and refresh
        setBatchDetails(prev => { const n = { ...prev }; delete n[itemId]; return n; });
        setLoadingBatches(prev => { const s = new Set(prev); s.delete(itemId); return s; });
        setExpandedCards(prev => { const s = new Set(prev); s.delete(itemId); return s; });
        anyModalOpenRef.current = false;
        setDeleteConfirmItem(null);
        setDeleteUsageDetails(null);
        // Clear accumulated removed links
        deletePendingRemovedLinksRef.current = [];
        deletePendingRemovedPrepsRef.current = [];
        deletePendingRemovedCompRef.current = [];
        setError(null);
        fetchInventoryData(false);
      }
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      // 409 = item is in active use in recipes/preparations/complimentary items
      if (error?.status === 409 && error?.data?.inUse) {
        setDeleteUsageDetails({
          menuItems: Array.isArray(error?.data?.menuItems) ? error.data.menuItems : [],
          preparations: Array.isArray(error?.data?.preparations) ? error.data.preparations : [],
          complimentaryItems: Array.isArray(error?.data?.complimentaryItems) ? error.data.complimentaryItems : []
        });
      } else {
        setError(`Failed to delete item: ${error.message}`);
        anyModalOpenRef.current = false;
        setDeleteConfirmItem(null);
        // Clear accumulated links on error too
        deletePendingRemovedLinksRef.current = [];
        deletePendingRemovedPrepsRef.current = [];
        deletePendingRemovedCompRef.current = [];
      }
    } finally {
      setDeleteCheckLoading(false);
    }
  }, [fetchInventoryData]);

  // Get batch details for expanded view
  const getBatchDetails = useCallback((item) => {
    return batchDetails[item.id] || [];
  }, [batchDetails]);

  // Get status class for styling
  const getStatusClass = useCallback((status, daysToExpiry) => {
    const days = Number(daysToExpiry);
    if (Number.isFinite(days)) {
      if (days < 0) return 'expired-red'; // Expired items
      if (days === 0) return 'expired-red'; // Expires Today - RED
      if (days <= 7) return 'expired-orange'; // Expires in 1-7 days - ORANGE
      return 'expired-green'; // Fresh items - GREEN
    }
    // daysToExpiry is not a usable number (malformed expiry date): never assume
    // "fresh". Derive urgency from the status label so an actually-expired item
    // can't render green.
    const label = String(status || '').toLowerCase();
    if (label.includes('expired')) return 'expired-red';
    if (label.includes('today') || label.includes('tomorrow')) return 'expired-red';
    if (label.includes('expires in')) return 'expired-orange';
    return 'expired-green';
  }, []);

  // Handle OCR items extraction
  const handleOCRItemsExtracted = useCallback((data) => {
    setOcrData(data);
    setShowStockInForm(true);
    // Keep on 'scan' tab since data came from bill scanning
  }, []);

  // Handle discarding active scanned bill when moving to manual entry
  const handleDiscardScannedBill = useCallback(() => {
    setStockInTab('manual');
    setShowStockInForm(true);
    if (billScannerRef.current) {
      billScannerRef.current.resetScanner();
    }
    setOcrData(null);
    setShowStockInDiscardConfirm(false);
  }, []);

  // Handle successful stock-in submission
  const handleStockInSuccess = useCallback(async () => {
    // Clear batch details cache so new batches will be fetched
    setBatchDetails({});
    setLoadingBatches(new Set());
    
    // Also clear expanded cards to force re-expansion with fresh data
    setExpandedCards(new Set());
    
    // Clear OCR data and reset BillScanner after successful submission
    setOcrData(null);
    setShowStockInForm(false);
    
    // Reset the BillScanner component to clear images and state
    if (billScannerRef.current) {
      billScannerRef.current.resetScanner();
    }
    
    // ✅ CRITICAL: Trigger onboarding completion IMMEDIATELY
    // Do this BEFORE any async operations to guarantee progression to next step
    // This ensures both OCR and manual entry paths complete onboarding reliably
    if (isOnboarding && onOnboardingComplete) {
      onOnboardingComplete();
    }
    
    // Now perform background operations (these can fail without blocking onboarding)
    
    // Refresh inventory data after successful stock-in
    try {
      await fetchInventoryData(false);
    } catch (error) {
      console.error('❌ Inventory refresh failed:', error);
      // Don't block - onboarding already completed above
    }
    
    // Mark stock-in checkpoint as complete in daily operations
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const response = await fetch(`${API_URL}/operations/complete-stockin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-business-id': businessId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          referenceType: 'inventory_batch'
        })
      });
      
      const result = await response.json();
      if (result.success) {
        window.dispatchEvent(new CustomEvent('daily-operations:refresh'));
      }
    } catch (err) {
      console.error('❌ Failed to mark stock-in checkpoint:', err);
      // Don't block - onboarding already completed
    } finally {
      // Ensure dashboard card can refresh even if the follow-up checkpoint call is skipped.
      window.dispatchEvent(new CustomEvent('daily-operations:refresh'));
    }
    
    // Auto-trigger notifications for low stock items after update
    setTimeout(() => {
      const lowStockItems = inventoryItems.filter(item => 
        item.stockLevel === 'low' || item.stockLevel === 'out-of-stock'
      );
      
      
      // Auto-alert for expiring items
      const expiringItems = inventoryItems.filter(item => 
        item.urgencyLevel === 'critical' || item.urgencyLevel === 'urgent'
      );
      
    }, 2000);
  }, [fetchInventoryData, inventoryItems, isOnboarding, onOnboardingComplete, ocrData]);

  // Handle OCR error
  const handleOCRError = useCallback((error) => {
    console.error('OCR error:', error);
    const safeMessage = error && typeof error.message === 'string' ? error.message : (error ? String(error) : 'Unknown error');
    setError(`OCR processing failed: ${safeMessage}`);
    
    // Automatically switch to manual entry mode when OCR fails with a 500 error
    const isServerError = (error && error.status === 500) || (error && typeof error.message === 'string' && error.message.includes('500'));
    if (isServerError) {
      setStockInTab('manual');
      setShowStockInForm(true);
      
      // If the error contains a preview image, pass it to the StockInForm
      // so the user doesn't have to re-upload the image
      if (error.previewImage) {
        setOcrData({
          items: [], // No items extracted
          previewImage: error.previewImage,
          rawText: '',
          filename: 'failed-ocr-image'
        });
      } else {
        setOcrData(null); // Clear any partial OCR data
      }
    }
  }, []);

  const handleManualHeaderAdd = useCallback(() => {
    // Keep manual mode active and signal StockInForm to prefill from selected vendor.
    setStockInTab('manual');
    setShowStockInForm(true);
    setManualHeaderAddTrigger(prev => prev + 1);
  }, []);

  // Closing stock editing is allowed in post-onboarding flow.
  // Prompt indicators (red dots) remain separately dismissible to avoid showing every time.
  const isPostOnboardingFlow = !shouldBlockClosingStockModal;
  const canEditClosingStock = closingStockCorrectionEnabled || isPostOnboardingFlow || (fromOnboarding && isOnboarding);
  // During onboarding, ALWAYS show dots (ignore dismissal). Post-onboarding, respect dismissal.
  const showClosingStockPrompt = (fromOnboarding && isOnboarding)
    ? true
    : (!closingStockCorrectionEnabled && isPostOnboardingFlow && !closingStockEntryDismissed);
  const closingStockCorrectionDraftCount = Object.keys(closingStockCorrectionDrafts || {}).length;

  // ── Merge Handlers ──
  const handleNavyDotClick = async (item) => {
    setMergeItem(item);
    setMergeTarget(null);
    setShowMergeModal(true);
    setMergeLoading(true);
    try {
      const bId = UnitMappingService.getBusinessId();
      const result = await http.get(`/recipes/merge-target/${item.id}?businessId=${bId}`);
      if (result.success && result.has_target) {
        setMergeTarget(result.target);
      }
    } catch (err) {
      console.error('Failed to fetch merge target:', err);
    } finally {
      setMergeLoading(false);
    }
  };

  const handleMergeConfirm = async () => {
    if (!mergeItem || !mergeTarget) return;
    setMergeLoading(true);
    try {
      const bId = UnitMappingService.getBusinessId();
      const result = await http.post('/recipes/merge-items', {
        duplicate_item_id: mergeItem.id,
        master_item_id: mergeTarget.target_item_id,
        businessId: bId
      });
      if (result.success) {
        setShowMergeModal(false);
        setMergeItem(null);
        setMergeTarget(null);
        // Refresh inventory list
        fetchInventoryData(false);
      }
    } catch (err) {
      console.error('Merge failed:', err);
    } finally {
      setMergeLoading(false);
    }
  };

  const inventoryEditTitle = inventoryEditMode === 'rename'
    ? 'Change item name'
    : inventoryEditMode === 'merge'
      ? 'Merge items'
      : inventoryEditMode === 'unit'
        ? 'Change item unit'
        : 'Edit item';

  const inventoryEditDescription = inventoryEditMode === 'rename'
    ? (isMobile ? 'Update the item name across the inventory.' : 'Update the item name across inventory and recipes.')
    : inventoryEditMode === 'merge'
      ? 'Move all stock and recipes into another item — or pick a saved preparation to make this item from.'
      : inventoryEditMode === 'unit'
        ? 'Change the unit used for this item.'
        : '';

  const inventoryEditPrimaryLabel = inventoryEditLoading
    ? (inventoryEditMode === 'merge' ? (mergeTargetIsPreparation ? 'Applying...' : 'Merging...') : 'Saving...')
    : inventoryEditMode === 'rename'
      ? 'Rename item'
      : inventoryEditMode === 'merge'
        ? (mergeTargetIsPreparation ? 'Use this preparation' : 'Merge items')
        : inventoryEditMode === 'unit'
          ? 'Change unit'
          : 'Save';

  if (blockedBySnc) {
    return (
      <div className="container-of-Overview">
        <BlockedBySalesNameCheck onOpen={onOpenSalesNameCheck} />
      </div>
    );
  }

  return (
    <div className="container-of-Overview">
      <StandardScrollStyles />

      {/* ── Merge Modal ── */}
      {showMergeModal && mergeItem && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px'
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '28px',
            maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '40px', marginBottom: '8px' }}>🔀</div>
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#111' }}>
                Merge Duplicate Item
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
                This item was force-created as a potential duplicate during onboarding.
              </p>
            </div>

            <div style={{
              background: '#f0f4ff', borderRadius: '10px', padding: '14px',
              marginBottom: '16px', border: '1px solid #d0d8f0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px' }}>
                <span style={{ color: '#6b7280' }}>Duplicate:</span>
                <span style={{ fontWeight: 600, color: '#dc2626' }}>{mergeItem.name}</span>
              </div>
              {mergeLoading ? (
                <div style={{ textAlign: 'center', padding: '8px', color: '#6b7280', fontSize: '13px' }}>
                  Finding original item...
                </div>
              ) : mergeTarget ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                    <span style={{ color: '#6b7280' }}>Merge into:</span>
                    <span style={{ fontWeight: 600, color: '#059669' }}>{mergeTarget.target_item_name}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px', lineHeight: 1.4 }}>
                    All recipes, stock batches, and purchase history will transfer to "{mergeTarget.target_item_name}".
                    "{mergeItem.name}" will be permanently deleted.
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '8px', color: '#d97706', fontSize: '13px' }}>
                  ⚠️ Original item not found in override log. Cannot auto-merge.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              {mergeTarget && (
                <button
                  onClick={handleMergeConfirm}
                  disabled={mergeLoading}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '10px',
                    background: mergeLoading ? '#9ca3af' : '#1e3a5f', color: '#fff', border: 'none',
                    fontSize: '14px', fontWeight: 700, cursor: mergeLoading ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s'
                  }}
                >
                  {mergeLoading ? 'Merging...' : '🔀 Merge Now'}
                </button>
              )}
              <button
                onClick={() => { setShowMergeModal(false); setMergeItem(null); setMergeTarget(null); }}
                style={{
                  flex: mergeTarget ? 1 : 'auto', padding: '12px', borderRadius: '10px',
                  background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  width: mergeTarget ? 'auto' : '100%'
                }}
              >
                Keep Separate
              </button>
            </div>
          </div>
        </div>
      )}
      {showTruckLoader && (
        <div className={`truck-loader-overlay ${truckLeaving ? 'truck-loading-leave' : ''}`}>
          <TruckLoader message={isLoading ? 'Truck is delivering your inventory…' : 'Unloading complete'} />
        </div>
      )}
      <header className={`app-header inventory-header ${headerScrolled ? 'scrolled' : ''}`}>
        {fromOnboarding && isOnboarding && activeTab === 'overview' && (
          <button
            type="button"
            className="onboarding-overview-back-btn"
            // Onboarding is a linear wizard, so "back" is the previous STEP — Ingredient
            // Reconciliation — never previousScreen. Every onboarding hop into this screen
            // is an untracked setScreen, so previousScreen is whatever tracked navigation
            // happened last: after a trip to Closing Stock and back it is 'closing-stock',
            // and the arrow bounced between the two and never reached reconciliation.
            onClick={() => goTo('ingredient-reconciliation', { backTo: 'itemmap' })}
            aria-label="Back to Ingredient Reconciliation"
            title="Back to Ingredient Reconciliation"
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '18px' }} />
          </button>
        )}
        <h1 className="inventory-title" style={fromOnboarding && isOnboarding && activeTab === 'overview' ? { paddingLeft: '0.25rem' } : {}}>
          Inventory
        </h1>
        {fromOnboarding && isOnboarding && (
          <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
            <button
              className="closing-stock-btn"
              onClick={() => goTo('closing-stock')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                background: '#000000',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#1f2937'}
              onMouseOut={(e) => e.currentTarget.style.background = '#000000'}
            >
              <span>Closing Stock</span>
            </button>
          </div>
        )}
      </header>
      {/* Main Tabs */}
      <div className="top-tabs invtabv2-top-tabs" ref={topTabsRef}>
        <div
          className={`tab ${activeTab === 'overview' ? 'active' : ''} ${(!fromOnboarding && tsrLockActive) || (activeTab === 'stock-out' && stockOutLock.isActive) ? 'disabled' : ''}`}
          onClick={() => {
            if ((!fromOnboarding && !tsrLockActive) || (fromOnboarding && isOnboarding)) {
              if (!(activeTab === 'stock-out' && stockOutLock.isActive)) {
                setActiveTab('overview');
              }
            }
          }}
          style={((!fromOnboarding && tsrLockActive) || (activeTab === 'stock-out' && stockOutLock.isActive)) ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
        >
          {t('inventory.overview')}
        </div>
        <div
          className={`tab ${activeTab === 'stock-in' ? 'active' : ''} ${tsrLockActive || (activeTab === 'stock-out' && stockOutLock.isActive) || (fromOnboarding && isOnboarding) ? 'disabled' : ''}`}
          onClick={() => {
            if (!tsrLockActive && !(activeTab === 'stock-out' && stockOutLock.isActive) && !(fromOnboarding && isOnboarding)) {
              dismissClosingStockEntry();
              setActiveTab('stock-in');
            }
          }}
          style={(tsrLockActive || (activeTab === 'stock-out' && stockOutLock.isActive) || (fromOnboarding && isOnboarding)) ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
        >
          {t('inventory.stock_in')}
        </div>
        <div
          className={`tab ${activeTab === 'stock-out' ? 'active' : ''} ${fromOnboarding && isOnboarding ? 'disabled' : ''}`}
          onClick={() => !(fromOnboarding && isOnboarding) && setActiveTab('stock-out')}
          style={fromOnboarding && isOnboarding ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
        >
          {t('inventory.stock_out')}
        </div>
        <span className="invtabv2-indicator invtabv2-top-indicator" style={topTabsIndicatorStyle} aria-hidden="true" />
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <motion.div
          className="overview-container"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="inventory-overview">
            {/* Search and Filter Header */}
            <motion.div
              className="overview-header"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2, delay: 0.04, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="search-filter-row">
                <div className="overview-search-wrapper">
                  <StandardSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    onClear={clearSearch}
                    placeholder={t('inventory.search_placeholder')}
                    inputRef={searchInputRef}
                    style={{ marginBottom: 0 }}
                    rightContent={
                    <div className="filter-container" ref={filterDropdownRef}>
                      <button
                        className="filter-btn-inline"
                        onClick={toggleFilterDropdown}
                        title="Filter items"
                      >
                        <FontAwesomeIcon icon={faFilter} className="filter-icon" />
                      </button>

                      {/* Filter Dropdown Menu */}
                      <AnimatePresence>
                      {showFilterDropdown && (
                        <motion.div
                          className="filter-dropdown-menu"
                          initial={prefersReducedMotion ? false : { opacity: 0, y: -8, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
                          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.16, ease: [0.25, 0.46, 0.45, 0.94] }}
                        >
                          <div
                            className={`filter-option ${currentFilter === 'expiry-nearest' ? 'active' : ''}`}
                            onClick={() => handleFilterSelect('expiry-nearest')}
                          >
                            {t('inventory.filter_expiry_nearest')}
                          </div>
                          <div
                            className={`filter-option ${currentFilter === 'newly-added' ? 'active' : ''}`}
                            onClick={() => handleFilterSelect('newly-added')}
                          >
                            {t('inventory.filter_newly_added')}
                          </div>
                          <div
                            className={`filter-option ${currentFilter === 'cost-high-low' ? 'active' : ''}`}
                            onClick={() => handleFilterSelect('cost-high-low')}
                          >
                            {t('inventory.filter_cost_high_low')}
                          </div>
                          <div
                            className={`filter-option ${currentFilter === 'cost-low-high' ? 'active' : ''}`}
                            onClick={() => handleFilterSelect('cost-low-high')}
                          >
                            {t('inventory.filter_cost_low_high')}
                          </div>

                          <div
                            className={`filter-option ${currentFilter === 'quantity-high-low' ? 'active' : ''}`}
                            onClick={() => handleFilterSelect('quantity-high-low')}
                          >
                            {t('inventory.filter_quantity_high_low')}
                          </div>

                          <div
                            className={`filter-option ${currentFilter === 'quantity-low-high' ? 'active' : ''}`}
                            onClick={() => handleFilterSelect('quantity-low-high')}
                          >
                            {t('inventory.filter_quantity_low_high')}
                          </div>

                          <div
                            className={`filter-option ${currentFilter === 'category' ? 'active' : ''}`}
                            onClick={() => handleFilterSelect('category')}
                          >
                            {t('inventory.filter_category')}
                          </div>

                          <div
                            className={`filter-option ${currentFilter === 'saved-prep' ? 'active' : ''}`}
                            onClick={() => handleFilterSelect('saved-prep')}
                          >
                            {t('inventory.filter_saved_prep')}
                          </div>

                          <div
                            className={`filter-option ${currentFilter === 'not-associated' ? 'active' : ''}`}
                            onClick={() => handleFilterSelect('not-associated')}
                          >
                            {t('inventory.filter_not_associated')}
                          </div>

                          <div
                            className={`filter-option ${currentFilter === 'non-consumable' ? 'active' : ''}`}
                            onClick={() => handleFilterSelect('non-consumable')}
                          >
                            {t('inventory.filter_non_consumable')}
                          </div>
                        </motion.div>
                      )}
                      </AnimatePresence>
                    </div>
                    }
                  />
                </div>
                <div className="filter-language-buttons">
                  <div className="overview-edit-actions" ref={editActionsRef}>
                    <button
                      type="button"
                      className="overview-edit-btn"
                      onClick={toggleEditActionsMenu}
                      title="Edit inventory & language options"
                      aria-label="Edit inventory & language options"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <AnimatePresence>
                      {showEditActionsMenu && (
                        <motion.div
                          className="overview-edit-menu"
                          initial={prefersReducedMotion ? false : { opacity: 0, y: -8, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
                          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.16, ease: [0.25, 0.46, 0.45, 0.94] }}
                        >
                          <button
                            type="button"
                            className="overview-edit-option"
                            onClick={() => openInventoryEditModal('rename')}
                          >
                            Change item name
                          </button>
                          <button
                            type="button"
                            className="overview-edit-option"
                            onClick={() => openInventoryEditModal('merge')}
                          >
                            Merge with other item
                          </button>
                          <button
                            type="button"
                            className="overview-edit-option"
                            onClick={() => openInventoryEditModal('unit')}
                          >
                            Change units
                          </button>
                          {fromOnboarding && isOnboarding && (
                            <button
                              type="button"
                              className="overview-edit-option"
                              onClick={() => {
                                setShowEditActionsMenu(false);
                                goTo('closing-stock', { backTo: 'inventory' });
                              }}
                            >
                              Closing stock
                            </button>
                          )}
                          <div className="overview-edit-divider" />
                          <div className="overview-edit-section-header">
                            Language / भाषा
                          </div>
                          <button
                            type="button"
                            className={`overview-edit-option ${selectedLanguage === 'en' ? 'active' : ''}`}
                            onClick={() => {
                              handleLanguageSelect('en');
                              setShowEditActionsMenu(false);
                            }}
                          >
                            English {selectedLanguage === 'en' && '✓'}
                          </button>
                          <button
                            type="button"
                            className={`overview-edit-option ${selectedLanguage === 'te' ? 'active' : ''}`}
                            onClick={() => {
                              handleLanguageSelect('te');
                              setShowEditActionsMenu(false);
                            }}
                          >
                            తెలుగు {selectedLanguage === 'te' && '✓'}
                          </button>
                          <button
                            type="button"
                            className={`overview-edit-option ${selectedLanguage === 'hi' ? 'active' : ''}`}
                            onClick={() => {
                              handleLanguageSelect('hi');
                              setShowEditActionsMenu(false);
                            }}
                          >
                            हिन्दी {selectedLanguage === 'hi' && '✓'}
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </motion.div>

            {activeTab === 'overview' && fromOnboarding && isOnboarding && !onboardingLocked && (
              <div className="onboarding-add-stock-row">
                <button
                  type="button"
                  className="onboarding-add-stock-btn"
                  onClick={openAddStockWizard}
                >
                  Add Stock
                </button>
              </div>
            )}

            {/* Inventory Items Grid */}
            <div className="inventory-grid">
              {error && inventoryItems.length === 0 ? (
                <div className="error-state">
                  <p>❌ {t('inventory.failed_to_load')}</p>
                  <p>{error}</p>
                  <button onClick={handleRefresh} className="retry-btn">
                    {t('common.retry')}</button>
                  </div>
              ) : filteredItems.length === 0 ? (
                <div className="no-items">
                  {debouncedSearchTerm ? (
                    <p>{t('inventory.no_items_found')} "{debouncedSearchTerm}"</p>
                  ) : currentFilter === 'non-consumable' ? (
                    <p>
                      {nonConsumableOpen.loading
                        ? 'Loading non-consumable items…'
                        : nonConsumableOpen.error || 'No non-consumable items yet.'}
                    </p>
                  ) : (
                    <div>
                      <p>{t('inventory.no_inventory_items')}</p>
                      <p>{t('inventory.add_items_hint')} <strong>{t('inventory.stock_in')}</strong> {t('inventory.tab_to_see')}.</p>
                      <button
                        onClick={() => setActiveTab('stock-in')}
                        className="goto-manual-btn"
                      >
                        {t('inventory.go_to_stock_in')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <AnimatePresence initial={false} mode="sync">
                {filteredItems.map((item, index) => {
                  const isExpanded = expandedCards.has(item.id);
                  const isNegativeStock = Number(item.quantity) < 0;
                  const batchDetailsData = getBatchDetails(item);
                  const isSearchFiltering = Boolean(searchTerm && searchTerm.trim().length > 0);
                  const correctionDraft = closingStockCorrectionDrafts[item.id] || closingStockCorrectionDrafts[String(item.id)];
                  const correctionUnit = String(correctionDraft?.unit || item.rawUnit || item.unit || 'g').trim() || 'g';
                  const stagedQuantity = parseFiniteNumber(correctionDraft?.quantity);
                  const normalizedStagedQuantity = stagedQuantity !== null ? Math.abs(stagedQuantity) : null;
                  const stagedUnitPrice = parseFiniteNumber(correctionDraft?.unitPrice);
                  const stagedTotalPrice = parseFiniteNumber(correctionDraft?.totalPrice);
                  const stagedBatchDetailsData = closingStockCorrectionEnabled && normalizedStagedQuantity !== null
                    ? [{
                      id: `staged-${item.id}`,
                      batchNumber: 'BATCH-01',
                      quantityDisplay: formatQuantityDisplay(normalizedStagedQuantity, correctionUnit),
                      quantity: normalizedStagedQuantity,
                      unit: correctionUnit,
                      procuredDate: formatClosingStockDateDisplay(closingStockCorrectionDate),
                      expiryDate: null,
                      supplier: 'Closing Stock',
                      status: 'Good',
                      useFirst: false,
                      daysToExpiry: 999,
                      isOpeningStock: true,
                      isDraft: true,
                      isStagedCorrection: true,
                      unitCost: stagedUnitPrice !== null ? stagedUnitPrice : 0,
                      totalCost: stagedTotalPrice !== null
                        ? stagedTotalPrice
                        : (stagedUnitPrice !== null ? normalizedStagedQuantity * stagedUnitPrice : 0)
                    }]
                    : batchDetailsData;
                  // Saved preparations (amber name-dot) are made to order, not counted as stock —
                  // the card reads 0 so nobody is asked to reconcile a quantity they never hold.
                  // Display only: item.quantity, batch details and valuation are untouched.
                  const isSavedPreparationItem = savedPreparationNameSet.has(normalizeInventoryName(item?.name || ''));
                  // Non Consumable filter: what is left of the stock received since the last
                  // closing stock — 0 once that stock has been closed. Display only, as above.
                  const quantityLabel = isSavedPreparationItem
                    ? formatQuantityDisplay(0, item.unit || item.rawUnit || 'g')
                    : item.nonConsumableQuantity !== undefined
                      ? formatQuantityDisplay(item.nonConsumableQuantity, item.unit || item.rawUnit || 'g')
                    : closingStockCorrectionEnabled
                      ? (normalizedStagedQuantity !== null
                        ? formatQuantityDisplay(normalizedStagedQuantity, correctionUnit)
                        : item.quantityDisplay)
                      : item.quantityDisplay;
                  const masterUnitPrice = getMasterUnitPrice(item);

                  return (
                    <motion.div
                      key={item.id || item.name}
                      className={`inventory-item-card ${isExpanded ? 'expanded' : ''}`}
                      onDoubleClick={(e) => {
                        if (
                          e.target.closest('.expand-icon') || 
                          e.target.closest('.fix-negative-btn') || 
                          e.target.closest('.onboarding-indicator') || 
                          e.target.closest('.item-quantity') || 
                          e.target.closest('.delete-item-icon')
                        ) {
                          return;
                        }
                        anyModalOpenRef.current = true;
                        const requestSeq = viewRelatedRequestSeqRef.current + 1;
                        viewRelatedRequestSeqRef.current = requestSeq;
                        setViewRelatedConfirmItem(item);
                        setViewRelatedUsageDetails(null);
                        setViewRelatedCheckLoading(true);
                        loadRelatedUsageDetails(item.id, requestSeq);
                      }}
                      onClickCapture={isMobile ? (event) => {
                        if (event.target && typeof event.target.closest === 'function' && event.target.closest('.fix-negative-btn')) {
                          return;
                        }
                        const wasExpanded = expandedCards.has(item.id);
                        toggleCardExpansion(item.id);
                        setItemExpandState(prev => ({
                          ...prev,
                          [item.id]: !wasExpanded
                        }));
                        if (!wasExpanded && selectedLanguage !== 'en') {
                          translateItemName(item.id, item.name);
                        }
                      } : undefined}
                      layout={prefersReducedMotion || isMobile ? false : (!isSearchFiltering ? true : false)}
                      initial={prefersReducedMotion || isMobile ? false : { opacity: 0, y: isSearchFiltering ? 8 : 12 }}
                      animate={prefersReducedMotion ? { opacity: 1, y: 0 } : {
                        opacity: 1,
                        y: 0
                      }}
                      exit={prefersReducedMotion || isMobile ? { opacity: 0 } : {
                        opacity: 0,
                        y: -8,
                        transition: {
                          duration: isSearchFiltering ? 0.14 : 0.18,
                          ease: [0.4, 0, 1, 1]
                        }
                      }}
                      transition={prefersReducedMotion ? { duration: 0 } : {
                        duration: isMobile ? 0.08 : (isSearchFiltering ? 0.16 : 0.25),
                        delay: isMobile || isSearchFiltering ? 0 : Math.min(index, 10) * 0.03,
                        ease: [0.25, 0.46, 0.45, 0.94],
                        layout: {
                          type: 'spring',
                          stiffness: 460,
                          damping: 38,
                          mass: 0.8
                        }
                      }}
                    >
                      <div
                        className="card-header"
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="title-quantity-row">
                          <div className="item-title-container" style={{marginTop:'0.3rem', position: 'relative'}}>
                            {isExpanded && selectedLanguage !== 'en' && translatedNames[item.id] ? (
                              <h3 className="item-title translated-text">
                                {translatedNames[item.id]}
                                {savedPreparationNameSet.has(normalizeInventoryName(item?.name || '')) && (
                                  <span
                                    title="Saved preparation"
                                    aria-label="Saved preparation"
                                    style={{
                                      display: 'inline-block',
                                      width: '9px',
                                      height: '9px',
                                      borderRadius: '50%',
                                      backgroundColor: '#FBBF24',
                                      marginLeft: '6px',
                                      verticalAlign: 'middle'
                                    }}
                                  />
                                )}
                              </h3>
                            ) : isExpanded && selectedLanguage !== 'en' && translatingItems.has(item.id) ? (
                              <h3 className="item-title translating">
                                {item.name} <span className="dot-pulse">...</span>
                                {savedPreparationNameSet.has(normalizeInventoryName(item?.name || '')) && (
                                  <span
                                    title="Saved preparation"
                                    aria-label="Saved preparation"
                                    style={{
                                      display: 'inline-block',
                                      width: '9px',
                                      height: '9px',
                                      borderRadius: '50%',
                                      backgroundColor: '#FBBF24',
                                      marginLeft: '6px',
                                      verticalAlign: 'middle'
                                    }}
                                  />
                                )}
                              </h3>
                            ) : (
                              <h3 className="item-title">
                                {highlightMatch(item.name, debouncedSearchTerm)}
                                {savedPreparationNameSet.has(normalizeInventoryName(item?.name || '')) && (
                                  <span
                                    title="Saved preparation"
                                    aria-label="Saved preparation"
                                    style={{
                                      display: 'inline-block',
                                      width: '9px',
                                      height: '9px',
                                      borderRadius: '50%',
                                      backgroundColor: '#FBBF24',
                                      marginLeft: '6px',
                                      verticalAlign: 'middle'
                                    }}
                                  />
                                )}
                              </h3>
                            )}
                            {/* Non Consumable filter: the item's Not in Recipe expense head, as a small tag. */}
                            {item.nonConsumableExpenseHead && EXPENSE_HEAD_LABELS[item.nonConsumableExpenseHead] && (
                              <span
                                title="Expense head (Not in Recipe)"
                                style={{
                                  display: 'inline-block',
                                  marginLeft: '6px',
                                  padding: '1px 8px',
                                  borderRadius: '999px',
                                  backgroundColor: '#ede9fe',
                                  color: '#5b21b6',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  verticalAlign: 'middle'
                                }}
                              >
                                {EXPENSE_HEAD_LABELS[item.nonConsumableExpenseHead]}
                              </span>
                            )}
                            {/* Onboarding indicators (only during onboarding) */}
                            {showClosingStockPrompt && item.is_duplicate_override && !savedPreparationNameSet.has(normalizeInventoryName(item?.name || '')) ? (
                              <span 
                                className="onboarding-indicator navy-dot"
                                title="Forced duplicate — tap to merge"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNavyDotClick(item);
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                ●
                              </span>
                            ) : showClosingStockPrompt && Number(item.quantity) <= 0 && !savedPreparationNameSet.has(normalizeInventoryName(item?.name || '')) ? (
                              <span 
                                className="onboarding-indicator red-dot"
                                title="Stock not entered"
                              >
                                ●
                              </span>
                            ) : showClosingStockPrompt && Number(item.quantity) > 0 && !savedPreparationNameSet.has(normalizeInventoryName(item?.name || '')) ? (
                              <span 
                                className="onboarding-indicator green-dot"
                                title="Stock entered"
                              >
                                ●
                              </span>
                            ) : null}
                          </div>
                          <div className="quantity-expand-row">
                            <div className="item-quantity item-quantity-flex">
                                {/* Closing stock: quantity click opens modal in allowed modes */}
                                {canEditClosingStock ? (
                                  <span 
                                    style={{ cursor: 'pointer', fontWeight: '600', color: '#000000' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (closingStockCorrectionEnabled) {
                                        openClosingStockCorrectionModal(item);
                                      } else {
                                        openOnboardingStockModal(item);
                                      }
                                    }}
                                    title={closingStockCorrectionEnabled ? 'Click to correct closing stock' : 'Click to enter closing stock'}
                                  >
                                    {quantityLabel}
                                  </span>
                                ) : (
                                  <span>{quantityLabel}</span>
                                )}
                                {closingStockCorrectionEnabled && correctionDraft && (
                                  <span className="closing-stock-correction-chip">staged</span>
                                )}
                                <motion.span
                                  className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
                                  style={{ marginLeft: '4px', display: 'inline-flex', cursor: 'pointer' }}
                                  animate={{ rotate: isExpanded ? 180 : 0 }}
                                  transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Only handle chevron clicks on non-mobile (desktop) to avoid double-toggling
                                    if (isMobile) return;
                                    const wasExpanded = expandedCards.has(item.id);
                                    toggleCardExpansion(item.id);
                                    setItemExpandState(prev => ({
                                      ...prev,
                                      [item.id]: !wasExpanded
                                    }));
                                    if (!wasExpanded && selectedLanguage !== 'en') {
                                      translateItemName(item.id, item.name);
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon icon={faAngleDown} />
                                </motion.span>
                              </div>
                              {isNegativeStock && !isExpanded && (
                                <button
                                  className="fix-negative-btn"
                                  title="Fix negative stock with closing stock entry"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openNegativeFixModal(item);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    margin: 0,
                                    zIndex: 2
                                  }}
                                >{t('inventory.fix')}</button>
                              )}
                            {/*
                              {fromOnboarding && isOnboarding && !onboardingLocked && Number(item.unitCost) > 0 && (
                                <div
                                  className="item-unit-price"
                                  style={{
                                    fontSize: '0.78rem',
                                    lineHeight: 1.2,
                                    color: '#64748b',
                                    textAlign: 'right',
                                    marginTop: '2px',
                                    paddingRight: '20px'
                                  }}
                                >
                                  ₹{Number(item.unitCost).toFixed(2)}/{item.unit || 'unit'}
                                </div>
                              )}
                            */}
                          </div>
                        </div>
                      </div>

                      <div className="item-details-container">
                        <div className="left-section">
                          <div className="item-category-text">{abbreviateCategory(item.category)}</div>
                        </div>
                        <div className="right-section">
                          <div className="item-status-row">
                            {/* Latest purchase price per master unit replaces the expiry
                                badge — except when expiry is actually actionable (already
                                expired or within a week), which still outranks the price. */}
                            {masterUnitPrice && item.daysToExpiry > 7 ? (
                              <span
                                className="status-text item-master-unit-price"
                                title={item.latestUnitCostDate
                                  ? `Latest purchase price (${new Date(item.latestUnitCostDate).toLocaleDateString('en-IN')})`
                                  : 'Latest purchase price'}
                              >
                                {masterUnitPrice.label}
                              </span>
                            ) : (
                              <span className={`status-text ${getStatusClass(item.status, item.daysToExpiry)}`}>
                                {item.daysToExpiry < 0 ? `• ${t('inventory.expired')}` :
                                  item.daysToExpiry === 0 ? `• ${t('inventory.expires_today')}` :
                                  item.daysToExpiry === 1 ? `• Expires soon` :
                                  item.daysToExpiry <= 7 ? `• Expires in ${item.daysToExpiry} days` :
                                  item.daysToExpiry <= 30 ? `• ${t('inventory.fresh')}` :
                                  item.daysToExpiry === 999 ? `• ${t('inventory.no_expiry')}` :
                                  `• ${t('inventory.good')}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content - FIFO Batch Details */}
                      <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          className="expanded-content"
                          initial={prefersReducedMotion ? false : (isMobile ? { opacity: 0 } : { opacity: 0, clipPath: 'inset(0 0 100% 0)' })}
                          animate={prefersReducedMotion ? { opacity: 1 } : (isMobile ? { opacity: 1 } : { opacity: 1, clipPath: 'inset(0 0 0% 0)' })}
                          exit={prefersReducedMotion ? { opacity: 0 } : (isMobile ? { opacity: 0 } : { opacity: 0, clipPath: 'inset(0 0 100% 0)' })}
                          transition={prefersReducedMotion ? { duration: 0 } : { duration: isMobile ? 0.08 : 0.22, ease: [0.22, 1, 0.36, 1] }}
                          style={{ willChange: isMobile ? 'opacity' : 'opacity, clip-path' }}
                        >
                          <div className="fifo-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4>FIFO BATCHES</h4>
                            {!fromOnboarding && (
                              <span
                                className="delete-item-icon"
                                title="Delete ingredient"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  anyModalOpenRef.current = true;
                                  setDeleteConfirmItem(item);
                                  setDeleteUsageDetails(null);
                                  setDeleteCheckLoading(true);
                                  loadDeleteUsageDetails(item.id);
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </span>
                            )}
                          </div>

                          {batchErrors[item.id] && (
                            <div className="batch-error-box">
                              <p className="batch-error-text">{t('inventory.failed_batch_details')}: {batchErrors[item.id]}</p>
                              <button
                                className="retry-batch-btn"
                                onClick={(e) => { e.stopPropagation(); fetchBatchDetails(item.id, true); }}
                                disabled={loadingBatches.has(item.id)}
                              >{loadingBatches.has(item.id) ? t('common.retrying') : t('common.retry')}</button>
                            </div>
                          )}
                          {!batchErrors[item.id] && stagedBatchDetailsData.length === 0 && (
                            <>
                              {/* Show closing stock during onboarding if draft exists */}
                              {fromOnboarding && isOnboarding && !onboardingLocked && onboardingDrafts[item.id] ? (
                                <div className="batch-item">
                                  <div className="batch-header">
                                    <div className="batch-info">
                                      <span className="batch-number">CLOSING-STOCK</span>
                                    </div>
                                  </div>

                                  <div className="batch-quantity">
                                    {t('inventory.quantity')}: {onboardingDrafts[item.id].quantity} {onboardingDrafts[item.id].unit}
                                  </div>

                                  <div className="batch-details">
                                    <div className="batch-detail-row closing-stock-date-row">
                                      <span className="detail-label">Closing:</span>
                                      <span className="detail-value">{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                                    </div>
                                    <div className="batch-detail-row supplier-row">
                                      <span className="detail-label">Price:</span>
                                      <span className="detail-value">
                                        {onboardingDrafts[item.id].total_price 
                                          ? `₹${parseFloat(onboardingDrafts[item.id].total_price).toFixed(2)}`
                                          : 'Not specified'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="no-batches-msg">
                                  No batches
                                  {isPostOnboardingFlow && item.quantity <= 0 && (
                                    <button
                                      className="fix-stock-btn"
                                      style={{
                                        marginTop: '10px',
                                        padding: '8px 16px',
                                        backgroundColor: 'black',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '0.85rem'
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveTab('stock-in');
                                      }}
                                    >
                                      Add Stock
                                    </button>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          {stagedBatchDetailsData.map((batch, index) => (
                            <div key={batch.id} className="batch-item">
                              <div className="batch-header">
                                <div className="batch-info">
                                  {/* Show CLOSING-STOCK badge for closing stock batches, otherwise show USE FIRST */}
                                  {batch.isOpeningStock ? (
                                    <span className="batch-number">CLOSING-STOCK</span>
                                  ) : (
                                    <>
                                      {batch.useFirst && (
                                        <span className="use-first-badge">{t('inventory.use_first')}</span>
                                      )}
                                      <span className="batch-number">{batch.batchNumber}</span>
                                    </>
                                  )}
                                </div>
                                {/* Delete button removed by request */}
                              </div>

                              <div className="batch-quantity">
                                {t('inventory.quantity')}: {batch.quantityDisplay}
                                {/* Show conversion info if available */}
                                {batch.conversionInfo && (
                                  <div style={{ 
                                    fontSize: '0.85em', 
                                    color: '#6c757d', 
                                    marginTop: '4px',
                                    fontStyle: 'italic'
                                  }}>
                                    📐 Conversion: {batch.conversionInfo}
                                  </div>
                                )}
                              </div>

                              <div className="batch-details">
                                {/* Closing stock batches show Closing Stock Date and Price */}
                                {batch.isOpeningStock ? (
                                  <>
                                    <div className="batch-detail-row closing-stock-date-row">
                                      <span className="detail-label">Closing:</span>
                                      <span className="detail-value">{batch.procuredDate}</span>
                                    </div>
                                    {/* This is unitCost x quantity — the WORTH of what is left in
                                        the batch, not a price. Labelling it "Price" beside
                                        "Quantity: 28g" reads as "28 g cost Rs 588", which is how a
                                        correct Rs 21,000/kg ingredient looks like a costing error.
                                        The rate is what the reader is actually checking, so it is
                                        shown too, in the same master unit as the card header. */}
                                    <div className="batch-detail-row supplier-row">
                                      <span className="detail-label">Value:</span>
                                      <span className="detail-value">
                                        {batch.totalCost 
                                          ? `₹${parseFloat(batch.totalCost).toFixed(2)}`
                                          : (batch.unitCost && batch.quantity 
                                              ? `₹${(parseFloat(batch.unitCost) * parseFloat(batch.quantity)).toFixed(2)}`
                                              : 'Not specified')}
                                      </span>
                                    </div>
                                    {(() => {
                                      const rate = getMasterUnitPrice({
                                        latestUnitCost: batch.unitCost,
                                        stockUnitSymbol: item.stockUnitSymbol || item.rawUnit || item.unit
                                      });
                                      if (!rate) return null;
                                      return (
                                        <div className="batch-detail-row supplier-row">
                                          <span className="detail-label">Rate:</span>
                                          <span className="detail-value">{rate.label}</span>
                                        </div>
                                      );
                                    })()}
                                  </>
                                ) : (
                                  /* Regular batches show Procured date, Supplier, and Price */
                                  <>
                                    <div className="batch-detail-row">
                                      <span className="detail-label">{isMobile ? 'Date:' : 'Procured:'}</span>
                                      <span className="detail-value">{batch.procuredDate}</span>
                                    </div>
                                    {batch.expiryDate && (
                                      <div className="batch-detail-row">
                                        <span className="detail-label">Expires:</span>
                                        <span className="detail-value">{batch.expiryDate}</span>
                                      </div>
                                    )}
                                    <div className="batch-detail-row supplier-row">
                                      <span className="detail-label">{t('inventory.supplier')}:</span>
                                      <span className="detail-value supplier-value">{batch.supplier}</span>
                                    </div>
                                    <div className="batch-detail-row">
                                      <span className="detail-label">Price:</span>
                                      <span className="detail-value">
                                        {batch.totalCost 
                                          ? `₹${parseFloat(batch.totalCost).toFixed(2)}`
                                          : (batch.unitCost && batch.quantity 
                                              ? `₹${(parseFloat(batch.unitCost) * parseFloat(batch.quantity)).toFixed(2)}`
                                              : 'Not specified')}
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* Only show status badge for non-closing-stock batches */}
                              {!batch.isOpeningStock && batch.status && !batch.status.toLowerCase().includes('no expiry') && (
                                <div className="batch-status">
                                  <span className={`batch-status-text ${batch.status.toLowerCase()}`}>
                                    {batch.status}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </motion.div>
                      )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
                </AnimatePresence>
              )}
              {fromOnboarding && isOnboarding && <div style={{ height: '80px' }} />}
            </div>
            {!error && (
              <div className="overview-ingredient-count" aria-live="polite">
                Showing {filteredItems.length} ingredient{filteredItems.length === 1 ? '' : 's'}
                {filteredItems.length !== inventoryItems.length
                  ? ` of ${inventoryItems.length}`
                  : ''}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {activeTab === 'overview' && closingStockCorrectionEnabled && closingStockCorrectionDraftCount > 0 && (
        <div className="closing-stock-correction-action-bar">
          <div className="closing-stock-correction-action-text">
            {closingStockCorrectionDraftCount} ingredient{closingStockCorrectionDraftCount === 1 ? '' : 's'} staged for closing stock correction
          </div>
          <div className="closing-stock-correction-action-buttons">
            <button
              className="closing-stock-correction-btn closing-stock-correction-btn-cancel"
              onClick={cancelClosingStockCorrections}
              disabled={isApplyingClosingStockCorrections}
            >
              Cancel
            </button>
            <button
              className="closing-stock-correction-btn closing-stock-correction-btn-confirm"
              onClick={openClosingStockApplyConfirmModal}
              disabled={isApplyingClosingStockCorrections}
            >
              {isApplyingClosingStockCorrections ? 'Applying...' : 'Confirm'}
            </button>
          </div>
        </div>
      )}

      {selectedClosingStockCorrectionItem && ReactDOM.createPortal(
        <ClosingStockCorrectionModal
          item={selectedClosingStockCorrectionItem}
          onClose={closeClosingStockCorrectionModal}
          onSave={saveClosingStockCorrectionDraft}
        />,
        document.body
      )}

      {showClosingStockApplyConfirmModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={closeClosingStockApplyConfirmModal}>
          <div className="modal-box closing-stock-apply-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Apply Closing Stock Corrections?</h3>
            <p>
              Warning: all your previous quantities and prices will be replaced with the new given quantities and prices.
            </p>
            <div style={{ margin: '12px 0 10px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.86rem', fontWeight: 600 }}>
                Closing
              </label>
              <input
                type="date"
                className="stock-input"
                value={closingStockCorrectionDate}
                max={getISTDateString()}
                onChange={(e) => setClosingStockCorrectionDate(e.target.value)}
                disabled={isApplyingClosingStockCorrections}
                style={{ width: '100%' }}
              />
            </div>
            <p>Do you want to continue?</p>
            <div className="closing-stock-apply-confirm-actions">
              <button
                className="delete-modal-cancel-btn"
                onClick={closeClosingStockApplyConfirmModal}
                disabled={isApplyingClosingStockCorrections}
              >
                Cancel
              </button>
              <button
                className="delete-modal-delete-btn"
                onClick={applyClosingStockCorrections}
                disabled={isApplyingClosingStockCorrections}
              >
                {isApplyingClosingStockCorrections ? 'Applying...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/*
        PIECE GUARD §4 — the read-back, in front of the opening batch.
        Asked once per pcs item, ever. It sits IN FRONT of the save rather than around
        it, so there is still exactly one save path; the question is simply answered
        first, and the save then continues with the count the human just confirmed.
      */}
      {piecePrompt && ReactDOM.createPortal(
        <PieceProfileModal
          isOpen
          itemId={piecePrompt.itemId}
          itemName={piecePrompt.itemName}
          category={piecePrompt.category}
          enteredQuantity={piecePrompt.quantity}
          enteredTotal={piecePrompt.totalPrice}
          // S1 — the raw names still carry "4N"/"X4"/"PKT OF 4"; the matched DB name does not.
          rawItemName={piecePrompt.rawItemName || piecePrompt.itemName}
          originalOcrName={piecePrompt.originalOcrName || null}
          saving={pieceProfileSaving}
          error={pieceProfileError}
          onSave={async (data) => {
            setPieceProfileSaving(true);
            setPieceProfileError(null);
            try {
              const bId = UnitMappingService.getBusinessId();
              const res = await http.post(`/stock-in/v2/piece-profiles?businessId=${bId}`, { ...data, businessId: bId });
              if (!res?.success) throw new Error(res?.error || 'Could not save the piece definition');

              // Do not ask again for this item, whatever happens to the save below.
              piecePromptResolvedRef.current.add(piecePrompt.itemId);

              // Resume the deferred save, with the count the person actually chose.
              //
              // P3 — this door used to multiply whenever `corrected_quantity` was present,
              // and the modal used to send it ALWAYS, because it assumed the typed number
              // was packets and offered no way to say otherwise. Somebody entering an
              // opening stock of 12 loose pieces of a 4-pack item had no honest answer but
              // to lie about the pack size. The shared function is the rule now: the money
              // is never touched, and the count only moves when a human said it should.
              const args = [...piecePrompt.pendingArgs];
              const applied = applyPieceProfileResult(data, {
                quantity: piecePrompt.quantity,
                total_price: piecePrompt.totalPrice,
              });
              if (applied.corrected && Number.isFinite(applied.quantity) && applied.quantity > 0) {
                args[1] = applied.quantity;                            // quantity
                if (applied.unitPrice != null) args[2] = applied.unitPrice;  // unit price follows
              }
              setPiecePrompt(null);
              await saveOnboardingStockDraft(...args);
            } catch (e) {
              setPieceProfileError(e.data?.error || e.message || 'Could not save the piece definition');
            } finally {
              setPieceProfileSaving(false);
            }
          }}
          onCancel={() => { setPiecePrompt(null); setPieceProfileError(null); }}
        />,
        document.body
      )}

      {/* Onboarding Stock Entry Modal */}
      {onboardingStockModal && selectedOnboardingItem && (!shouldBlockClosingStockModal || negativeFixFlowActive) && ReactDOM.createPortal(
        <OnboardingStockModal 
          item={selectedOnboardingItem}
          onClose={closeOnboardingStockModal}
          onSave={saveOnboardingStockDraft}
          existingDraft={negativeFixFlowActive ? null : onboardingDrafts[selectedOnboardingItem.id]}
            isOnboardingFlow={fromOnboarding}
        />,
        document.body
      )}

      {fromOnboarding && isOnboarding && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(600px, 100vw)',
          padding: '12px 20px 16px',
          background: '#ffffff',
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          zIndex: 1001,
          boxSizing: 'border-box'
        }}>
          {/* WHICH DAY THIS COUNT IS FOR. Everything after it is the app's to
              track, so the owner has to be able to say "this is what was on the
              shelves on the 31st" even when they finish setup ten days later.
              Once the count is on the books the date is settled — re-dating it
              is a re-count, and that belongs on the Closing Stock page. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label
              htmlFor="onboarding-closing-date"
              style={{ fontSize: '13px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}
            >
              Stock counted on
            </label>
            {hasCommittedOpeningStock ? (
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                {formatTrackingDate(onboardingClosingDate)}
                <span style={{ fontWeight: 400, color: '#6b7280' }}> · already recorded</span>
              </span>
            ) : (
              <input
                id="onboarding-closing-date"
                type="date"
                value={onboardingClosingDate}
                max={getISTDateString()}
                onChange={(e) => setOnboardingClosingDate(e.target.value || getISTDateString())}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  color: '#111827',
                  background: '#ffffff'
                }}
              />
            )}
          </div>
          {/* What that date means, before it is committed. */}
          <div style={{ fontSize: '12px', color: '#6b7280', minHeight: '16px' }}>
            {describeTrackingPeriod(computeTrackingPeriod(onboardingClosingDate)) ||
              'Pick the day the shelves were counted.'}
          </div>
          {(() => {
            // Finishable when there is opening stock to finish WITH: drafts
            // waiting to be committed, or a count already on the books. The
            // second case used to be missing, which left the button permanently
            // dead for any business whose opening stock arrived another way.
            const canContinue = Object.keys(onboardingDrafts).length > 0 || hasCommittedOpeningStock;
            return (
              <button
                onClick={handleOnboardingSaveBtnClick}
                disabled={!canContinue}
                title={canContinue
                  ? 'Save the opening stock and go to the next setup step'
                  : 'Add opening stock for at least one item first'}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: canContinue ? '#000000' : '#d1d5db',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: canContinue ? 'pointer' : 'not-allowed',
                  transition: 'background 0.2s'
                }}
              >
                Save &amp; Continue
              </button>
            );
          })()}
        </div>
      )}

      {showAddStockWizard && fromOnboarding && isOnboarding && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          onClick={() => {
            if (!isSavingRecipeStock) {
              closeAddStockWizard();
            }
          }}
        >
          <div className="modal-box add-stock-wizard-modal" onClick={(event) => event.stopPropagation()}>
            <div className="add-stock-wizard-header">
              <div>
                <h3 className="add-stock-wizard-title">
                  {selectedRecipeName ? selectedRecipeName : 'Add stock from menu item'}
                </h3>
                {selectedRecipeName && recipeServingQty && addStockStep >= 2 && (
                  <div className="add-stock-wizard-title-meta">
                    Quantity: {recipeServingQty} {recipeQuantityMode === 'batches' ? 'batch(es)' : 'plate(s)'}
                    {recipeQuantityMode === 'batches' && recipeBatchServings > 1
                      ? ` (~${effectivePreparedPlates} plates)`
                      : ''}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="add-stock-wizard-close"
                onClick={closeAddStockWizard}
                disabled={isSavingRecipeStock}
                aria-label="Close add stock"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="add-stock-steps">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`add-stock-step-indicator ${addStockStep === step ? 'active' : ''} ${addStockStep > step ? 'complete' : ''}`}
                >
                  {step}
                </div>
              ))}
            </div>

            {addStockError && (
              <div className="add-stock-wizard-error">{addStockError}</div>
            )}

            {addStockStep === 1 && (
              <div className="add-stock-step">
                <label className="add-stock-label" htmlFor="add-stock-menu-item">Menu item</label>
                <input
                  id="add-stock-menu-item"
                  type="text"
                  className="stock-input add-stock-search-input"
                  value={recipeSearchTerm}
                  onChange={handleRecipeSearchChange}
                  placeholder="Search menu item"
                  autoComplete="off"
                  disabled={isLoadingRecipes || isSavingRecipeStock}
                />

                {isLoadingRecipes || isLoadingPreparations ? (
                  <div className="add-stock-loading">Loading items...</div>
                ) : (
                  <div className="add-stock-search-results">
                    {!hasRecipeSearchInput ? (
                      <div className="add-stock-search-empty">Type to search menu items or preparations.</div>
                    ) : filteredAddStockOptions.length === 0 ? (
                      <div className="add-stock-search-empty">No items found.</div>
                    ) : (
                      filteredAddStockOptions.map((option, index) => (
                        <button
                          key={`${option.__type}-${option.__displayName || 'item'}-${index}`}
                          type="button"
                          className="add-stock-search-item"
                          onClick={() => handleRecipeSelect(option)}
                          disabled={isSavingRecipeStock}
                        >
                          <span className="add-stock-search-name">{option.__displayName}</span>
                          {option.__category && (
                            <span className="add-stock-search-category">{option.__category}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}

                <div className="add-stock-actions">
                  <button
                    type="button"
                    className="add-stock-secondary-btn"
                    onClick={closeAddStockWizard}
                    disabled={isSavingRecipeStock}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {addStockStep === 2 && (
              <div className="add-stock-step">
                {(selectedRecipe || selectedPreparation) ? (
                  <>
                    <div className="add-stock-selected-recipe">
                      <div className="add-stock-selected-title">{selectedRecipeName || 'Selected item'}</div>
                      {selectedAddStockCategory && (
                        <div className="add-stock-selected-meta">{selectedAddStockCategory}</div>
                      )}
                    </div>
                    {selectedPreparation ? (
                      <>
                        <label className="add-stock-label" htmlFor="add-stock-servings">
                          Quantity prepared{selectedPreparationUnit ? ` (${selectedPreparationUnit})` : ''}
                        </label>
                        <input
                          id="add-stock-servings"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          className="stock-input add-stock-quantity-input"
                          value={recipeServingQty}
                          onChange={(event) => setRecipeServingQty(event.target.value)}
                          placeholder="e.g. 2"
                          disabled={isSavingRecipeStock}
                        />
                        {selectedPreparation?.total_weight && selectedPreparationUnit && (
                          <div className="add-stock-hint">
                            Template batch: {selectedPreparation.total_weight} {selectedPreparationUnit}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <label className="add-stock-label" htmlFor="add-stock-servings">Quantity prepared</label>
                        <input
                          id="add-stock-servings"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          className="stock-input add-stock-quantity-input"
                          value={recipeServingQty}
                          onChange={(event) => setRecipeServingQty(event.target.value)}
                          placeholder="e.g. 20"
                          disabled={isSavingRecipeStock}
                        />
                        <div className="add-stock-quantity-toggle">
                          <button
                            type="button"
                            className={`add-stock-quantity-btn ${recipeQuantityMode === 'plates' ? 'active' : ''}`}
                            onClick={() => setRecipeQuantityMode('plates')}
                            disabled={isSavingRecipeStock}
                          >
                            Plates
                          </button>
                          <button
                            type="button"
                            className={`add-stock-quantity-btn ${recipeQuantityMode === 'batches' ? 'active' : ''}`}
                            onClick={() => {
                              setRecipeQuantityMode('batches');
                              if (!recipeBatchServingsOverride && recipeBatchServings > 0) {
                                setRecipeBatchServingsOverride(String(recipeBatchServings));
                              }
                            }}
                            disabled={isSavingRecipeStock}
                          >
                            Batches
                          </button>
                        </div>
                        {recipeQuantityMode === 'batches' && recipeBatchServings > 1 && (
                          <div className="add-stock-hint">1 batch = {recipeBatchServings} plates</div>
                        )}
                        {recipeQuantityMode === 'batches' && (
                          <div className="add-stock-batch-row">
                            <label className="add-stock-label" htmlFor="add-stock-batch-size">Plates per batch</label>
                            <input
                              id="add-stock-batch-size"
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="0.01"
                              className="stock-input add-stock-quantity-input"
                              value={recipeBatchServingsOverride}
                              onChange={(event) => setRecipeBatchServingsOverride(event.target.value)}
                              placeholder="e.g. 10"
                              disabled={isSavingRecipeStock}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="add-stock-search-empty">Select a menu item to continue.</div>
                )}

                <div className="add-stock-actions">
                  <button
                    type="button"
                    className="add-stock-secondary-btn"
                    onClick={handleRecipeStepBack}
                    disabled={isSavingRecipeStock}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="add-stock-primary-btn"
                    onClick={handleRecipeQuantityNext}
                    disabled={isSavingRecipeStock}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {addStockStep === 3 && (
              <div className="add-stock-step add-stock-step-ingredients">
                <div className="add-stock-selected-recipe">
                  <div className="add-stock-selected-title">{selectedRecipeName || 'Selected item'}</div>
                  <div className="add-stock-selected-meta">
                    {selectedPreparation ? (
                      `Quantity: ${recipeServingQty || '0'}${selectedPreparationUnit ? ` ${selectedPreparationUnit}` : ''}`
                    ) : (
                      <>Quantity: {recipeServingQty || '0'} {recipeQuantityMode === 'batches' ? 'batch(es)' : 'plate(s)'}
                        {recipeQuantityMode === 'batches' && recipeBatchServings > 1
                          ? ` (~${effectivePreparedPlates} plates)`
                          : ''}
                      </>
                    )}
                  </div>
                </div>
                {isAutoFillingRecipePrices && (
                  <div className="add-stock-loading">Fetching ingredient prices...</div>
                )}

                <div className="add-stock-ingredient-list add-stock-ingredient-list-scroll">
                  {recipeIngredients.length === 0 && !isAutoFillingRecipePrices ? (
                    <div className="add-stock-search-empty">No ingredients found for this item.</div>
                  ) : (
                    recipeIngredients.map((ingredient, index) => {
                      const pricing = getRecipeIngredientPricing(ingredient);
                      const priceMode = pricing.priceMode;
                      const totalDisplay = pricing.totalPrice !== null
                        ? `₹${pricing.totalPrice.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
                        : '—';
                      const unitLabel = ingredient?.unit ? `₹/${ingredient.unit}` : '₹/unit';
                      const priceInputLabel = priceMode === 'total'
                        ? 'Total price (₹)'
                        : `Unit price (${unitLabel})`;
                      const priceInputValue = priceMode === 'total'
                        ? ingredient.totalPrice
                        : ingredient.unitPrice;

                      return (
                        <div
                          key={`${ingredient?.inventoryId || ingredient?.id || 'ingredient'}-${index}`}
                          className="add-stock-ingredient-row"
                        >
                          <div className="add-stock-ingredient-header">
                            <div className="add-stock-ingredient-name">{ingredient.name}</div>
                          </div>

                          <div className="add-stock-ingredient-controls">
                            <div className="add-stock-ingredient-qty">
                              <label className="add-stock-label">Quantity</label>
                              <div className="add-stock-ingredient-qty-input">
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  min="0"
                                  step="0.001"
                                  className="stock-input add-stock-ingredient-input"
                                  value={ingredient?.scaledQuantity ?? ''}
                                  onChange={(event) => handleRecipeIngredientQuantityChange(index, event.target.value)}
                                  disabled={ingredient?.ingredientMissing || isSavingRecipeStock}
                                />
                                <span className="add-stock-ingredient-qty-unit">
                                  {normalizeUnitDisplay(ingredient.unit)}
                                </span>
                              </div>
                              {ingredient?.ingredientMissing && (
                                <div className="add-stock-ingredient-status">Missing in inventory</div>
                              )}
                            </div>

                            <div className="add-stock-ingredient-price-type">
                              <label className="add-stock-label">Price type</label>
                              <div className="closing-stock-entry-select-wrapper">
                                <div
                                  className={`closing-stock-entry-select-trigger ${ingredient?.ingredientMissing || isSavingRecipeStock ? 'disabled' : ''}`}
                                  style={{
                                    height: '40px',
                                    minHeight: '40px',
                                    borderRadius: '10px',
                                    border: '1px solid #e5e7eb',
                                    background: '#ffffff',
                                    cursor: (ingredient?.ingredientMissing || isSavingRecipeStock) ? 'not-allowed' : 'pointer',
                                    opacity: (ingredient?.ingredientMissing || isSavingRecipeStock) ? 0.6 : 1
                                  }}
                                  onClick={() => {
                                    if (!ingredient?.ingredientMissing && !isSavingRecipeStock) {
                                      setActivePriceTypeDropdownIndex(activePriceTypeDropdownIndex === index ? null : index);
                                    }
                                  }}
                                >
                                  <span>{priceMode === 'total' ? 'Total price' : 'Per unit'}</span>
                                  <svg
                                    className={`closing-stock-entry-select-arrow ${activePriceTypeDropdownIndex === index ? 'open' : ''}`}
                                    width="14"
                                    height="14"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                  >
                                    <path d="M2 4L6 8L10 4" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                                {activePriceTypeDropdownIndex === index && (
                                  <div 
                                    className="closing-stock-entry-select-dropdown" 
                                    role="listbox" 
                                    aria-label="Select price type"
                                    style={{
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '10px',
                                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                                      zIndex: 100
                                    }}
                                  >
                                    <div
                                      className={`add-stock-select-option ${priceMode === 'unit' ? 'selected' : ''}`}
                                      onClick={() => {
                                        handleRecipeIngredientPriceModeChange(index, 'unit');
                                        setActivePriceTypeDropdownIndex(null);
                                      }}
                                    >
                                      Per unit
                                    </div>
                                    <div
                                      className={`add-stock-select-option ${priceMode === 'total' ? 'selected' : ''}`}
                                      onClick={() => {
                                        handleRecipeIngredientPriceModeChange(index, 'total');
                                        setActivePriceTypeDropdownIndex(null);
                                      }}
                                    >
                                      Total price
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="add-stock-ingredient-unit-price">
                              <label className="add-stock-label">{priceInputLabel}</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="0.01"
                                className="stock-input add-stock-ingredient-input"
                                value={priceInputValue}
                                onChange={(event) => handleRecipeIngredientPriceChange(index, event.target.value)}
                                disabled={ingredient?.ingredientMissing || isSavingRecipeStock}
                              />
                              {ingredient?.priceSource && (
                                <div className="add-stock-ingredient-source">{ingredient.priceSource}</div>
                              )}
                            </div>
                          </div>

                          <div className="add-stock-ingredient-total">
                            <div className="add-stock-ingredient-total-label">Total</div>
                            <div className="add-stock-ingredient-total-value">{totalDisplay}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="add-stock-summary">
                  <span className="add-stock-summary-label">Estimated total</span>
                  <span className="add-stock-summary-value">
                    ₹{addStockTotal.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="add-stock-actions">
                  <button
                    type="button"
                    className="add-stock-secondary-btn"
                    onClick={handleRecipeStepBack}
                    disabled={isSavingRecipeStock}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="add-stock-secondary-btn"
                    onClick={closeAddStockWizard}
                    disabled={isSavingRecipeStock}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="add-stock-primary-btn"
                    onClick={handleRecipeAddStockSave}
                    disabled={isSavingRecipeStock || isAutoFillingRecipePrices}
                  >
                    {isSavingRecipeStock ? 'Adding...' : 'Add Stock'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Delete Item Confirmation Modal — portal to body to escape stacking contexts */}
      {deleteConfirmItem && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => { if (!deleteCheckLoading) { anyModalOpenRef.current = false; setDeleteConfirmItem(null); setDeleteUsageDetails(null); setDeleteCheckedIds(new Set()); setDeleteCheckedPrepNames(new Set()); setDeleteCheckedCompIds(new Set()); deletePendingRemovedLinksRef.current = []; deletePendingRemovedPrepsRef.current = []; deletePendingRemovedCompRef.current = []; } }}>
          <div className="modal-box delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            {deleteCheckLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 10px' }}>
                <div className="view-related-spinner" style={{ marginBottom: 16 }}></div>
                <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>Checking Related Items</h3>
                <p style={{ margin: 0, fontSize: '0.88rem', color: '#6b7280', textAlign: 'center' }}>
                  Loading recipes and preparations that use <strong>{deleteConfirmItem.name}</strong>.
                </p>
              </div>
            ) : (deleteUsageDetails && (deleteUsageDetails.menuItems.length > 0 || deleteUsageDetails.preparations.length > 0 || (deleteUsageDetails.complimentaryItems || []).length > 0)) ? (
              <>
                <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem', flexShrink: 0, color: '#111827', fontWeight: 700, textAlign: 'center' }}>Ingredient In Active Use</h3>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 10, flexShrink: 0, textAlign: 'center' }}>
                  Select items below to remove <strong>{deleteConfirmItem.name}</strong> from them, then delete.
                </p>

                <div className="delete-modal-scrollable-body">
                  {deleteUsageDetails.menuItems.length > 0 && (
                    <div style={{ width: '100%', marginBottom: 12, textAlign: 'left' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        fontWeight: 700, fontSize: '0.8rem', color: '#374151',
                        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8
                      }}>
                        <span>Menu Items</span>
                        {deleteUsageDetails.menuItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const allIds = new Set(deleteUsageDetails.menuItems.map((m) => m.id || m));
                              const allChecked = deleteUsageDetails.menuItems.every((m) => deleteCheckedIds.has(m.id || m));
                              setDeleteCheckedIds(allChecked ? new Set() : allIds);
                            }}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              fontSize: '0.75rem', color: '#000000', fontWeight: 700, padding: 0
                            }}
                          >
                            {deleteUsageDetails.menuItems.every((m) => deleteCheckedIds.has(m.id || m))
                              ? 'Deselect All' : 'Select All'}
                          </button>
                        )}
                      </div>
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                        {deleteUsageDetails.menuItems.map((menuItem, i) => {
                          const itemId = menuItem.id || menuItem;
                          const itemName = typeof menuItem === 'object' ? menuItem.name : menuItem;
                          const isChecked = deleteCheckedIds.has(itemId);
                          return (
                            <li key={`del-menu-${i}`} style={{ marginBottom: 4 }}>
                              <label style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                cursor: 'pointer', padding: '6px 10px', borderRadius: 10,
                                backgroundColor: isChecked ? 'rgba(0,0,0,0.06)' : '#f9fafb',
                                border: `1.5px solid ${isChecked ? '#000000' : '#e5e7eb'}`,
                                transition: 'all 0.15s ease'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    setDeleteCheckedIds((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) { next.add(itemId); } else { next.delete(itemId); }
                                      return next;
                                    });
                                  }}
                                  style={{ width: 16, height: 16, accentColor: '#000000', cursor: 'pointer', flexShrink: 0 }}
                                />
                                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: isChecked ? '#000000' : '#111827' }}>
                                  {itemName}
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {deleteUsageDetails.preparations.length > 0 && (
                    <div style={{ width: '100%', marginBottom: 12, textAlign: 'left' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        fontWeight: 700, fontSize: '0.8rem', color: '#374151',
                        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8
                      }}>
                        <span>Preparations</span>
                        {deleteUsageDetails.preparations.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const allNames = new Set(deleteUsageDetails.preparations);
                              const allChecked = deleteUsageDetails.preparations.every((n) => deleteCheckedPrepNames.has(n));
                              setDeleteCheckedPrepNames(allChecked ? new Set() : allNames);
                            }}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              fontSize: '0.75rem', color: '#000000', fontWeight: 700, padding: 0
                            }}
                          >
                            {deleteUsageDetails.preparations.every((n) => deleteCheckedPrepNames.has(n))
                              ? 'Deselect All' : 'Select All'}
                          </button>
                        )}
                      </div>
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                        {(() => {
                        const prepQty = buildPrepQuantityLookup(deleteUsageDetails.preparationDetails);
                        return deleteUsageDetails.preparations.map((prepName, i) => {
                          const isChecked = deleteCheckedPrepNames.has(prepName);
                          return (
                            <li key={`del-prep-${i}`} style={{ marginBottom: 4 }}>
                              <label style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                cursor: 'pointer', padding: '6px 10px', borderRadius: 10,
                                backgroundColor: isChecked ? 'rgba(0,0,0,0.06)' : '#f9fafb',
                                border: `1.5px solid ${isChecked ? '#000000' : '#e5e7eb'}`,
                                transition: 'all 0.15s ease'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    setDeleteCheckedPrepNames((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) { next.add(prepName); } else { next.delete(prepName); }
                                      return next;
                                    });
                                  }}
                                  style={{ width: 16, height: 16, accentColor: '#000000', cursor: 'pointer', flexShrink: 0 }}
                                />
                                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: isChecked ? '#000000' : '#111827', flex: 1 }}>
                                  {prepName}
                                </span>
                                {prepQty.get(prepName) && (
                                  <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#6b7280', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                    {prepQty.get(prepName)}
                                  </span>
                                )}
                              </label>
                            </li>
                          );
                        });
                        })()}
                      </ul>
                    </div>
                  )}

                  {(deleteUsageDetails.complimentaryItems || []).length > 0 && (
                    <div style={{ width: '100%', marginBottom: 12, textAlign: 'left' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        fontWeight: 700, fontSize: '0.8rem', color: '#374151',
                        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8
                      }}>
                        <span>Complimentary Items</span>
                        {deleteUsageDetails.complimentaryItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const allIds = new Set(deleteUsageDetails.complimentaryItems.map((m) => m.id || m));
                              const allChecked = deleteUsageDetails.complimentaryItems.every((m) => deleteCheckedCompIds.has(m.id || m));
                              setDeleteCheckedCompIds(allChecked ? new Set() : allIds);
                            }}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              fontSize: '0.75rem', color: '#000000', fontWeight: 700, padding: 0
                            }}
                          >
                            {deleteUsageDetails.complimentaryItems.every((m) => deleteCheckedCompIds.has(m.id || m))
                              ? 'Deselect All' : 'Select All'}
                          </button>
                        )}
                      </div>
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                        {deleteUsageDetails.complimentaryItems.map((item, i) => {
                          const itemId = item.id || item;
                          const itemName = typeof item === 'object' ? item.name : item;
                          const isChecked = deleteCheckedCompIds.has(itemId);
                          return (
                            <li key={`del-comp-${i}`} style={{ marginBottom: 4 }}>
                              <label style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                cursor: 'pointer', padding: '6px 10px', borderRadius: 10,
                                backgroundColor: isChecked ? 'rgba(0,0,0,0.06)' : '#f9fafb',
                                border: `1.5px solid ${isChecked ? '#000000' : '#e5e7eb'}`,
                                transition: 'all 0.15s ease'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    setDeleteCheckedCompIds((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) { next.add(itemId); } else { next.delete(itemId); }
                                      return next;
                                    });
                                  }}
                                  style={{ width: 16, height: 16, accentColor: '#000000', cursor: 'pointer', flexShrink: 0 }}
                                />
                                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: isChecked ? '#000000' : '#111827' }}>
                                  {itemName}
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="delete-modal-actions-row">
                  <button
                    className="delete-modal-cancel-btn-white-modal"
                    onClick={() => { anyModalOpenRef.current = false; setDeleteConfirmItem(null); setDeleteUsageDetails(null); setDeleteCheckedIds(new Set()); setDeleteCheckedPrepNames(new Set()); setDeleteCheckedCompIds(new Set()); deletePendingRemovedLinksRef.current = []; deletePendingRemovedPrepsRef.current = []; deletePendingRemovedCompRef.current = []; }}
                    style={{
                      flex: 1,
                      margin: 0
                    }}
                  >
                    Close
                  </button>

                  {deleteCheckedPrepNames.size === 0 && deleteCheckedIds.size === 0 && deleteCheckedCompIds.size === 0 && (
                    <button
                      type="button"
                      disabled
                      className="delete-usage-submit-btn"
                      style={{
                        flex: 1,
                        backgroundColor: '#ebebeb',
                        color: '#9ca3af',
                        cursor: 'not-allowed',
                        boxShadow: 'none',
                        margin: 0
                      }}
                    >
                      Remove
                    </button>
                  )}

                  {deleteCheckedPrepNames.size > 0 && (
                    <button
                      type="button"
                      onClick={handleRemovePrepsFromDeleteModal}
                      disabled={deleteRemovingPreps}
                      className="delete-usage-submit-btn"
                      style={{
                        flex: 1,
                        backgroundColor: deleteRemovingPreps ? '#f3f4f6' : '#000000',
                        color: deleteRemovingPreps ? '#9ca3af' : '#fff',
                        cursor: deleteRemovingPreps ? 'not-allowed' : 'pointer',
                        boxShadow: deleteRemovingPreps ? 'none' : '0 4px 12px rgba(0,0,0,0.3)'
                      }}
                    >
                      {deleteRemovingPreps
                        ? 'Removing...'
                        : `Remove from ${deleteCheckedPrepNames.size} Prep${deleteCheckedPrepNames.size > 1 ? 's' : ''}`}
                    </button>
                  )}

                  {deleteCheckedIds.size > 0 && (
                    <button
                      type="button"
                      onClick={handleRemoveFromDeleteModal}
                      disabled={deleteRemoving}
                      className="delete-usage-submit-btn"
                      style={{
                        flex: 1,
                        backgroundColor: deleteRemoving ? '#f3f4f6' : '#000000',
                        color: deleteRemoving ? '#9ca3af' : '#fff',
                        cursor: deleteRemoving ? 'not-allowed' : 'pointer',
                        boxShadow: deleteRemoving ? 'none' : '0 4px 12px rgba(0,0,0,0.3)'
                      }}
                    >
                      {deleteRemoving
                        ? 'Removing...'
                        : `Remove from ${deleteCheckedIds.size} Item${deleteCheckedIds.size > 1 ? 's' : ''}`}
                    </button>
                  )}

                  {deleteCheckedCompIds.size > 0 && (
                    <button
                      type="button"
                      onClick={handleRemoveCompFromDeleteModal}
                      disabled={deleteRemovingComp}
                      className="delete-usage-submit-btn"
                      style={{
                        flex: 1,
                        backgroundColor: deleteRemovingComp ? '#f3f4f6' : '#000000',
                        color: deleteRemovingComp ? '#9ca3af' : '#fff',
                        cursor: deleteRemovingComp ? 'not-allowed' : 'pointer',
                        boxShadow: deleteRemovingComp ? 'none' : '0 4px 12px rgba(0,0,0,0.3)'
                      }}
                    >
                      {deleteRemovingComp
                        ? 'Removing...'
                        : `Remove from ${deleteCheckedCompIds.size} Comp${deleteCheckedCompIds.size > 1 ? 's' : ''}`}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 0' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 700, color: '#111827', flexShrink: 0, textAlign: 'center' }}>Delete {deleteConfirmItem.name}?</h3>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 18, textAlign: 'center', flexShrink: 0 }}>
                  This will permanently remove this ingredient and all its batches. This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', width: '100%', flexShrink: 0 }}>
                  <button className="delete-modal-cancel-btn" onClick={() => { anyModalOpenRef.current = false; setDeleteConfirmItem(null); setDeleteUsageDetails(null); setDeleteCheckedIds(new Set()); setDeleteCheckedPrepNames(new Set()); setDeleteCheckedCompIds(new Set()); deletePendingRemovedLinksRef.current = []; deletePendingRemovedPrepsRef.current = []; deletePendingRemovedCompRef.current = []; }} disabled={deleteCheckLoading}>Cancel</button>
                  <button className="delete-modal-delete-btn" onClick={() => deleteEntireItem(deleteConfirmItem.id)} disabled={deleteCheckLoading}>
                    {deleteCheckLoading ? 'Checking...' : 'Delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* View Related Items Modal (Double Click) */}
      {viewRelatedConfirmItem && ReactDOM.createPortal(
        <div 
          className="modal-overlay view-related-overlay" 
          onClick={closeViewRelatedModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div 
            className="modal-box related-items-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              padding: '28px 24px',
              borderRadius: '24px',
              maxWidth: '480px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(229, 231, 235, 0.5)',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              fontFamily: 'Roboto, sans-serif'
            }}
          >
            {viewRelatedCheckLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', padding: '30px 10px' }}>
                <div className="view-related-spinner"></div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#111827', fontWeight: 600, fontFamily: 'Roboto, sans-serif' }}>Scanning Connections</h3>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280', textAlign: 'center', lineHeight: '1.5', fontFamily: 'Roboto, sans-serif' }}>
                  Locating menu items and preparations linked to <strong style={{ color: '#111827' }}>{viewRelatedConfirmItem.name}</strong>...
                </p>
                <button
                  type="button"
                  onClick={closeViewRelatedModal}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#6b7280',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: '6px 10px'
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Header */}
                <h3 style={{ 
                  margin: '0 0 6px 0', 
                  fontSize: '22px', 
                  color: '#111827', 
                  fontWeight: 800, 
                  letterSpacing: '-0.025em',
                  fontFamily: 'Roboto, sans-serif' 
                }}>
                  {viewRelatedConfirmItem.name}
                </h3>
                <p style={{ 
                  margin: '0 0 24px 0', 
                  fontSize: '0.9rem', 
                  color: '#6b7280', 
                  lineHeight: '1.5',
                  fontFamily: 'Roboto, sans-serif' 
                }}>
                  This ingredient is connected to the following food items.
                </p>

                {/* Content Container */}
                <div style={{ 
                  maxHeight: '280px', 
                  overflowY: 'auto', 
                  width: '100%', 
                  marginBottom: '18px',
                  paddingRight: '6px',
                  textAlign: 'left'
                }}>
                  {viewRelatedUsageDetails && (viewRelatedUsageDetails.menuItems.length > 0 || viewRelatedUsageDetails.preparations.length > 0 || (viewRelatedUsageDetails.complimentaryItems || []).length > 0) ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {viewRelatedUsageDetails.menuItems.length > 0 && (
                        <div>
                          <div style={{ 
                            fontSize: '16px', 
                            fontWeight: 700, 
                            color: '#6b7280', 
                            textTransform: 'uppercase', 
                            letterSpacing: '0.08em',
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span>Menu Items Using This</span>
                          </div>
                          <ul style={{
                            margin: 0,
                            padding: 0,
                            listStyle: 'none',
                            color: '#111827',
                            fontSize: '14px',
                            fontWeight: 600,
                            lineHeight: 1.7
                          }}>
                            {viewRelatedUsageDetails.menuItems.map((item, idx) => (
                              <li key={`menu-${item.id}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '4px 0' }}>
                                <span style={{ color: '#111827', flexShrink: 0 }}>
                                  {idx + 1}.
                                </span>
                                <span style={{ color: '#111827', flex: 1 }}>
                                  {item.name}
                                </span>
                                {item.quantityLabel && (
                                  <span style={{ color: '#6b7280', fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap' }}>
                                    {item.quantityLabel} / plate
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {viewRelatedUsageDetails.preparations.length > 0 && (
                        <div>
                          <div style={{ 
                            fontSize: '16px', 
                            fontWeight: 700, 
                            color: '#6b7280', 
                            textTransform: 'uppercase', 
                            letterSpacing: '0.08em',
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span>Preparations Using This</span>
                          </div>
                          <ul style={{
                            margin: 0,
                            padding: 0,
                            listStyle: 'none',
                            color: '#111827',
                            fontSize: '14px',
                            fontWeight: 600,
                            lineHeight: 1.7
                          }}>
                            {(() => {
                              const prepQty = buildPrepQuantityLookup(viewRelatedUsageDetails.preparationDetails);
                              return viewRelatedUsageDetails.preparations.map((name, idx) => (
                                <li key={`prep-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '4px 0' }}>
                                  <span style={{ color: '#111827', flexShrink: 0 }}>
                                    {idx + 1}.
                                  </span>
                                  <span style={{ color: '#111827', flex: 1 }}>
                                    {name}
                                  </span>
                                  {prepQty.get(name) && (
                                    <span style={{ color: '#6b7280', fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap' }}>
                                      {prepQty.get(name)}
                                    </span>
                                  )}
                                </li>
                              ));
                            })()}
                          </ul>
                        </div>
                      )}

                      {(viewRelatedUsageDetails.complimentaryItems || []).length > 0 && (
                        <div>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: 700,
                            color: '#6b7280',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span>Complimentary Items Using This</span>
                          </div>
                          <ul style={{
                            margin: 0,
                            padding: 0,
                            listStyle: 'none',
                            color: '#111827',
                            fontSize: '14px',
                            fontWeight: 600,
                            lineHeight: 1.7
                          }}>
                            {viewRelatedUsageDetails.complimentaryItems.map((item) => (
                              <li key={`comp-${item.id}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0' }}>
                                <span style={{
                                  width: 6, height: 6, borderRadius: '50%',
                                  backgroundColor: '#000000', flexShrink: 0, display: 'inline-block'
                                }} />
                                <span style={{ color: '#111827', flex: 1 }}>
                                  {item.name}
                                </span>
                                {item.quantityLabel && (
                                  <span style={{ color: '#6b7280', fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap' }}>
                                    {item.quantityLabel} / serving
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ 
                      padding: '30px 20px', 
                      backgroundColor: '#f9fafb', 
                      borderRadius: '20px', 
                      textAlign: 'center',
                      border: '1.5px dashed #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{ fontSize: '1.8rem' }}>🍃</div>
                      <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#374151', fontWeight: 600 }}>Unused Ingredient</h4>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.45 }}>
                        This ingredient is not currently used in any recipe, menu item, or preparation.
                      </p>
                    </div>
                  )}
                </div>

                {/* Remove from Selected Button removed — use dropdown delete to manage */}

                {/* Close Button */}
                <button 
                  onClick={closeViewRelatedModal}
                  style={{
                    width: '100px',
                    maxWidth: '100px',
                    padding: '10px 14px',
                    borderRadius: '14px',
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'block',
                    margin: '0 auto',
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {showInventoryEditModal && inventoryEditMode && ReactDOM.createPortal(
        <div
          className="modal-overlay inventory-edit-overlay"
        >
          <div className="modal-box inventory-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="inventory-edit-header">
              <h3 className="inventory-edit-title">{inventoryEditTitle}</h3>
              <button
                type="button"
                className="inventory-edit-close"
                onClick={closeInventoryEditModal}
                disabled={inventoryEditLoading}
                aria-label="Close edit dialog"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            {inventoryEditDescription && (
              <p className="inventory-edit-subtitle">{inventoryEditDescription}</p>
            )}
            <form
              className="inventory-edit-form"
              autoComplete="off"
              onSubmit={(event) => {
                event.preventDefault();
                handleInventoryEditSubmit();
              }}
            >
              <div className="inventory-edit-field">
                <label className="inventory-edit-label" htmlFor="inventory-edit-current-name">Current item name</label>
                <input
                  id="inventory-edit-current-name"
                  name="inventory-current-name"
                  className="inventory-edit-input"
                  type="text"
                  value={inventoryEditForm.currentName}
                  onChange={(event) => handleInventoryEditFieldChange('currentName', event.target.value)}
                  onFocus={() => setShowInventoryNameSuggestions(true)}
                  onBlur={() => {
                    setTimeout(() => setShowInventoryNameSuggestions(false), 120);
                  }}
                  placeholder="e.g. Almond Powder"
                  autoComplete="off"
                  autoFocus
                  disabled={inventoryEditLoading}
                />
                {showInventoryNameSuggestions && inventoryNameSuggestions.length > 0 && (
                  <div className="inventory-edit-suggestions">
                    {inventoryNameSuggestions.map((name) => (
                      <button
                        key={name}
                        type="button"
                        className="inventory-edit-suggestion"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          applyInventoryNameSuggestion(name);
                        }}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Related items removed — only current/new name shown in rename edit per request */}
              {inventoryEditMode === 'rename' && (
                <div className="inventory-edit-field">
                  <label className="inventory-edit-label" htmlFor="inventory-edit-new-name">New item name</label>
                  <input
                    id="inventory-edit-new-name"
                    name="inventory-new-name"
                    className="inventory-edit-input"
                    type="text"
                    value={inventoryEditForm.newName}
                    onChange={(event) => handleInventoryEditFieldChange('newName', event.target.value)}
                    placeholder="Enter new item name"
                    autoComplete="off"
                    disabled={inventoryEditLoading}
                  />
                </div>
              )}
              {inventoryEditMode === 'merge' && (
                <div className="inventory-edit-field">
                  <label className="inventory-edit-label" htmlFor="inventory-edit-merge-target">Merge into item or preparation</label>
                  <input
                    id="inventory-edit-merge-target"
                    name="inventory-merge-target"
                    className="inventory-edit-input"
                    type="text"
                    value={inventoryEditForm.mergeTargetName}
                    onChange={(event) => {
                      setMergeTargetPrep(null);
                      handleInventoryEditFieldChange('mergeTargetName', event.target.value);
                    }}
                    onFocus={() => setShowMergeTargetSuggestions(true)}
                    onBlur={() => {
                      setTimeout(() => setShowMergeTargetSuggestions(false), 120);
                    }}
                    placeholder="Select an item or a saved preparation"
                    autoComplete="off"
                    disabled={inventoryEditLoading}
                  />
                  {showMergeTargetSuggestions && mergeTargetSuggestions.length > 0 && (
                    <div className="inventory-edit-suggestions">
                      {mergeTargetSuggestions.map((entry) => (
                        <button
                          key={entry.key}
                          type="button"
                          className="inventory-edit-suggestion"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            applyMergeTargetSuggestion(entry);
                          }}
                        >
                          {entry.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {mergeTargetIsPreparation && (
                    <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.5, color: '#6b7280' }}>
                      Nothing is deleted. Every recipe keeps pointing at &quot;{inventoryEditForm.currentName}&quot;, and
                      sales will deduct what this preparation is made of instead of its own stock.
                    </div>
                  )}
                </div>
              )}
              {inventoryEditMode === 'unit' && (
                <>
                  <div className="inventory-edit-field" style={{ position: 'relative' }}>
                    <label className="inventory-edit-label" htmlFor="inventory-edit-current-unit">Current unit</label>
                    <div
                      id="inventory-edit-current-unit"
                      tabIndex={0}
                      role="button"
                      aria-haspopup="listbox"
                      aria-expanded={showCurrentUnitDropdown}
                      className="inventory-edit-input"
                      onClick={() => setShowCurrentUnitDropdown(prev => !prev)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowCurrentUnitDropdown(prev => !prev); } }}
                      onBlur={() => { setTimeout(() => setShowCurrentUnitDropdown(false), 120); }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <span style={{ color: inventoryEditForm.currentUnit ? '#111827' : '#9ca3af' }}>
                        {inventoryCurrentUnitOptions.find(u => u.value === inventoryEditForm.currentUnit)?.label || inventoryEditForm.currentUnit || 'Select unit'}
                      </span>
                      <span style={{ marginLeft: 8, color: '#6b7280' }}>▾</span>
                    </div>
                    {showCurrentUnitDropdown && (
                      <div className="inventory-edit-suggestions" style={{ position: 'absolute', left: 0, right: 0, zIndex: 1200, bottom: 'calc(100% + 6px)' }}>
                        {inventoryCurrentUnitOptions.map((unit) => (
                          <button
                            key={unit.value}
                            type="button"
                            className="inventory-edit-suggestion"
                            onMouseDown={(event) => { event.preventDefault(); handleInventoryEditFieldChange('currentUnit', unit.value); setShowCurrentUnitDropdown(false); }}
                          >
                            {unit.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="inventory-edit-field" style={{ position: 'relative' }}>
                    <label className="inventory-edit-label" htmlFor="inventory-edit-new-unit">New unit</label>
                    <div
                      id="inventory-edit-new-unit"
                      tabIndex={0}
                      role="button"
                      aria-haspopup="listbox"
                      aria-expanded={showUnitDropdown}
                      className="inventory-edit-input"
                      onClick={() => setShowUnitDropdown(prev => !prev)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowUnitDropdown(prev => !prev); } }}
                      onBlur={() => { setTimeout(() => setShowUnitDropdown(false), 120); }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <span style={{ color: inventoryEditForm.newUnit ? '#111827' : '#9ca3af' }}>
                        {inventoryUnitOptions.find(u => u.value === inventoryEditForm.newUnit)?.label || 'Select unit'}
                      </span>
                      <span style={{ marginLeft: 8, color: '#6b7280' }}>▾</span>
                    </div>
                    {showUnitDropdown && (
                      <div className="inventory-edit-suggestions" style={{ position: 'absolute', left: 0, right: 0, zIndex: 1200, bottom: 'calc(100% + 6px)' }}>
                        {inventoryUnitOptions.map((unit) => (
                          <button
                            key={unit.value}
                            type="button"
                            className="inventory-edit-suggestion"
                            onMouseDown={(event) => { event.preventDefault(); handleInventoryEditFieldChange('newUnit', unit.value); setShowUnitDropdown(false); }}
                          >
                            {unit.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {(() => {
                    const normalisedCurrent = UnitMappingService.normalizeUnitSymbol(inventoryEditForm.currentUnit || '');
                    const isSameUnit = inventoryEditForm.newUnit &&
                      normalisedCurrent === UnitMappingService.normalizeUnitSymbol(inventoryEditForm.newUnit);

                    return (
                      <>
                        {isSameUnit && (
                          <div style={{
                            marginTop: '4px',
                            padding: '10px 14px',
                            borderRadius: '10px',
                            background: '#fff7ed',
                            border: '1px solid #fdba74',
                            color: '#9a3412',
                            fontSize: '13px',
                            lineHeight: 1.45,
                            fontWeight: 500,
                          }}>
                            ⚠️ Source and destination units are the same — please select a different unit.
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
              {inventoryEditError && (
                <div className="inventory-edit-error">{inventoryEditError}</div>
              )}
              <div className="inventory-edit-actions">
                <button
                  type="button"
                  className="inventory-edit-secondary-btn"
                  onClick={closeInventoryEditModal}
                  disabled={inventoryEditLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inventory-edit-primary-btn"
                  disabled={inventoryEditLoading || (() => {
                    if (inventoryEditMode !== 'unit') return false;
                    const normalisedCurrent = UnitMappingService.normalizeUnitSymbol(inventoryEditForm.currentUnit || '');
                    return inventoryEditForm.newUnit &&
                      normalisedCurrent === UnitMappingService.normalizeUnitSymbol(inventoryEditForm.newUnit);
                  })()}
                >
                  {inventoryEditPrimaryLabel}
                </button>
              </div>
            </form>
            <datalist id="inventory-edit-item-options">
              {inventoryNameOptions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>
        </div>,
        document.body
      )}

      {showInventoryEditModal && showInventoryRenameConfirm && ReactDOM.createPortal(
        <div
          className="modal-overlay inventory-edit-overlay inventory-edit-confirm-overlay"
          onClick={() => {
            if (!inventoryEditLoading) {
              setShowInventoryRenameConfirm(false);
            }
          }}
          style={{ zIndex: 10000 }}
        >
          <div
            className="modal-box inventory-edit-confirm-modal"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 'min(92vw, 520px)',
              background: '#fff',
              borderRadius: '18px',
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
              padding: '22px 22px 18px',
              fontFamily: 'Roboto, sans-serif'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '18px' }}>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#111827' }}>
                  {inventoryEditMode === 'merge'
                    ? (mergeTargetIsPreparation ? 'Confirm preparation' : 'Confirm merge')
                    : inventoryEditMode === 'unit' ? 'Confirm unit change' : 'Confirm rename'}
                </h3>
                {inventoryEditMode === 'merge' && mergeTargetIsPreparation && (
                  <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.5, color: '#6b7280' }}>
                    Nothing is deleted. Every recipe keeps pointing at &quot;{inventoryEditForm.currentName}&quot;, and
                    sales will deduct what &quot;{inventoryEditForm.mergeTargetName}&quot; is made of instead of its own stock.
                  </p>
                )}
            </div>

            <div style={{ marginBottom: '14px', padding: '8px 0', borderRadius: 0, border: 'none', background: 'transparent', textAlign: 'center' }}>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>{inventoryEditForm.currentName || '-'}</div>
                </div>
                <div style={{ flex: '1 1 180px', minWidth: 0, padding: '10px 12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {inventoryEditMode === 'merge'
                      ? 'Merge into'
                      : inventoryEditMode === 'unit' ? 'New unit' : 'New'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>
                    {inventoryEditMode === 'rename'
                      ? (inventoryEditForm.newName || '-')
                      : inventoryEditMode === 'merge'
                        ? (inventoryEditForm.mergeTargetName || '-')
                        : (inventoryEditForm.newUnit || '-')}
                  </div>
                </div>
              </div>
            </div>

            {inventoryEditUsagePanel}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setShowInventoryRenameConfirm(false)}
                disabled={inventoryEditLoading}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#111827',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: inventoryEditLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleInventoryEditSubmit}
                disabled={inventoryEditLoading}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: 'none',
                  background: inventoryEditLoading ? '#9ca3af' : '#111827',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: inventoryEditLoading ? 'not-allowed' : 'pointer'
                }}
                >
                {inventoryEditLoading
                  ? (inventoryEditMode === 'merge' ? 'Merging...' : inventoryEditMode === 'unit' ? 'Changing...' : 'Renaming...')
                  : (inventoryEditMode === 'merge'
                    ? (mergeTargetIsPreparation ? 'Use this preparation' : 'Confirm Merge')
                    : inventoryEditMode === 'unit' ? 'Confirm Change' : 'Confirm Rename')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Global Unit Change Confirmation Modal */}
      {showUnitConfirmModal && unitChangePreviewData && (
        <UnitChangeConfirmationModal
          isOpen={showUnitConfirmModal}
          previewData={unitChangePreviewData}
          onConfirm={handleOverviewUnitChangeConfirm}
          onCancel={() => {
            setShowUnitConfirmModal(false);
            setPendingOverviewUnitChange(null);
            setUnitChangePreviewData(null);
          }}
          isLoading={isApplyingOverviewUnitChange}
        />
      )}

      {/* Cross-Family Conversion Modal (Strict 5 Unit Enforcement) */}
      {showCrossFamilyModal && crossFamilyModalData && (
        <CrossFamilyConversionModal
          isOpen={showCrossFamilyModal}
          itemId={crossFamilyModalData.itemId}
          itemName={crossFamilyModalData.itemName}
          currentStock={crossFamilyModalData.currentStock}
          currentUnit={crossFamilyModalData.currentUnit}
          currentUnitId={crossFamilyModalData.currentUnitId}
          newUnit={crossFamilyModalData.newUnit}
          newUnitId={crossFamilyModalData.newUnitId}
          onConfirm={handleCrossFamilyConversionConfirm}
          onCancel={handleCrossFamilyConversionCancel}
        />
      )}

      {/* Stock In Discard Progress Confirmation Modal */}
      {showStockInDiscardConfirm && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          onClick={() => setShowStockInDiscardConfirm(false)}
          style={{ zIndex: 10000 }}
        >
          <div
            className="modal-box"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 'min(92vw, 420px)',
              background: '#fff',
              borderRadius: '18px',
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
              padding: '24px 24px 20px',
              fontFamily: 'Roboto, sans-serif',
              textAlign: 'center'
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '20px', fontWeight: 800, color: '#111827' }}>
                Discard Scanned Progress?
              </h3>
              <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.5, color: '#4b5563' }}>
                Moving to Manual Entry will clear your currently scanned bill image and vendor details. Your current progress will be lost.
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setShowStockInDiscardConfirm(false)}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#111827',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDiscardScannedBill}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: 'none',
                  background: '#111827',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Discard
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Stock In Tab Content */}
      {activeTab === 'stock-in' && (
        <motion.div
          className="stock-in-container"
          initial={prefersReducedMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2 }}
        >
          <div className="sub-tabs invtabv2-sub-tabs" ref={stockInTabsRef}>
            <div
              className={`sub-tab ${stockInTab === 'scan' ? 'active' : ''}`}
              onClick={() => {
                setStockInTab('scan');
                setShowStockInForm(false);
                // Reset BillScanner when switching to scan tab
                if (billScannerRef.current) {
                  billScannerRef.current.resetScanner();
                }
                // Clear OCR data when switching tabs
                setOcrData(null);
              }}
            >
              Scan Bill
            </div>
            <div
              className={`sub-tab ${stockInTab === 'manual' ? 'active' : ''}`}
              onClick={() => {
                if (ocrData !== null) {
                  setShowStockInDiscardConfirm(true);
                } else {
                  setStockInTab('manual');
                  setShowStockInForm(true);
                  if (billScannerRef.current) {
                    billScannerRef.current.resetScanner();
                  }
                }
              }}
            >
              Manual Entry
            </div>
            <span className="invtabv2-indicator invtabv2-sub-indicator" style={stockInTabsIndicatorStyle} aria-hidden="true" />
          </div>
          {/* Auto-correct toggle removed: feature is always enabled via backend AI */}
          
          {stockInTab === 'scan' && !showStockInForm && (
            <BillScanner 
              ref={billScannerRef}
              onItemsExtracted={handleOCRItemsExtracted}
              onError={handleOCRError}
              bmVendorId={bmPick.scannerVendorId}
            />
          )}
          
          {(stockInTab === 'manual' || showStockInForm) && (
            <>
              {/* key forces remount to avoid stale disabled state on quantity input observed for some vegetables */}
              <StockInForm
                key={`${stockInTab}-${ocrData ? 'ocr' : 'manual'}`}
                ocrData={ocrData}
                manualHeaderAddTrigger={manualHeaderAddTrigger}
                onBack={null}
                onSuccess={handleStockInSuccess}
                autoCorrect={true}
                goTo={goTo}
                onVoiceRecordingChange={setIsVoiceRecording}
                bmPickedVendor={bmPick.picked}
                onBmVendorPicked={bmPick.onPicked}
                stockInDate={isOnboarding ? (() => {
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  return yesterday.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                })() : null}
              />
            </>
          )}
        </motion.div>
      )}

      {/* Stock Out Tab Content */}
      {activeTab === 'stock-out' && (
        <motion.div
          className="stock-out-container"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <StockOutForm 
            initialSubTab={initialStockOutSubTab} 
            goTo={goTo}
            onLockStateChange={setStockOutLock}
            recoveryDate={recoveryDate}
            openReasonModal={openWastageReasonModal}
            onReasonModalOpened={onWastageReasonModalOpened}
          />
        </motion.div>
      )}
    </div>
  );
};

// Global units (don't need conversion)
const globalUnits = ['g', 'kg', 'ml', 'l'];
const globalUnitAliases = new Set([
  'g', 'gm', 'gram', 'grams',
  'kg', 'kilogram', 'kilograms',
  'ml', 'milliliter', 'milliliters',
  'l', 'ltr', 'liter', 'litre', 'liters', 'litres',
  'pc', 'pcs', 'piece', 'pieces'
]);

// Common weight units
const weightUnits = ['g', 'kg', 'ml', 'l', 'units', 'pieces', 'pcs', 'bottles', 'packets', 'bags'];

// Onboarding Stock Entry Modal Component
const OnboardingStockModal = ({ item, onClose, onSave, existingDraft, isOnboardingFlow = false }) => {
  const isNegativeStockEntry = Number(item?.quantity) < 0;
  const shouldUseExistingDraft = Boolean(existingDraft) && !isNegativeStockEntry;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const suggestedNegativeQuantity = isNegativeStockEntry
    ? (item?.fixSuggestedQuantity !== '' && item?.fixSuggestedQuantity !== undefined && item?.fixSuggestedQuantity !== null
      ? String(item.fixSuggestedQuantity)
      : (Math.abs(Number(item?.quantity || 0)) > 0 ? String(Math.abs(Number(item.quantity || 0))) : ''))
    : '';
  const suggestedNegativeUnit = isNegativeStockEntry
    ? (item?.fixSuggestedUnit || item?.unit || 'g')
    : (item?.unit || 'g');

  // The price the Overview card is ALREADY showing for this item ("₹658/kg"), restated
  // per base unit. The list payload carries it, so a negative-stock fix can open with the
  // price filled in instead of blank — the round trip behind Fix Price is a refresh, not
  // the only way to get a number. Null (not 0) when the item has never had a priced
  // batch, so "no price known" stays distinguishable from a real ₹0.
  const initialCostBasis = getItemCostBasisPerBaseUnit(item);
  const initialSuggestedUnitPrice = isNegativeStockEntry && initialCostBasis
    ? pricePerUnitFromBaseCost(initialCostBasis.costPerBase, initialCostBasis.baseUnit, suggestedNegativeUnit)
    : null;

  // ✅ CRITICAL: When reopening a saved draft with conversion, show ORIGINAL input values
  // If raw_quantity exists, user entered conversion (e.g., 1 bags → 10 kg)
  // Display the original input (1 bags) not the converted value (10 kg)
  const hasConversion = shouldUseExistingDraft
    && existingDraft?.raw_quantity
    && existingDraft?.original_unit
    && existingDraft?.unit_conversion;
  
  // Calculate original unit price from converted unit price if conversion exists
  let originalUnitPrice = shouldUseExistingDraft ? (existingDraft?.unit_price || '') : '';
  if (shouldUseExistingDraft && hasConversion && existingDraft?.unit_price && existingDraft?.unit_conversion) {
    // If saved as ₹0.10/kg (converted), calculate back to ₹1.00/bag (original)
    originalUnitPrice = (parseFloat(existingDraft.unit_price) * parseFloat(existingDraft.unit_conversion)).toFixed(2);
    console.log(`🔄 [MODAL INIT] Recalculating unit price for original unit:`);
    console.log(`   Converted: ₹${existingDraft.unit_price}/${existingDraft.unit}`);
    console.log(`   Factor: ${existingDraft.unit_conversion}`);
    console.log(`   Original: ₹${originalUnitPrice}/${existingDraft.original_unit}`);
  }
  
  const [quantity, setQuantity] = useState(
    shouldUseExistingDraft
      ? (hasConversion ? existingDraft.raw_quantity : (existingDraft?.quantity || ''))
      : (suggestedNegativeQuantity || '')
  );
  const [selectedUnit, setSelectedUnit] = useState(
    shouldUseExistingDraft
      ? (hasConversion ? existingDraft.original_unit : (existingDraft?.unit || suggestedNegativeUnit))
      : suggestedNegativeUnit
  );
  const [priceMode, setPriceMode] = useState(
    shouldUseExistingDraft
      ? (existingDraft?.price_mode || 'total')
      : (initialSuggestedUnitPrice ? 'unit' : 'total')
  );
  const [pricingUnit, setPricingUnit] = useState(
    shouldUseExistingDraft
      ? (existingDraft?.unit || suggestedNegativeUnit)
      : suggestedNegativeUnit
  );
  const [pricingQuantity, setPricingQuantity] = useState(() => {
    if (!shouldUseExistingDraft) {
      // A prefilled basis is a price for ONE unit; the reference quantity has to say so,
      // or the per-unit price gets divided by the shortfall quantity on save.
      if (initialSuggestedUnitPrice) return '1';
      return isNegativeStockEntry && suggestedNegativeQuantity ? String(suggestedNegativeQuantity) : '';
    }

    const storedReferenceQty = parseFloat(existingDraft?.pack_quantity);
    if (Number.isFinite(storedReferenceQty) && storedReferenceQty > 0) {
      return String(storedReferenceQty);
    }

    if (existingDraft?.price_mode === 'unit') {
      return '1';
    }

    const draftQty = hasConversion ? existingDraft?.raw_quantity : existingDraft?.quantity;
    return draftQty !== null && draftQty !== undefined && draftQty !== '' ? String(draftQty) : '';
  });
  const [pricingPrice, setPricingPrice] = useState(() => {
    if (!shouldUseExistingDraft) {
      return initialSuggestedUnitPrice ? formatAutoPriceValue(initialSuggestedUnitPrice) : '';
    }

    if (existingDraft?.price_mode === 'unit') {
      return originalUnitPrice !== '' && originalUnitPrice !== null && originalUnitPrice !== undefined
        ? String(originalUnitPrice)
        : '';
    }

    return existingDraft?.total_price !== null && existingDraft?.total_price !== undefined && existingDraft?.total_price !== ''
      ? String(existingDraft.total_price)
      : '';
  });
  const [unitConversion, setUnitConversion] = useState(shouldUseExistingDraft ? (existingDraft?.unit_conversion || '') : '');
  const [conversionUnit, setConversionUnit] = useState(shouldUseExistingDraft ? (existingDraft?.conversion_unit || 'g') : 'g');
  const [directPrice, setDirectPrice] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoFillingPrice, setIsAutoFillingPrice] = useState(false);
  // The suggested price is held as a per-BASE-unit cost, not as the string in the input.
  // Keeping the basis means switching the invoice unit g -> kg rescales the suggestion
  // instead of leaving a per-gram number sitting under a "per kg" label.
  const [autoPriceBasis, setAutoPriceBasis] = useState(initialCostBasis);
  // True only while the price field still holds the suggestion. Any keystroke in a price
  // field clears it, so a unit change never overwrites a number the user typed.
  const [isPriceAutoFilled, setIsPriceAutoFilled] = useState(Boolean(initialSuggestedUnitPrice));
  const [autoPriceSource, setAutoPriceSource] = useState(initialSuggestedUnitPrice ? 'latest_purchase' : null);
  const [alertModal, setAlertModal] = useState({ visible: false, message: '' });
  const showInAppAlert = (message) => setAlertModal({ visible: true, message });
  const closeInAppAlert = () => setAlertModal({ visible: false, message: '' });

  // Has the user typed in a price field? A ref rather than state because the fetch below
  // awaits, and the value must be read as of resolution, not as of the closure's capture.
  const priceTouchedRef = useRef(false);

  const markPriceManuallyEdited = useCallback(() => {
    priceTouchedRef.current = true;
    setIsPriceAutoFilled(false);
    setAutoPriceSource(null);
  }, []);

  // Refresh the suggested price from the server's authoritative cost basis.
  //
  // The response quotes the cost per BASE unit alongside the stock unit it was stored
  // in, precisely so this function never has to infer which unit a bare number means:
  // InventoryBatches.unit_cost is per the item's stock unit, and pairing a per-kg figure
  // with a 'g' label under-costs by 1000x. Everything is routed through the base unit.
  const handleAutoFillFromLatestBatch = useCallback(async ({ silent = false } = {}) => {
    if (!item?.id) return;
    // The silent pass runs on open to replace the card-derived estimate with the server's
    // authoritative cost — which, unlike the card, applies the legacy per-kg-stored-per-g
    // correction. It must never touch a price the user has already started editing, and
    // it must never raise a dialog for an item that simply has no price.
    if (silent && priceTouchedRef.current) return;
    setIsAutoFillingPrice(true);
    try {
      const bId = typeof UnitMappingService !== 'undefined'
        ? UnitMappingService.getBusinessId()
        : localStorage.getItem('businessId');
      const result = await http.get(`/stock-in/v2/inventory/items/${item.id}/latest-batch?businessId=${bId}`);

      const batch = result && result.success ? result.batch : null;
      if (!batch) {
        // The item has genuinely never been priced. Fall back to whatever the card
        // already knows before declaring defeat.
        if (autoPriceBasis) {
          const fallbackPrice = pricePerUnitFromBaseCost(autoPriceBasis.costPerBase, autoPriceBasis.baseUnit, pricingUnit);
          if (fallbackPrice !== null) {
            setPriceMode('unit');
            setPricingQuantity('1');
            setPricingPrice(formatAutoPriceValue(fallbackPrice));
            setDirectPrice('');
            setIsPriceAutoFilled(true);
            return;
          }
        }
        if (!silent) {
          showInAppAlert('No purchase or closing-stock price is on record for this item. Please enter it manually.');
        }
        return;
      }

      const costPerBase = Number(batch.cost_per_base_unit);
      const basis = Number.isFinite(costPerBase) && costPerBase > 0
        ? { costPerBase, baseUnit: batch.base_unit || UnitMappingService.baseUnitOf(batch.unit), unit: batch.unit }
        : getItemCostBasisPerBaseUnit({ latestUnitCost: batch.unit_cost, stockUnitSymbol: batch.unit });

      if (!basis) {
        if (!silent) {
          showInAppAlert('The stored price for this item is not usable. Please enter it manually.');
        }
        return;
      }

      const price = pricePerUnitFromBaseCost(basis.costPerBase, basis.baseUnit, pricingUnit);
      if (price === null) {
        if (!silent) {
          showInAppAlert(`The recorded price is per ${basis.unit || basis.baseUnit}, which cannot be converted to ${pricingUnit}. Please enter it manually.`);
        }
        return;
      }

      // Re-check after the await: the user may have typed a price while it was in flight.
      if (silent && priceTouchedRef.current) return;

      setAutoPriceBasis(basis);
      setAutoPriceSource(batch.source === 'closing_stock' ? 'closing_stock' : 'latest_purchase');
      setPriceMode('unit');
      setPricingQuantity('1');
      setPricingPrice(formatAutoPriceValue(price));
      // The direct-total override would otherwise keep winning over the price just fetched.
      setDirectPrice('');
      setIsPriceAutoFilled(true);
    } catch (err) {
      console.error('❌ Fix Price fetch failed:', err);
      // On the silent pass the card-derived estimate stays in place; a failed background
      // refresh is not worth a dialog over a price the user can see and edit.
      if (!silent) {
        showInAppAlert('Could not fetch latest price. Please enter manually.');
      }
    } finally {
      setIsAutoFillingPrice(false);
    }
  }, [item, pricingUnit, autoPriceBasis]);
  // Confirm the opening suggestion against the server exactly once per open. The card's
  // price is the right number to SHOW instantly, but the server's is the right number to
  // SAVE, and the two can differ on legacy mis-scaled batches. Runs regardless of whether
  // the card had a price at all — items with no card price often do have a closing-stock
  // price, which is the rung the card never looks at.
  const hasRefreshedPriceOnOpen = useRef(false);
  useEffect(() => {
    if (hasRefreshedPriceOnOpen.current) return;
    if (shouldUseExistingDraft || !isNegativeStockEntry || !item?.id) return;
    hasRefreshedPriceOnOpen.current = true;
    handleAutoFillFromLatestBatch({ silent: true });
  }, [shouldUseExistingDraft, isNegativeStockEntry, item?.id, handleAutoFillFromLatestBatch]);

  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [showPricingUnitDropdown, setShowPricingUnitDropdown] = useState(false);
  const [showPriceTypeDropdown, setShowPriceTypeDropdown] = useState(false);
  const [showConversionUnitDropdown, setShowConversionUnitDropdown] = useState(false);
  const priceModeOptions = [
    { value: 'unit', label: 'Per Unit' },
    { value: 'total', label: 'Invoice Total' }
  ];
  const selectedPriceModeOption = priceModeOptions.find((option) => option.value === priceMode);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.closing-stock-entry-select-wrapper')) {
        setShowUnitDropdown(false);
        setShowPricingUnitDropdown(false);
        setShowPriceTypeDropdown(false);
        setShowConversionUnitDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUnitDropdown, showPricingUnitDropdown, showPriceTypeDropdown, showConversionUnitDropdown]);
  
  // Log modal initialization for debugging
  useEffect(() => {
    if (shouldUseExistingDraft && existingDraft) {
      console.log('📋 [CLOSING STOCK MODAL] Initializing with existing draft:');
      console.log('   Draft data:', existingDraft);
      if (hasConversion) {
        console.log(`   ✅ Conversion detected: Showing original input`);
        console.log(`      Original: ${existingDraft.raw_quantity} ${existingDraft.original_unit}`);
        console.log(`      Conversion: 1 ${existingDraft.original_unit} = ${existingDraft.unit_conversion} ${existingDraft.conversion_unit}`);
        console.log(`      Converted to: ${existingDraft.quantity} ${existingDraft.unit}`);
      } else {
        console.log(`   ℹ️  No conversion: Showing as-is`);
        console.log(`      Quantity: ${existingDraft.quantity} ${existingDraft.unit}`);
      }
    }
  }, [existingDraft, hasConversion, shouldUseExistingDraft]);



  // normalizeUnitSymbol returns the DISPLAY symbol, which for litres is an uppercase
  // 'L'. Every comparison below is against a lowercase table, so comparing the display
  // symbol directly made 'L' match nothing: litre items got no unit choices and their
  // ml<->L conversions returned null, which is the "Unable to convert" save failure and
  // the permanently blank estimated price. Case is folded once, here.
  const canonicalizeInvoiceUnit = useCallback((unitValue) => {
    const normalized = UnitMappingService.normalizeUnitSymbol(unitValue);
    return String(normalized || '').toLowerCase();
  }, []);

  const unitOptions = useMemo(() => {
    if (isNegativeStockEntry) {
      const canonicalBase = canonicalizeInvoiceUnit(item?.unit);
      if (canonicalBase === 'g' || canonicalBase === 'kg') {
        return ['g', 'kg'];
      }
      if (canonicalBase === 'ml' || canonicalBase === 'l') {
        return ['ml', 'L'];
      }
      if (canonicalBase === 'pcs') {
        return ['pcs'];
      }
      const fallback = String(item?.unit || '').trim();
      return fallback ? [fallback] : ['g', 'kg'];
    }
    return weightUnits;
  }, [isNegativeStockEntry, item?.unit, canonicalizeInvoiceUnit]);

  const getQuantityConversionMultiplier = useCallback((fromUnit, toUnit) => {
    const from = canonicalizeInvoiceUnit(fromUnit);
    const to = canonicalizeInvoiceUnit(toUnit);

    if (!from || !to) return null;
    if (from === to) return 1;

    if (from === 'g' && to === 'kg') return 0.001;
    if (from === 'kg' && to === 'g') return 1000;
    if (from === 'ml' && to === 'l') return 0.001;
    if (from === 'l' && to === 'ml') return 1000;

    return null;
  }, [canonicalizeInvoiceUnit]);

  const convertQuantityBetweenUnits = useCallback((value, fromUnit, toUnit) => {
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) return null;

    const multiplier = getQuantityConversionMultiplier(fromUnit, toUnit);
    if (multiplier === null) return null;

    return parsedValue * multiplier;
  }, [getQuantityConversionMultiplier]);

  const formatComputedNumber = useCallback((value, precision = 6) => {
    if (!Number.isFinite(value)) return '';
    return String(Number(value.toFixed(precision)));
  }, []);

  const pricingUnitOptions = useMemo(() => {
    const canonicalSelected = canonicalizeInvoiceUnit(selectedUnit);

    if (canonicalSelected === 'g' || canonicalSelected === 'kg') {
      return ['g', 'kg'];
    }

    if (canonicalSelected === 'ml' || canonicalSelected === 'l') {
      return ['ml', 'L'];
    }

    if (canonicalSelected === 'pcs') {
      return ['pcs'];
    }

    const fallback = String(selectedUnit || '').trim();
    return fallback ? [fallback] : ['g'];
  }, [selectedUnit, canonicalizeInvoiceUnit]);

  useEffect(() => {
    const normalizedCurrentPricingUnit = canonicalizeInvoiceUnit(pricingUnit);
    const normalizedAllowed = pricingUnitOptions.map((option) => canonicalizeInvoiceUnit(option));

    if (normalizedAllowed.includes(normalizedCurrentPricingUnit)) {
      return;
    }

    const fallbackUnit = pricingUnitOptions[0] || selectedUnit;
    setPricingUnit(fallbackUnit);
  }, [
    pricingUnit,
    pricingUnitOptions,
    selectedUnit,
    canonicalizeInvoiceUnit
  ]);

  // Keep the suggested price expressed in the invoice unit currently selected. Without
  // this, switching g -> kg leaves the per-gram number in place and the save records a
  // price 1000x low. Guarded on isPriceAutoFilled so a typed price is never overwritten.
  useEffect(() => {
    if (!isPriceAutoFilled || !autoPriceBasis) return;

    const price = pricePerUnitFromBaseCost(autoPriceBasis.costPerBase, autoPriceBasis.baseUnit, pricingUnit);
    if (price === null) return;

    const nextValue = formatAutoPriceValue(price);
    setPricingPrice((prev) => (prev === nextValue ? prev : nextValue));
  }, [pricingUnit, isPriceAutoFilled, autoPriceBasis]);

  
  // Check if current unit needs conversion
  const normalizedSelectedUnit = String(selectedUnit || '').trim().toLowerCase();
  const needsConversion = !globalUnitAliases.has(normalizedSelectedUnit);

  const parsedEnteredQuantity = parseFloat(quantity || 0);
  const parsedReferenceQuantity = parseFloat(pricingQuantity || 0);
  const parsedReferencePrice = parseFloat(pricingPrice || 0);
  const parsedReferenceQuantityInSelectedUnit = convertQuantityBetweenUnits(
    parsedReferenceQuantity,
    pricingUnit,
    selectedUnit
  );

  const hasValidEnteredQuantity = Number.isFinite(parsedEnteredQuantity) && parsedEnteredQuantity >= 0;
  const hasValidReferenceQuantityInSelectedUnit = Number.isFinite(parsedReferenceQuantityInSelectedUnit)
    && parsedReferenceQuantityInSelectedUnit > 0;
  const hasValidReferencePrice = Number.isFinite(parsedReferencePrice) && parsedReferencePrice > 0;

  const calculatedUnitPrice = hasValidReferenceQuantityInSelectedUnit && hasValidReferencePrice
    ? (parsedReferencePrice / parsedReferenceQuantityInSelectedUnit)
    : null;
  const calculatedTotalPrice = hasValidEnteredQuantity && calculatedUnitPrice !== null
    ? (parsedEnteredQuantity * calculatedUnitPrice)
    : null;

  const calculatedUnitPriceDisplay = calculatedUnitPrice !== null ? calculatedUnitPrice.toFixed(4) : '';
  const calculatedTotalPriceDisplay = calculatedTotalPrice !== null ? calculatedTotalPrice.toFixed(2) : '';
  const enteredQuantityLabel = hasValidEnteredQuantity
    ? formatComputedNumber(parsedEnteredQuantity)
    : '';
  const enteredReferenceQuantityLabel = Number.isFinite(parsedReferenceQuantity) && parsedReferenceQuantity > 0
    ? formatComputedNumber(parsedReferenceQuantity)
    : '1';
  const autoPriceLabel = priceMode === 'total'
    ? (quantity
      ? `Price for ${quantity} ${selectedUnit} (₹)`
      : (isMobile
        ? `Price per qty (${selectedUnit})`
        : `Price for entered quantity (${selectedUnit})`))
    // In per-unit mode this field is bound to the SAME value as the reference-price box
    // below, so it is the price for the reference quantity — not necessarily for one unit
    // of the entry unit. Label it as what it is, in the unit it is actually quoted in.
    : `Price for ${enteredReferenceQuantityLabel} ${pricingUnit} (₹)`;
  // A number that appears on its own needs to say where it came from, or it reads as
  // something the user typed and forgot.
  const autoPriceSourceNote = isPriceAutoFilled && autoPriceSource
    ? (autoPriceSource === 'closing_stock'
      ? ' Prefilled from the last confirmed closing-stock price — edit it if this batch cost something else.'
      : ' Prefilled from the last purchase price — edit it if this batch cost something else.')
    : '';
  const autoPriceHelpText = priceMode === 'total'
    ? `Enter the invoice total for the selected quantity. Estimated price for entered quantity: ${calculatedTotalPriceDisplay ? `₹${calculatedTotalPriceDisplay}` : 'not available yet'}.`
    : `Enter the reference amount for the selected quantity. Estimated total: ${calculatedTotalPriceDisplay ? `₹${calculatedTotalPriceDisplay}` : 'not available yet'}.${autoPriceSourceNote}`;

  const autoPriceInputValue = priceMode === 'total'
    ? (directPrice !== '' ? directPrice : (calculatedTotalPriceDisplay || ''))
    : pricingPrice;

  const handleDirectPriceEntry = (val) => {
    markPriceManuallyEdited();
    if (priceMode === 'total') {
      setDirectPrice(val);
    } else {
      if (val === '') {
        setPricingPrice('');
        return;
      }
      const enteredAutoPrice = Number(val);
      if (!Number.isFinite(enteredAutoPrice) || enteredAutoPrice < 0) {
        return;
      }
      setPricingPrice(String(val));
    }
  };

  const handleSelectedUnitChange = (nextUnit) => {
    setSelectedUnit(nextUnit);
  };

  const handlePricingUnitChange = (nextUnit) => {
    setPricingUnit(nextUnit);
  };

  const handleQuantityInputChange = (value) => {
    setQuantity(value);
  };

  const handlePricingQuantityInputChange = (value) => {
    // A reference quantity other than 1 means the amount field is no longer a per-unit
    // price, so the suggestion no longer owns it.
    markPriceManuallyEdited();
    setPricingQuantity(value);
    setDirectPrice('');
  };

  const handlePriceModeChange = (nextMode) => {
    if (nextMode !== 'unit' && nextMode !== 'total') {
      return;
    }

    setPriceMode(nextMode);

    if (nextMode === 'unit' && (!pricingQuantity || Number(pricingQuantity) <= 0)) {
      setPricingQuantity('1');
      return;
    }
  };

  const handleAutoPriceInputChange = (value) => {
    if (value === '') {
      setPricingPrice('');
      return;
    }

    const enteredAutoPrice = Number(value);
    if (!Number.isFinite(enteredAutoPrice) || enteredAutoPrice < 0) {
      return;
    }

    setPricingPrice(String(value));
  };

  const handleSave = async () => {
    const enteredQty = parseFloat(quantity || 0);
    if (!Number.isFinite(enteredQty) || enteredQty <= 0) {
      showInAppAlert('Please enter quantity greater than zero');
      return;
    }

    const parsedConversionFactor = needsConversion ? parseFloat(unitConversion || 0) : 1;
    if (needsConversion && (!Number.isFinite(parsedConversionFactor) || parsedConversionFactor <= 0)) {
      showInAppAlert(`Please enter a valid conversion factor for ${selectedUnit} to ${conversionUnit}`);
      return;
    }

    let normalizedUnitPrice;
    let normalizedTotalPrice;
    let finalReferenceQtyInSelectedUnit;
    const isDirectTotalMode = priceMode === 'total' && directPrice !== '' && Number(directPrice) >= 0;

    if (isDirectTotalMode) {
      normalizedTotalPrice = Number(directPrice);
      normalizedUnitPrice = normalizedTotalPrice / enteredQty;
      finalReferenceQtyInSelectedUnit = enteredQty;
      
      if (!Number.isFinite(normalizedUnitPrice) || normalizedUnitPrice <= 0) {
        showInAppAlert('Please enter a valid total price greater than zero');
        return;
      }
    } else {
      const enteredReferenceQty = parseFloat(pricingQuantity || 0);
      const enteredReferenceUnit = pricingUnit;
      const enteredReferencePrice = parseFloat(pricingPrice || 0);

      if (!Number.isFinite(enteredReferenceQty) || enteredReferenceQty <= 0) {
        showInAppAlert(`Please enter a valid ${priceMode === 'unit' ? 'reference quantity' : 'invoice quantity'} greater than zero`);
        return;
      }

      if (!Number.isFinite(enteredReferencePrice) || enteredReferencePrice <= 0) {
        showInAppAlert(`Please enter a valid ${priceMode === 'unit' ? 'price amount' : 'invoice amount'}`);
        return;
      }

      finalReferenceQtyInSelectedUnit = convertQuantityBetweenUnits(
        enteredReferenceQty,
        enteredReferenceUnit,
        selectedUnit
      );

      if (!Number.isFinite(finalReferenceQtyInSelectedUnit) || finalReferenceQtyInSelectedUnit <= 0) {
        showInAppAlert(`Unable to convert ${enteredReferenceUnit} to ${selectedUnit}. Please choose a compatible invoice unit.`);
        return;
      }

      normalizedUnitPrice = enteredReferencePrice / finalReferenceQtyInSelectedUnit;
      normalizedTotalPrice = enteredQty * normalizedUnitPrice;
      
      if (!Number.isFinite(normalizedUnitPrice) || normalizedUnitPrice <= 0 || !Number.isFinite(normalizedTotalPrice) || normalizedTotalPrice <= 0) {
        showInAppAlert('Unable to compute price values. Please check quantity and price.');
        return;
      }
    }

    const conversionFactor = parsedConversionFactor;
    const finalQuantity = enteredQty * conversionFactor;
    const finalUnit = needsConversion ? conversionUnit : selectedUnit;

    const existingQuantity = shouldUseExistingDraft ? parseFloat(existingDraft?.quantity || 0) : 0;
    const existingUnit = shouldUseExistingDraft ? (existingDraft?.unit || finalUnit) : finalUnit;
    // The stored draft quantity and the newly entered quantity may be in different
    // units (e.g. existing 1000 g vs new 2 kg). Compare in a common unit (finalUnit)
    // so an increase isn't mislabelled as a reduction (and vice-versa).
    const existingQuantityInFinalUnit = (() => {
      if (!(existingQuantity > 0)) return existingQuantity;
      const converted = convertQuantityBetweenUnits(existingQuantity, existingUnit, finalUnit);
      return Number.isFinite(converted) ? converted : existingQuantity;
    })();
    if (existingQuantityInFinalUnit > 0 && finalQuantity < existingQuantityInFinalUnit) {
      const lostQuantity = existingQuantityInFinalUnit - finalQuantity;
      const existingUnitPrice = shouldUseExistingDraft ? parseFloat(existingDraft?.unit_price || 0) : 0;

      let newFinalUnitPrice = normalizedUnitPrice;
      if (needsConversion && conversionFactor > 0) {
        newFinalUnitPrice = normalizedUnitPrice / conversionFactor;
      }

      const shouldContinue = window.confirm(
        `Reducing stock will lose ${lostQuantity.toFixed(2)} ${finalUnit}.\n\n` +
        `Current quantity: ${existingQuantity.toFixed(2)} ${existingUnit}\n` +
        `New quantity: ${finalQuantity.toFixed(2)} ${finalUnit}\n\n` +
        `Current unit price: ₹${existingUnitPrice.toFixed(2)}/${existingUnit}\n` +
        `New unit price: ₹${(newFinalUnitPrice || 0).toFixed(2)}/${finalUnit}\n\n` +
        `Do you want to continue?`
      );

      if (!shouldContinue) {
        return;
      }
    }

    setIsSaving(true);
    const success = await onSave(
      item.id,
      enteredQty,
      normalizedUnitPrice,
      normalizedTotalPrice,
      priceMode,
      finalReferenceQtyInSelectedUnit,
      1,
      selectedUnit,
      needsConversion ? parseFloat(unitConversion || 0) : null,
      needsConversion ? conversionUnit : null
    );
    setIsSaving(false);

    if (success) {
      onClose();
    }
  };

  return (
    <div className="closing-stock-entry-modal-overlay" onClick={onClose}>
      <div className="closing-stock-entry-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="closing-stock-entry-modal-header">
          <div>
            <h3 className="closing-stock-entry-modal-title">{isNegativeStockEntry ? 'Negative Stock Entry' : 'Closing Stock Entry'}</h3>
          </div>
          <button className="closing-stock-entry-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="closing-stock-entry-modal-body">
          {/* Item Name */}
          <div className="closing-stock-entry-form-row">
            <div className="closing-stock-entry-input-group closing-stock-entry-item-group">
              <label className="closing-stock-entry-label">Item Name</label>
              <input
                type="text"
                className="closing-stock-entry-input closing-stock-entry-input-readonly"
                value={item.name}
                readOnly
              />
            </div>
          </div>

          {/* Quantity, Unit and Price - All in one horizontal line */}
          <div className="closing-stock-entry-form-row closing-stock-entry-form-row-two-col">
            <div className="closing-stock-entry-input-group closing-stock-entry-quantity-group">
              <label className="closing-stock-entry-label">Quantity *</label>
              <input
                type="number"
                className="closing-stock-entry-input"
                placeholder="Enter quantity"
                value={quantity}
                onChange={(e) => handleQuantityInputChange(e.target.value)}
                min="0"
                step="any"
                required
              />
            </div>
            <div className="closing-stock-entry-input-group closing-stock-entry-unit-group">
              <label className="closing-stock-entry-label">Unit *</label>
              <div className="closing-stock-entry-select-wrapper">
                <div 
                  className="closing-stock-entry-select-trigger"
                  onClick={() => {
                    setShowUnitDropdown(!showUnitDropdown);
                    setShowPricingUnitDropdown(false);
                    setShowPriceTypeDropdown(false);
                    setShowConversionUnitDropdown(false);
                  }}
                >
                  <span>{selectedUnit}</span>
                  <svg 
                    className={`closing-stock-entry-select-arrow ${showUnitDropdown ? 'open' : ''}`}
                    width="14" 
                    height="14" 
                    viewBox="0 0 12 12" 
                    fill="none"
                  >
                    <path d="M2 4L6 8L10 4" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {showUnitDropdown && (
                  <div className="closing-stock-entry-select-dropdown">
                    {unitOptions.map(unit => (
                      <div
                        key={unit}
                        className={`closing-stock-entry-select-option ${canonicalizeInvoiceUnit(selectedUnit) === canonicalizeInvoiceUnit(unit) ? 'selected' : ''}`}
                        onClick={() => {
                          handleSelectedUnitChange(unit);
                          setShowUnitDropdown(false);
                        }}
                      >
                        {unit}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="closing-stock-entry-form-row closing-stock-entry-form-row-two-col closing-stock-entry-price-fields-row">
            <div className="closing-stock-entry-input-group closing-stock-entry-price-mode-group">
              <label className="closing-stock-entry-label">Price Type *</label>
              <div className="closing-stock-entry-select-wrapper">
                <div
                  className="closing-stock-entry-select-trigger"
                  onClick={() => {
                    setShowPriceTypeDropdown(!showPriceTypeDropdown);
                    setShowUnitDropdown(false);
                    setShowPricingUnitDropdown(false);
                    setShowConversionUnitDropdown(false);
                  }}
                >
                  <span>{selectedPriceModeOption?.label || 'Invoice Total'}</span>
                  <svg
                    className={`closing-stock-entry-select-arrow ${showPriceTypeDropdown ? 'open' : ''}`}
                    width="14"
                    height="14"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path d="M2 4L6 8L10 4" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {showPriceTypeDropdown && (
                  <div className="closing-stock-entry-select-dropdown" role="listbox" aria-label="Select price type">
                    {priceModeOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`closing-stock-entry-select-option ${priceMode === option.value ? 'selected' : ''}`}
                        onClick={() => {
                          handlePriceModeChange(option.value);
                          setShowPriceTypeDropdown(false);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="closing-stock-entry-input-group closing-stock-entry-price-mode-group">
              <label className="closing-stock-entry-label">{autoPriceLabel}</label>
              <input
                type="number"
                className="closing-stock-entry-input closing-stock-entry-price-input"
                value={autoPriceInputValue}
                onChange={(e) => handleDirectPriceEntry(e.target.value)}
                min="0"
                step="any"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="closing-stock-entry-form-row closing-stock-entry-form-row-three-col closing-stock-entry-price-fields-row closing-stock-entry-reference-row">
            <div className="closing-stock-entry-input-group closing-stock-entry-price-mode-group closing-stock-entry-reference-qty-group">
              <label className="closing-stock-entry-label">{priceMode === 'unit' ? `Reference Quantity (${pricingUnit})` : `Invoice Quantity (${pricingUnit})`}</label>
              <input
                type="number"
                className="closing-stock-entry-input closing-stock-entry-price-input"
                placeholder={priceMode === 'unit' ? 'Usually 1' : 'Enter invoice quantity'}
                value={pricingQuantity}
                onChange={(e) => handlePricingQuantityInputChange(e.target.value)}
                min="0"
                step="any"
                required
              />
            </div>

            <div className="closing-stock-entry-input-group closing-stock-entry-price-mode-group closing-stock-entry-reference-unit-group">
              <label className="closing-stock-entry-label">Units</label>
              <div className="closing-stock-entry-select-wrapper">
                <div
                  className="closing-stock-entry-select-trigger"
                  onClick={() => {
                    setShowPricingUnitDropdown(!showPricingUnitDropdown);
                    setShowUnitDropdown(false);
                    setShowPriceTypeDropdown(false);
                    setShowConversionUnitDropdown(false);
                  }}
                >
                  <span>{pricingUnit}</span>
                  <svg
                    className={`closing-stock-entry-select-arrow ${showPricingUnitDropdown ? 'open' : ''}`}
                    width="14"
                    height="14"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path d="M2 4L6 8L10 4" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {showPricingUnitDropdown && (
                  <div className="closing-stock-entry-select-dropdown" role="listbox" aria-label="Select invoice unit">
                    {pricingUnitOptions.map((unitOption) => (
                      <div
                        key={unitOption}
                        className={`closing-stock-entry-select-option ${canonicalizeInvoiceUnit(pricingUnit) === canonicalizeInvoiceUnit(unitOption) ? 'selected' : ''}`}
                        onClick={() => {
                          handlePricingUnitChange(unitOption);
                          setShowPricingUnitDropdown(false);
                        }}
                      >
                        {unitOption}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="closing-stock-entry-input-group closing-stock-entry-price-mode-group closing-stock-entry-reference-price-group">
              <label className="closing-stock-entry-label">{priceMode === 'unit' ? 'Price for Reference Qty (₹)' : 'Invoice Amount (₹)'}</label>
              <input
                type="number"
                className="closing-stock-entry-input closing-stock-entry-price-input"
                placeholder="₹ 0.00"
                value={pricingPrice}
                onChange={(e) => { markPriceManuallyEdited(); setPricingPrice(e.target.value); }}
                min="0"
                step="any"
                required
              />
            </div>
          </div>

          {!isOnboardingFlow && (
            <div className="closing-stock-entry-form-row closing-stock-entry-price-total-row">
              <div className="closing-stock-entry-input-group closing-stock-entry-price-mode-group closing-stock-entry-reference-helper-group">
                <label className="closing-stock-entry-label" style={{ color: 'transparent', fontSize: '0.1px' }}>.</label>
                <button
                  type="button"
                  className="closing-stock-entry-latest-price-btn"
                  onClick={() => handleAutoFillFromLatestBatch()}
                  disabled={isAutoFillingPrice}
                  style={{ width: '100%' }}
                >
                  {isAutoFillingPrice ? 'Fetching...' : 'Fix Price'}
                </button>
              </div>
            </div>
          )}

          {/* Unit Conversion (for non-global units) */}
          {needsConversion && (
            <>
              <div className="closing-stock-entry-form-row closing-stock-entry-conversion-row">
                <div className="closing-stock-entry-input-group closing-stock-entry-conversion-factor-group">
                  <label className="closing-stock-entry-label">Conversion Factor *</label>
                  <input
                    type="number"
                    className="closing-stock-entry-input"
                    placeholder={`How many ${conversionUnit} in 1 ${selectedUnit}?`}
                    value={unitConversion}
                    onChange={(e) => setUnitConversion(e.target.value)}
                    min="0"
                    step="any"
                    required
                  />
                </div>
                <div className="closing-stock-entry-input-group closing-stock-entry-conversion-unit-group">
                  <label className="closing-stock-entry-label">Convert To *</label>
                  <div className="closing-stock-entry-select-wrapper">
                    <div
                      className="closing-stock-entry-select-trigger"
                      onClick={() => {
                        setShowConversionUnitDropdown(!showConversionUnitDropdown);
                        setShowUnitDropdown(false);
                        setShowPricingUnitDropdown(false);
                        setShowPriceTypeDropdown(false);
                      }}
                    >
                      <span>{conversionUnit}</span>
                      <svg
                        className={`closing-stock-entry-select-arrow ${showConversionUnitDropdown ? 'open' : ''}`}
                        width="14"
                        height="14"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path d="M2 4L6 8L10 4" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    {showConversionUnitDropdown && (
                      <div className="closing-stock-entry-select-dropdown">
                        {globalUnits.map(unit => (
                          <div
                            key={unit}
                            className={`closing-stock-entry-select-option ${conversionUnit === unit ? 'selected' : ''}`}
                            onClick={() => {
                              setConversionUnit(unit);
                              setShowConversionUnitDropdown(false);
                            }}
                          >
                            {unit}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <small className="closing-stock-entry-conversion-note">
                e.g., 1 {selectedUnit} = X {conversionUnit}
              </small>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="closing-stock-entry-modal-actions">
          <button className="closing-stock-entry-btn closing-stock-entry-btn-cancel" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="closing-stock-entry-btn closing-stock-entry-btn-save" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {/* In-app alert modal */}
        {alertModal.visible && (
          <div className="inv-alert-modal-overlay" onClick={closeInAppAlert}>
            <div className="inv-alert-modal" onClick={(e) => e.stopPropagation()}>
              <p className="inv-alert-modal-message">{alertModal.message}</p>
              <div className="inv-alert-modal-actions">
                <button className="inv-alert-modal-ok-btn" onClick={closeInAppAlert}>Okay</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ClosingStockCorrectionModal = ({ item, onClose, onSave }) => {
  const resolvedInitialUnit = String(item?.unit || 'g').trim() || 'g';
  const initialQuantity = item?.quantity !== null && item?.quantity !== undefined && item?.quantity !== ''
    ? String(item.quantity)
    : '';

  const [quantity, setQuantity] = useState(initialQuantity);
  const [selectedUnit, setSelectedUnit] = useState(resolvedInitialUnit);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [manualTotalPrice, setManualTotalPrice] = useState('');
  const [isManualPriceEdited, setIsManualPriceEdited] = useState(false);
  const [validationError, setValidationError] = useState('');
  const unitDropdownRef = useRef(null);

  const canonicalizeUnit = useCallback((value) => {
    return UnitMappingService.normalizeUnitSymbol(value);
  }, []);

  const unitOptions = useMemo(() => {
    const normalized = canonicalizeUnit(selectedUnit || resolvedInitialUnit);

    if (normalized === 'g') return [{ value: 'kg', label: 'kg' }];
    if (normalized === 'kg') return [{ value: 'g', label: 'g' }];
    if (normalized === 'ml') return [{ value: 'L', label: 'L' }];
    if (normalized === 'L') return [{ value: 'ml', label: 'ml' }];

    return [];
  }, [selectedUnit, resolvedInitialUnit, canonicalizeUnit]);

  const hasUnitAlternatives = unitOptions.length > 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUnitDropdown && unitDropdownRef.current && !unitDropdownRef.current.contains(event.target)) {
        setShowUnitDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUnitDropdown]);

  useEffect(() => {
    const nextQuantity = item?.quantity !== null && item?.quantity !== undefined && item?.quantity !== ''
      ? String(item.quantity)
      : '';
    setQuantity(nextQuantity);
    setSelectedUnit(resolvedInitialUnit);
    setShowUnitDropdown(false);
    // If parent computed an initial manual total, use it; otherwise clear
    setManualTotalPrice(item?.initialManualTotal || '');
    setIsManualPriceEdited(false);
    setValidationError('');
  }, [item, resolvedInitialUnit]);

  const unitPrice = Number(item?.unitPrice || 0);
  const isLoadingPrice = Boolean(item?.isLoadingPrice);
  const hasValidUnitPrice = Number.isFinite(unitPrice) && unitPrice > 0;
  const normalizedSelectedUnit = String(selectedUnit || '').trim().toLowerCase();
  const normalizedBatchUnit = String(item?.previousUnit || item?.unit || '').trim().toLowerCase();
  const isUnitChanged = normalizedSelectedUnit !== normalizedBatchUnit;

  const parsedQuantity = Number(quantity);
  const hasValidQuantity = quantity !== '' && Number.isFinite(parsedQuantity) && parsedQuantity >= 0;
  const parsedManualTotal = Number(manualTotalPrice);
  const hasValidManualTotal = manualTotalPrice !== '' && Number.isFinite(parsedManualTotal) && parsedManualTotal > 0;
  

  const conversionMultiplier = getCostUnitMultiplier(normalizedBatchUnit, normalizedSelectedUnit);
  const canAutoConvertUnitPrice = conversionMultiplier !== null;

  const requiresManualPriceInput = !isLoadingPrice && (!hasValidUnitPrice || (isUnitChanged && !canAutoConvertUnitPrice));

  const convertedUnitPrice = (hasValidUnitPrice && canAutoConvertUnitPrice)
    ? (unitPrice * conversionMultiplier)
    : unitPrice;

  const autoCalculatedPrice = hasValidQuantity && hasValidUnitPrice
    ? (parsedQuantity * convertedUnitPrice)
    : 0;

  // Auto-fill total price unless the user has started manual edits.
  useEffect(() => {
    // If previous batch price exists and we can convert, show calculated total
    const batchHasPrice = hasValidUnitPrice && unitPrice > 0;
    const multiplier = getCostUnitMultiplier(normalizedBatchUnit, normalizedSelectedUnit);
    const canAuto = batchHasPrice && (multiplier !== null);

    if (!hasValidQuantity) {
      if (!isManualPriceEdited) {
        setManualTotalPrice('');
      }
      return;
    }

    if (canAuto) {
      const computedUnitPrice = unitPrice * multiplier;
      const computedTotal = parsedQuantity * computedUnitPrice;
      if (!isManualPriceEdited) {
        setManualTotalPrice(String(Number(computedTotal.toFixed(2))));
        if (validationError) {
          setValidationError('');
        }
      }
      return;
    }

    // If we cannot auto-convert, clear auto value so user must enter price
    if (!isManualPriceEdited) {
      setManualTotalPrice('');
    }
  }, [selectedUnit, item?.unitPrice, quantity, item?.previousUnit, isManualPriceEdited, hasValidQuantity, hasValidUnitPrice, parsedQuantity, normalizedBatchUnit, normalizedSelectedUnit, unitPrice, validationError]);

  const handleStage = () => {
    let finalUnitPrice = convertedUnitPrice;
    let finalTotalPrice = autoCalculatedPrice;
    const shouldUseManualTotal = requiresManualPriceInput || isManualPriceEdited;

    if (!hasValidQuantity) {
      setValidationError('Please enter a valid quantity (0 or greater).');
      return;
    }

    if (shouldUseManualTotal) {
      if (!hasValidManualTotal) {
        setValidationError('Please enter a valid price greater than zero.');
        return;
      }

      if (parsedQuantity <= 0) {
        setValidationError('Enter quantity greater than zero when price is entered manually.');
        return;
      }

      finalTotalPrice = parsedManualTotal;
      finalUnitPrice = parsedManualTotal / parsedQuantity;
    }

    onSave({
      itemId: item.itemId,
      itemName: item.itemName,
      quantity: parsedQuantity,
      unit: selectedUnit,
      unitPrice: finalUnitPrice,
      totalPrice: finalTotalPrice
    });
  };

  return (
    <div className="closing-stock-correction-modal-overlay">
      <div className="closing-stock-correction-modal" onClick={(e) => e.stopPropagation()}>
        <div className="closing-stock-correction-modal-header">
          <div className="closing-stock-correction-modal-heading">
            <h3 className="closing-stock-correction-modal-title">Closing Stock Correction</h3>
            <p className="closing-stock-correction-modal-subtitle">
              {requiresManualPriceInput
                ? 'Previous batch price is unavailable. Enter price manually.'
                : 'Quantity can be edited. Price is auto-filled from previous batch and can be edited.'}
            </p>
          </div>
          <button
            type="button"
            className="closing-stock-correction-modal-close"
            onClick={onClose}
            aria-label="Close closing stock correction modal"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="closing-stock-correction-modal-body">
          <div className="closing-stock-correction-form-row">
            <div className="closing-stock-correction-input-group closing-stock-correction-item-group">
              <label className="closing-stock-correction-label">Item Name</label>
              <input
                type="text"
                className="closing-stock-correction-input"
                value={item?.itemName || ''}
                readOnly
                style={{ background: '#f9fafb', color: '#000000', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          <div className="closing-stock-correction-form-row closing-stock-correction-form-row-compact">
            <div className="closing-stock-correction-input-group closing-stock-correction-quantity-group">
              <label className="closing-stock-correction-label">Quantity *</label>
              <input
                type="number"
                className="closing-stock-correction-input"
                placeholder="Enter quantity"
                value={quantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  if (validationError) {
                    setValidationError('');
                  }
                }}
                min="0"
                step="any"
                required
              />
            </div>

            <div className="closing-stock-correction-input-group closing-stock-correction-unit-group">
              <label className="closing-stock-correction-label">Unit</label>
              <div className="closing-stock-correction-unit-dropdown" ref={unitDropdownRef}>
                <button
                  type="button"
                  className={`closing-stock-correction-unit-trigger ${!hasUnitAlternatives ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!hasUnitAlternatives) return;
                    setShowUnitDropdown((prev) => !prev);
                  }}
                  aria-haspopup="listbox"
                  aria-expanded={hasUnitAlternatives && showUnitDropdown}
                  disabled={!hasUnitAlternatives}
                >
                  <span>{selectedUnit}</span>
                  {hasUnitAlternatives && (
                    <IoChevronDown className={`closing-stock-correction-unit-arrow ${showUnitDropdown ? 'open' : ''}`} />
                  )}
                </button>

                {showUnitDropdown && hasUnitAlternatives && (
                  <div className="closing-stock-correction-unit-menu" role="listbox" aria-label="Select unit">
                    {unitOptions.map((unitOption) => {
                      const isActive = String(selectedUnit || '').toLowerCase() === String(unitOption?.value || '').toLowerCase();
                      return (
                        <button
                          key={unitOption.value}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          className={`closing-stock-correction-unit-option ${isActive ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedUnit(unitOption.value);
                            setShowUnitDropdown(false);
                            if (validationError) {
                              setValidationError('');
                            }
                          }}
                        >
                          {unitOption.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="closing-stock-correction-input-group closing-stock-correction-price-group">
              <label className="closing-stock-correction-label">Price (₹){requiresManualPriceInput ? ' *' : ''}</label>
              <input
                type="number"
                className="closing-stock-correction-input closing-stock-correction-price-input"
                value={manualTotalPrice}
                placeholder={isLoadingPrice ? 'Loading...' : 'Enter total price'}
                min="0"
                step="any"
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setManualTotalPrice(nextValue);
                  setIsManualPriceEdited(nextValue !== '');
                  if (validationError) {
                    setValidationError('');
                  }
                }}
                style={{ background: '#ffffff', color: '#111827', cursor: 'text' }}
              />
            </div>
          </div>

          <small className="closing-stock-correction-rate-note">
            {isLoadingPrice
              ? 'Loading previous batch rate...'
              : (isUnitChanged
                ? `Selected unit is ${selectedUnit}. Previous batch rate is for ${item?.unit || 'g'}. Enter price manually for selected unit.`
                : (hasValidUnitPrice
                  ? `Auto-calculated from previous batch rate: ₹${unitPrice.toFixed(4)}/${item?.unit || 'g'}`
                  : 'Previous batch price is unavailable or zero. Enter price manually.'))}
          </small>

          <small className="closing-stock-correction-note">
            {requiresManualPriceInput
              ? 'Manual price will be used for this quantity and stored as the new closing stock value.'
              : 'Price is auto-filled from previous batch and can be edited for this correction.'}
          </small>

          {validationError && (
            <div className="closing-stock-correction-error">{validationError}</div>
          )}
        </div>

        <div className="closing-stock-correction-modal-actions">
          <button className="closing-stock-correction-modal-btn closing-stock-correction-modal-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="closing-stock-correction-modal-btn closing-stock-correction-modal-btn-stage"
            onClick={handleStage}
            disabled={isLoadingPrice}
          >
            Stage
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryOverview;
